// Prospector - spreadsheet-style entity workspace.
// Initial sheet data is server-rendered into window.PROSPECTOR_INITIAL_SHEETS
// by the prospector_page route. Persisted Prospector actions use the backend
// job substrate; demo-only surfaces keep local animation fallbacks.

import {
  BASE_COLUMN_KEYS,
  COLUMN_ACTIONS,
  COLUMN_GROUP_LABELS,
  COLUMN_GROUP_ORDER,
  COLUMN_PRESETS,
  EMPLOYEE_COUNT_ACTION_KIND,
  EMPLOYEE_COUNT_COLUMN_KEYS,
  EMPLOYEE_PROFILE_REFRESH_KEYS,
  ENTITY_TYPES,
  JOB_STATUS,
  JOB_STATUS_ACTIVE,
  JOB_STATUS_CLEARS_LOADING,
  PERSON_ADD_ROW_METHOD_COLUMNS,
  TRANSFORMS,
  WORK_EMAIL_ACTION_KIND,
  catalogueFor,
} from "./prospector/schema.js";
import {
  fetchJSON,
  insufficientCreditsDetails,
  insufficientCreditsMessage,
  isInsufficientCreditsError,
} from "./prospector/api.js";
import {
  buildDemoSheets,
  parseCSV,
  synthesizeTransformedRows,
} from "./prospector/demo.js";
import { Cell } from "./prospector/formatters.jsx";

const { useState, useEffect, useRef, useCallback } = React;

// ============================================================================
// Constants
// ============================================================================

// Tailwind width tokens → px (Tailwind's 0.25rem unit at 16px root). Used as
// the default column width when a sheet has no per-column resize override.
// Resized columns carry an explicit ``width_px`` from the server, which wins.
const TW_WIDTH_PX = {
  "w-10": 40, "w-12": 48, "w-16": 64, "w-20": 80, "w-24": 96,
  "w-28": 112, "w-32": 128, "w-40": 160, "w-44": 176, "w-52": 208, "w-56": 224,
  "w-64": 256, "w-80": 320,
};
const COL_MIN_PX = 48;
const COL_MAX_PX = 720;
const BUY_CREDITS_URL = "/dashboard/billing#plans";
const TRANSFORM_CREDIT_WARNING_STORAGE_PREFIX = "prospector_skip_credit_warning_";

// ── Iconography ──────────────────────────────────────────────────────────────
//
// Static lookup tables keyed on column key / type / platform. Hoisted to module
// scope so they're not reallocated on every render of ColumnTypeIcon / Cell.

// Column-key → Phosphor icon. Falls back to TYPE_ICONS[col.type] when the key
// isn't here, so new catalogue columns still get a sensible icon by type.
const COL_KEY_ICONS = {
  name: "ph-textbox",
  domain: "ph-globe",
  company_domain: "ph-globe",
  industry: "ph-tag",
  title: "ph-briefcase",
  company: "ph-buildings",
  work_email: "ph-envelope-simple",
  employee_count: "ph-users",
  funding_total: "ph-currency-circle-dollar",
  kind: "ph-tag",
  published: "ph-calendar",
  tagline: "ph-quotes",
  description: "ph-text-align-left",
  cover_image: "ph-image",
  company_type: "ph-buildings",
  source_domain: "ph-arrow-square-out",
  competition_reason: "ph-scales",
  customer_of: "ph-handshake",
  customer_of_domain: "ph-globe",
  relationship_type: "ph-arrows-left-right",
  founded_year: "ph-calendar",
  age_years: "ph-hourglass",
  hq_city: "ph-map-pin",
  hq_country: "ph-globe-hemisphere-west",
  branch_count: "ph-buildings",
  ceo_name: "ph-crown",
  twitter_url: "ph-x-logo",
  facebook_url: "ph-facebook-logo",
  instagram_url: "ph-instagram-logo",
  linkedin_url: "ph-linkedin-logo",
  twitter_followers: "ph-megaphone-simple",
  stock_symbol: "ph-chart-line",
  ipo_date: "ph-calendar-check",
  revenue_usd: "ph-currency-circle-dollar",
  ebitda_usd: "ph-currency-circle-dollar",
  market_cap_usd: "ph-currency-circle-dollar",
  total_funds_raised_usd: "ph-currency-circle-dollar",
  last_round_type: "ph-trend-up",
  last_round_date: "ph-calendar",
  last_round_amount_usd: "ph-currency-circle-dollar",
  lead_investor: "ph-user-circle",
  investor_count: "ph-users",
  num_rounds: "ph-stack",
  source_company: "ph-buildings",
  stage: "ph-flag",
  date: "ph-calendar",
  amount_usd: "ph-currency-circle-dollar",
  investor_name: "ph-user-circle",
  investor_website: "ph-globe",
  investor_type: "ph-tag",
  investor_amount_usd: "ph-currency-circle-dollar",
  id: "ph-identification-badge",
  slug: "ph-link-simple",
  profile_pic_url: "ph-image",
  full_name: "ph-user",
  first_name: "ph-user",
  middle_name: "ph-user",
  last_name: "ph-user",
  role: "ph-identification-card",
  employer_website: "ph-buildings",
  bio: "ph-text-align-left",
  follower_count: "ph-megaphone-simple",
  following_count: "ph-users",
  x_handle: "ph-x-logo",
  x_profile_url: "ph-x-logo",
  personal_website: "ph-link",
  work_email_lookup: "ph-envelope-simple",
  similar_people: "ph-users-three",
  person: "ph-user",
  person_id: "ph-identification-badge",
  school: "ph-graduation-cap",
  major: "ph-certificate",
  start_date: "ph-calendar",
  end_date: "ph-calendar-check",
  source: "ph-rss",
  url: "ph-link",
  specialty: "ph-tag-chevron",
  address_type: "ph-map-pin",
  line1: "ph-map-trifold",
  line2: "ph-map-trifold",
  city: "ph-buildings",
  state: "ph-map-pin",
  postal_code: "ph-hash",
  country_code: "ph-globe-hemisphere-west",
  country: "ph-globe-hemisphere-west",
  is_primary: "ph-check-circle",
};

const COL_TYPE_ICONS = {
  text: "ph-textbox",
  number: "ph-number-square-one",
  year: "ph-calendar",
  currency: "ph-currency-circle-dollar",
  date: "ph-calendar",
  image: "ph-image",
  chip_list: "ph-tag-chevron",
  domain: "ph-globe",
  url: "ph-link",
  social_icon: "ph-share-network",
  kind_badge: "ph-tag",
  logo: "ph-image",
};

const SOCIAL_ICONS = {
  twitter: "ph-x-logo",
  facebook: "ph-facebook-logo",
  instagram: "ph-instagram-logo",
};

const KIND_COLOR_MAP = {
  "Blog": "bg-blue-100 text-blue-700",
  "X post": "bg-zinc-900 text-white",
  "Website": "bg-emerald-100 text-emerald-700",
};

function colWidthPx(col) {
  if (!col) return 144;
  if (typeof col.width_px === "number" && col.width_px > 0) return col.width_px;
  if (col.width && TW_WIDTH_PX[col.width]) return TW_WIDTH_PX[col.width];
  return 144;
}

function fixedCellWidthStyle(widthPx) {
  return {
    width: widthPx,
    minWidth: widthPx,
    maxWidth: widthPx,
    boxSizing: "border-box",
  };
}

// ============================================================================
// Helpers
// ============================================================================

function uid(prefix = "id") {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

// Single-shape JSON fetch wrapper for the Prospector dashboard API. Throws
// on transport error, non-2xx, or `success: false` so call sites can use a
// single try/catch with a meaningful `e.message` for the user-facing alert.
// Functional update for a single sheet inside a sheets array. The updater
// returns a partial object that's spread onto the matching sheet; non-matches
// pass through unchanged. Replaces the
// `prev.map(s => s.id !== id ? s : { ...s, ...mutation })` boilerplate.
function patchSheet(sheets, sheetId, updater) {
  return sheets.map(s => (s.id === sheetId ? { ...s, ...updater(s) } : s));
}

// Append `incoming` keys to a row's `_shimmerCols`, dedup'd. Both args
// tolerate undefined.
function mergeShimmerCols(existing, incoming) {
  return Array.from(new Set([...(existing || []), ...(incoming || [])]));
}

function clearRowLoadingState(row) {
  const shouldClearStatus = !row._status || JOB_STATUS_ACTIVE.has(row._status) || JOB_STATUS_CLEARS_LOADING.has(row._status);
  if (!shouldClearStatus && (!row._shimmerCols || row._shimmerCols.length === 0)) return row;
  const next = { ...row };
  delete next._shimmerCols;
  if (shouldClearStatus) {
    delete next._status;
    delete next._error;
  }
  return next;
}

function clearSheetLoadingState(sheet) {
  return {
    progress: null,
    emptyState: sheet.parentId && (sheet.rows || []).length === 0 ? "aborted" : sheet.emptyState,
    rows: (sheet.rows || []).map(clearRowLoadingState),
  };
}

function isEmptySortValue(value) {
  return value === null || value === undefined || value === "";
}

function parseSortNumber(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return null;
  const raw = value.trim();
  if (!raw) return null;
  const magnitude = raw.match(/(-?\d+(?:\.\d+)?)\s*([kmbt])\b/i);
  if (magnitude) {
    const multipliers = { k: 1e3, m: 1e6, b: 1e9, t: 1e12 };
    return Number(magnitude[1]) * multipliers[magnitude[2].toLowerCase()];
  }
  const normalized = raw.replace(/[$€£¥,%\s,]/g, "");
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function sortComparableValue(row, col) {
  const raw = row ? row[col.key] : undefined;
  if (isEmptySortValue(raw)) return { empty: true, value: null, kind: "empty" };

  if (["number", "year", "currency"].includes(col.type)) {
    const parsed = parseSortNumber(raw);
    if (parsed !== null) return { empty: false, value: parsed, kind: "number" };
  }

  if (col.type === "date") {
    const timestamp = raw instanceof Date ? raw.getTime() : Date.parse(raw);
    if (Number.isFinite(timestamp)) return { empty: false, value: timestamp, kind: "number" };
  }

  const text = Array.isArray(raw)
    ? raw.join(" ")
    : (typeof raw === "object" ? JSON.stringify(raw) : String(raw));
  return { empty: false, value: text.toLowerCase(), kind: "text" };
}

function sortRowsByColumn(rows, col, direction) {
  const dir = direction === "desc" ? -1 : 1;
  return [...(rows || [])]
    .map((row, index) => ({ row, index, comparable: sortComparableValue(row, col) }))
    .sort((a, b) => {
      if (a.comparable.empty || b.comparable.empty) {
        if (a.comparable.empty && b.comparable.empty) return a.index - b.index;
        return a.comparable.empty ? 1 : -1;
      }

      let cmp = 0;
      if (a.comparable.kind === "number" && b.comparable.kind === "number") {
        cmp = a.comparable.value - b.comparable.value;
      } else {
        cmp = String(a.comparable.value).localeCompare(
          String(b.comparable.value),
          undefined,
          { numeric: true, sensitivity: "base" },
        );
      }
      if (cmp === 0) return a.index - b.index;
      return cmp * dir;
    })
    .map(item => item.row);
}

function sameRowOrder(aRows, bRows) {
  if ((aRows || []).length !== (bRows || []).length) return false;
  return (aRows || []).every((row, idx) => row?._id === bRows[idx]?._id);
}

function moveRowAround(rows, fromRowId, toRowId, placement = "before") {
  const nextRows = [...(rows || [])];
  const fromIdx = nextRows.findIndex(r => r._id === fromRowId);
  if (fromIdx === -1) return nextRows;
  const [moved] = nextRows.splice(fromIdx, 1);
  const toIdx = nextRows.findIndex(r => r._id === toRowId);
  if (toIdx === -1) return rows || [];
  nextRows.splice(placement === "after" ? toIdx + 1 : toIdx, 0, moved);
  return nextRows;
}

function restoreRowsByOrder(currentRows, previousOrder) {
  const byId = new Map((currentRows || []).map(r => [r._id, r]));
  const previousIds = new Set(previousOrder || []);
  const restored = (previousOrder || []).map(id => byId.get(id)).filter(Boolean);
  const leftovers = (currentRows || []).filter(r => !previousIds.has(r._id));
  return [...restored, ...leftovers];
}

function employeeProfileLookupInputAvailable(row) {
  if (!row) return false;
  const personId = String(row.id || row.person_id || "").trim();
  const slug = String(row.slug || "").trim();
  const workEmail = String(row.work_email || "").trim();
  const firstName = String(row.first_name || "").trim();
  const employerWebsite = String(row.employer_website || row.company_domain || row.domain || "").trim();
  const role = String(row.role || row.title || "").trim();
  return !!(personId || slug || workEmail || (firstName && employerWebsite) || (employerWebsite && role));
}

function rowHasEnrichedPersonIdentity(row) {
  if (!row) return false;
  return !!(
    String(row.id || row.person_id || "").trim()
    || String(row.slug || "").trim()
  );
}

function personNameParts(row) {
  const first = String(row?.first_name || "").trim();
  const last = String(row?.last_name || "").trim();
  if (first) return { firstName: first, lastName: last };

  const full = String(row?.full_name || row?.name || "").trim();
  if (!full) return { firstName: "", lastName: "" };
  const parts = full.split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.length > 1 ? parts[parts.length - 1] : "",
  };
}

function workEmailLookupInputAvailable(row) {
  if (!row) return false;
  if (String(row.work_email || "").trim()) return false;
  const { firstName } = personNameParts(row);
  const domain = String(row.company_domain || row.employer_website || row.domain || "").trim();
  return !!(firstName && domain);
}

function canRefreshFreeColumnsForRow(entityType, row) {
  if (entityType === "companies") return !!row?.domain;
  if (entityType === "employees") return employeeProfileLookupInputAvailable(row);
  return false;
}

function getLocalStorageFlag(key) {
  if (!key) return false;
  try {
    return localStorage.getItem(key) === "true";
  } catch (err) {
    console.debug("Unable to read localStorage flag:", key, err);
    return false;
  }
}

function setLocalStorageFlag(key, value) {
  if (!key) return;
  try {
    localStorage.setItem(key, value ? "true" : "false");
  } catch (err) {
    console.debug("Unable to persist localStorage flag:", key, err);
  }
}

function transformCreditWarningStorageKey(transformOrKey) {
  const rawKey = typeof transformOrKey === "string"
    ? transformOrKey
    : (transformOrKey && transformOrKey.key);
  if (!rawKey) return "";
  return `${TRANSFORM_CREDIT_WARNING_STORAGE_PREFIX}${String(rawKey).replace(/[^a-z0-9_-]/gi, "_")}`;
}

function isPersistedSheetId(sheetId) {
  return typeof sheetId === "string" && sheetId.startsWith("psheet_");
}

function isGeneratedSheet(sheet) {
  return !!sheet?.parentId;
}

function canAppendRowsToSheet(sheet) {
  return !!sheet && !isGeneratedSheet(sheet) && (sheet.entityType === "companies" || sheet.entityType === "employees");
}

function isPersistedRowId(rowId) {
  return typeof rowId === "string" && rowId.startsWith("prow_");
}

function columnConfigPayload(columns) {
  return (columns || []).map(c =>
    c && typeof c.width_px === "number" && c.width_px > 0
      ? { key: c.key, width: c.width_px }
      : c?.key,
  ).filter(Boolean);
}

function fallbackColumnForKey(entityType, key) {
  const labels = {
    work_email: "Work email",
    first_name: "First name",
    middle_name: "Middle name",
    last_name: "Last name",
    role: "Role",
    company: "Company",
    company_domain: "Company domain",
    similar_to: "Similar to",
  };
  const types = {
    work_email: "text",
    first_name: "text",
    middle_name: "text",
    last_name: "text",
    role: "text",
    company: "text",
    company_domain: "domain",
    similar_to: "text",
  };
  if (entityType !== "employees" || !labels[key]) return null;
  return {
    key,
    label: labels[key],
    width: key === "work_email" ? "w-52" : "w-40",
    type: types[key] || "text",
    group: key === "work_email" ? "contact" : "identity",
    free: key !== "work_email",
  };
}

function columnWithExistingConfig(catalogueCol, existingCol) {
  if (!catalogueCol) return null;
  if (existingCol && typeof existingCol.width_px === "number" && existingCol.width_px > 0) {
    return { ...catalogueCol, width_px: existingCol.width_px };
  }
  return catalogueCol;
}

function requiredColumnsForAddedPerson(partial) {
  const method = String(partial?._person_input_method || "").trim();
  return PERSON_ADD_ROW_METHOD_COLUMNS[method] || [];
}

function columnsWithAdditionalKeys(sheet, keys) {
  const required = (keys || []).filter(Boolean);
  if (!sheet || required.length === 0) return sheet?.columns || [];
  const currentKeys = new Set((sheet.columns || []).map(c => c.key));
  const missing = required.filter(k => !currentKeys.has(k));
  if (missing.length === 0) return sheet.columns || [];

  const catalogue = catalogueFor(sheet.entityType);
  const byKey = Object.fromEntries(catalogue.map(c => [c.key, c]));
  const existingByKey = Object.fromEntries((sheet.columns || []).map(c => [c.key, c]));
  const nextKeys = [...(sheet.columns || []).map(c => c.key), ...missing];
  return nextKeys
    .map(k => columnWithExistingConfig(byKey[k] || fallbackColumnForKey(sheet.entityType, k), existingByKey[k]))
    .filter(Boolean);
}

function columnsFromKeys(keys, byKey, existingByKey = {}) {
  return (keys || [])
    .map(k => columnWithExistingConfig(byKey[k], existingByKey[k]))
    .filter(Boolean);
}

async function persistSheetName(sheetId, name, errorFallback = "Failed to rename sheet.") {
  return fetchJSON(`/dashboard/api/prospector/sheets/${sheetId}`, {
    method: "PATCH",
    body: { name },
    errorFallback,
  });
}

async function createProspectorSheet(entityType, name) {
  const body = { entity_type: entityType };
  if (name) body.name = name;
  return fetchJSON("/dashboard/api/prospector/sheets", {
    method: "POST",
    body,
    errorFallback: "Failed to create sheet.",
  });
}

function mockEmail(name, domain) {
  if (!name) return "";
  const handle = name.toLowerCase().replace(/[^a-z]+/g, ".").replace(/^\.|\.$/g, "");
  const dom = (domain || "example.com").replace(/^https?:\/\//, "").replace(/^www\./, "");
  return `${handle}@${dom}`;
}

function descendantIds(sheets, rootId) {
  const out = [];
  const walk = (id) => {
    sheets.forEach(s => {
      if (s.parentId === id) {
        out.push(s.id);
        walk(s.id);
      }
    });
  };
  walk(rootId);
  return out;
}

function sheetTreeIds(sheets, rootId) {
  return [rootId, ...descendantIds(sheets, rootId)];
}

function rootsForSheets(sheets) {
  const ids = new Set(sheets.map(s => s.id));
  return sheets.filter(s => !s.parentId || !ids.has(s.parentId));
}

function flattenSheetTreeIds(sheets) {
  const ordered = [];
  const roots = rootsForSheets(sheets || []);
  function walk(sheet) {
    if (!sheet || ordered.includes(sheet.id)) return;
    ordered.push(sheet.id);
    (sheets || [])
      .filter(s => s.parentId === sheet.id)
      .forEach(walk);
  }
  roots.forEach(walk);
  return ordered;
}

function nextActiveSheetId(sheets, excludedIds = new Set()) {
  const next = sheets.find(s => !s.isArchived && !excludedIds.has(s.id));
  return next ? next.id : null;
}

function getLineage(sheets, sheetId) {
  // Returns ancestors-then-self, root-first.
  const chain = [];
  let cur = sheets.find(s => s.id === sheetId);
  while (cur) {
    chain.unshift(cur);
    cur = cur.parentId ? sheets.find(s => s.id === cur.parentId) : null;
  }
  return chain;
}

const SEARCH_ROW_META_KEYS = new Set([
  "_id", "_status", "_error", "_shimmerCols", "_person_input_method",
]);

function searchableCellText(value) {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) {
    return value.map(searchableCellText).filter(Boolean).join(" ");
  }
  if (typeof value === "object") {
    return Object.values(value).map(searchableCellText).filter(Boolean).join(" ");
  }
  return String(value);
}

function buildSheetSearchMatches(sheets, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return null;
  return (sheets || [])
    .map(sheet => {
      const nameText = String(sheet.name || "");
      const nameMatch = nameText.toLowerCase().includes(q);
      const columnsByKey = Object.fromEntries((sheet.columns || []).map(c => [c.key, c]));
      const cellMatches = [];
      let totalCellMatches = 0;

      (sheet.rows || []).forEach((row, rowIndex) => {
        Object.entries(row || {}).forEach(([key, value]) => {
          if (SEARCH_ROW_META_KEYS.has(key)) return;
          const text = searchableCellText(value);
          if (!text || !text.toLowerCase().includes(q)) return;
          totalCellMatches += 1;
          if (cellMatches.length >= 3) return;
          const col = columnsByKey[key];
          cellMatches.push({
            key,
            rowIndex,
            label: (col && (col.label || col.key)) || key,
            text: truncate(text, 96),
          });
        });
      });

      if (!nameMatch && totalCellMatches === 0) return null;
      return { sheet, nameMatch, cellMatches, totalCellMatches };
    })
    .filter(Boolean);
}


function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function csvEscape(value) {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (/[",\n\r]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

function exportCSV(sheet) {
  const exportable = sheet.columns.filter(c => c.key !== "logo");
  const header = exportable.map(c => csvEscape(c.label || c.key)).join(",");
  const lines = sheet.rows.map(row =>
    exportable.map(c => csvEscape(row[c.key])).join(",")
  );
  const csv = [header, ...lines].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  downloadBlob(blob, `${slugify(sheet.name)}.csv`);
}

const SAMPLE_CSVS = {
  companies: {
    filename: "prospector-companies-sample.csv",
    rows: [
      ["domain", "name", "industry"],
      ["nubela.co", "Nubela", "Sales intelligence"],
      ["stripe.com", "Stripe", "Financial infrastructure"],
    ],
  },
  employees: {
    filename: "prospector-people-sample.csv",
    rows: [
      ["work_email", "first_name", "last_name", "employer_website", "role"],
      ["alex@nubela.co", "Alex", "Lim", "nubela.co", "CEO"],
      ["patrick@stripe.com", "Patrick", "Collison", "stripe.com", "CEO"],
    ],
  },
};

function downloadSampleCSV(entityType) {
  const sampleDef = SAMPLE_CSVS[entityType] || SAMPLE_CSVS.companies;
  const csv = sampleDef.rows
    .map(row => row.map(csvEscape).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  downloadBlob(blob, sampleDef.filename);
}

function exportXLSX(sheet) {
  if (!window.XLSX) {
    alert("XLSX library not loaded.");
    return;
  }
  const exportable = sheet.columns.filter(c => c.key !== "logo");
  const headers = exportable.map(c => c.label || c.key);
  const data = [
    headers,
    ...sheet.rows.map(row => exportable.map(c => row[c.key] ?? "")),
  ];
  const ws = window.XLSX.utils.aoa_to_sheet(data);
  const wb = window.XLSX.utils.book_new();
  window.XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  window.XLSX.writeFile(wb, `${slugify(sheet.name)}.xlsx`);
}

function slugify(s) {
  return (s || "sheet").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function displayNameFromDomain(domain) {
  const base = String(domain || "")
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .split(/[/?#]/)[0]
    .split(".")[0]
    .replace(/[-_]+/g, " ")
    .trim();
  return base ? base.replace(/\b\w/g, c => c.toUpperCase()) : "Nubela";
}

function prospectorTemplateSource() {
  const product = (typeof window !== "undefined" && window.PROSPECTOR_PRODUCT) || {};
  const domain = product.template_domain || product.hostname || "nubela.co";
  return {
    domain,
    name: product.template_name || displayNameFromDomain(domain),
  };
}

// Loose parse of a single "website or company name" input. Detects a domain
// when the string contains a dot followed by 2+ alpha chars; otherwise treats
// the input as a name and leaves domain blank for backend resolution.
function parseCompanyQuery(raw) {
  const cleaned = raw.replace(/^https?:\/\//i, "").replace(/^www\./i, "").split(/[/?#]/)[0].trim();
  const looksLikeDomain = /^[\w-]+(\.[\w-]+)+$/.test(cleaned) && /\.[a-z]{2,}$/i.test(cleaned);
  if (looksLikeDomain) {
    const domain = cleaned.toLowerCase();
    const name = domain.split(".")[0].replace(/^./, c => c.toUpperCase());
    return { name, domain };
  }
  return { name: raw.trim(), domain: "" };
}

function nextDefaultName(sheets, entityType) {
  const label = entityType === "companies" ? "Companies sheet"
              : entityType === "employees" ? "People sheet"
              : entityType === "funding" ? "Funding sheet"
              : entityType === "specialties" ? "Specialties sheet"
              : entityType === "addresses" ? "Addresses sheet"
              : "Updates sheet";
  const re = new RegExp(`^${label} (\\d+)$`);
  const max = sheets.reduce((m, s) => {
    const match = s.name && s.name.match(re);
    return match ? Math.max(m, parseInt(match[1], 10)) : m;
  }, 0);
  return `${label} ${max + 1}`;
}

// ============================================================================
// Initial state
// ============================================================================

// Server-rendered by /dashboard/prospector - see prospector.html. The empty
// fallback keeps marketing-page demo embeds working when the route doesn't run.
// Marketing pages set window.PROSPECTOR_USE_DEMO_SHEETS = true to seed the three
// chip-driven demo sheets (Stripe customers/competitors/people).
const INITIAL_SHEETS = (typeof window !== "undefined" && window.PROSPECTOR_USE_DEMO_SHEETS === true)
  ? buildDemoSheets()
  : (typeof window !== "undefined" && Array.isArray(window.PROSPECTOR_INITIAL_SHEETS))
    ? window.PROSPECTOR_INITIAL_SHEETS
    : [];

function firstActiveSheetId(sheets) {
  const sheet = (sheets || []).find(s => !s.isArchived);
  return sheet ? sheet.id : null;
}

function sheetLifecycleVerb(action) {
  return action === "restore" ? "unarchive" : action;
}

function activeSheetIdExists(sheets, sheetId) {
  return !!sheetId && (sheets || []).some(s => s.id === sheetId && !s.isArchived);
}

function sheetIdExists(sheets, sheetId) {
  return !!sheetId && (sheets || []).some(s => s.id === sheetId);
}

function getProspectorProductId() {
  if (typeof window === "undefined") return "";
  return (window.PROSPECTOR_PRODUCT && window.PROSPECTOR_PRODUCT.id)
    ? String(window.PROSPECTOR_PRODUCT.id)
    : "";
}

function prospectorLastSheetStorageKey(productId) {
  return productId ? `prospector_last_viewed_sheet_${productId}` : "";
}

function readLastViewedSheetId(sheets, productId) {
  const key = prospectorLastSheetStorageKey(productId);
  if (!key || typeof window === "undefined") return null;
  try {
    const sheetId = localStorage.getItem(key);
    if (isPersistedSheetId(sheetId) && activeSheetIdExists(sheets, sheetId)) return sheetId;
    if (sheetId) localStorage.removeItem(key);
  } catch (err) {
    console.debug("Unable to read last viewed Prospector sheet:", key, err);
  }
  return null;
}

function writeLastViewedSheetId(sheetId, productId) {
  const key = prospectorLastSheetStorageKey(productId);
  if (!key || !isPersistedSheetId(sheetId) || typeof window === "undefined") return;
  try {
    localStorage.setItem(key, sheetId);
  } catch (err) {
    console.debug("Unable to persist last viewed Prospector sheet:", key, err);
  }
}

function prospectorPathSheetId() {
  if (typeof window === "undefined") return "";
  const match = window.location.pathname.match(/\/dashboard\/prospector\/([^/]+)\/?$/);
  return match ? match[1] : "";
}

function resolveProspectorLandingSheetId(sheets, productId) {
  return readLastViewedSheetId(sheets, productId) || firstActiveSheetId(sheets);
}

function resolveInitialActiveSheetId(sheets, demoMode, productId) {
  if (demoMode) return (sheets || [])[0]?.id || null;
  if (typeof window === "undefined") return firstActiveSheetId(sheets);
  if (window.location.hash === "#new") return null;

  const initial = (window.PROSPECTOR_INITIAL_SHEET || "").trim();
  const explicitSheetId = initial || prospectorPathSheetId();
  if (explicitSheetId) {
    return sheetIdExists(sheets, explicitSheetId)
      ? explicitSheetId
      : firstActiveSheetId(sheets);
  }

  return resolveProspectorLandingSheetId(sheets, productId);
}

function hasPendingColumnKeys(row, keys) {
  return (row._shimmerCols || []).some(k => keys.includes(k));
}

function refreshSlowColumnProgress(progress, rows) {
  if (!progress || progress.kind !== "slow_column") return progress;
  const keys = progress.keys || [];
  if (keys.length === 0) return progress;
  const rowIds = new Set(progress.rowIds || rows.filter(r => r.domain).map(r => r._id));
  const scopedRows = rows.filter(r => rowIds.has(r._id));
  const total = progress.total || scopedRows.length;
  if (total === 0) return null;
  const pending = scopedRows.filter(r => hasPendingColumnKeys(r, keys)).length;
  const done = Math.max(0, total - pending);
  if (pending === 0) return null;
  return { ...progress, done, total };
}

function eligibleRowsForPaidAction(action, rows) {
  if (action?.action_kind === WORK_EMAIL_ACTION_KIND) {
    return (rows || []).filter(workEmailLookupInputAvailable);
  }
  return (rows || []).filter(r => {
    if (!r.domain) return false;
    if (action.action_kind === EMPLOYEE_COUNT_ACTION_KIND) {
      return r.employee_count === null || r.employee_count === undefined || r.employee_count === "";
    }
    return true;
  });
}

function paidActionTargetKeys(action) {
  if (Array.isArray(action?.columns) && action.columns.length > 0) {
    return action.columns;
  }
  return action?.key ? [action.key] : [];
}

function isPaidColumnActionAvailable(action, columns, rows = []) {
  const existingKeys = new Set((columns || []).map(c => c.key));
  if (action?.action_kind === WORK_EMAIL_ACTION_KIND && existingKeys.has("work_email")) {
    return eligibleRowsForPaidAction(action, rows).length > 0;
  }
  return paidActionTargetKeys(action).every(k => !existingKeys.has(k));
}

const PEOPLE_SPREADSHEET_ACTION_KEYS = new Set([
  "get-similar-people",
  "get-work-experience",
  "get-education-history",
]);
const PROFILE_FALLBACK_TRANSFORM_KEYS = new Set([
  "get-work-experience",
  "get-education-history",
]);

function personTransformSourceKey(row) {
  if (!row) return "";
  const personId = String(row.id || row.person_id || "").trim().toLowerCase();
  if (personId) return `id:${personId}`;
  const slug = String(row.slug || "").trim().toLowerCase();
  if (slug) return `slug:${slug}`;
  const workEmail = String(row.work_email || "").trim().toLowerCase();
  if (workEmail) return `email:${workEmail}`;
  const firstName = String(row.first_name || "").trim().toLowerCase();
  const middleName = String(row.middle_name || "").trim().toLowerCase();
  const lastName = String(row.last_name || "").trim().toLowerCase();
  const employerWebsite = String(row.employer_website || row.company_domain || row.domain || "").trim().toLowerCase();
  const role = String(row.role || row.title || "").trim().toLowerCase();
  return [firstName, middleName, lastName, employerWebsite, role].join("|");
}

function eligibleRowsForTransform(transform, rows) {
  if (transform?.unwired) return [];
  if (PEOPLE_SPREADSHEET_ACTION_KEYS.has(transform?.actionKey)) {
    const seen = new Set();
    return (rows || []).filter(r => {
      if (!employeeProfileLookupInputAvailable(r)) return false;
      const key = personTransformSourceKey(r);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }
  if (transform?.actionKey || transform?.key === "competitors") {
    const seen = new Set();
    return (rows || []).filter(r => {
      const domain = String(r.domain || "").trim().toLowerCase();
      if (!domain || seen.has(domain)) return false;
      seen.add(domain);
      return true;
    });
  }
  return rows || [];
}

function buildSlowColumnProgress(action, rows, keys) {
  const rowIds = eligibleRowsForPaidAction(action, rows).map(r => r._id);
  if (rowIds.length === 0) return null;
  return refreshSlowColumnProgress({
    kind: "slow_column",
    label: action.action_kind === EMPLOYEE_COUNT_ACTION_KIND ? "Getting employee counts" : action.label,
    done: 0,
    total: rowIds.length,
    keys,
    rowIds,
    message: "This can take a while. You can check back later; rows fill in as they finish.",
  }, rows);
}

function deriveSlowBackfillProgress(sheet) {
  if (!sheet || sheet.progress || sheet.entityType !== "companies") return sheet;
  const visibleKeys = new Set((sheet.columns || []).map(c => c.key));
  const hasEmployeeCountColumn = EMPLOYEE_COUNT_COLUMN_KEYS.some(k => visibleKeys.has(k));
  if (!hasEmployeeCountColumn) return sheet;
  const pendingRows = (sheet.rows || []).filter(r => hasPendingColumnKeys(r, EMPLOYEE_COUNT_COLUMN_KEYS));
  if (pendingRows.length === 0) return sheet;
  const progress = refreshSlowColumnProgress({
    kind: "slow_column",
    label: "Getting employee counts",
    done: 0,
    total: pendingRows.length,
    keys: EMPLOYEE_COUNT_COLUMN_KEYS,
    rowIds: pendingRows.map(r => r._id),
    message: "This can take a while. You can check back later; rows fill in as they finish.",
  }, sheet.rows || []);
  return progress ? { ...sheet, progress } : sheet;
}

function hydrateInitialSheets(sheets) {
  return (sheets || []).map(deriveSlowBackfillProgress);
}

// Pure reducer used by the websocket-update handler and by every future row
// mutation (transforms, manual edits, removal). Returns a new sheets array
// with the patch merged into the matching row's data and status fields.
// Reserved keys: _id, _status, _error, _shimmerCols.
function updateRow(sheets, sheetId, rowId, patch) {
  if (!sheets || !sheetId || !rowId) return sheets;
  return sheets.map(s => {
    if (s.id !== sheetId) return s;
    const rows = s.rows.map(r => {
      if (r._id !== rowId) return r;
      const merged = { ...r, ...(patch || {}) };
      // When a job moves to a terminal settled status, drop the underscore-
      // prefixed shimmer / error markers so the row renders cleanly.
      if (JOB_STATUS_CLEARS_LOADING.has(merged._status)) {
        delete merged._status;
        delete merged._error;
        delete merged._shimmerCols;
      } else if (merged._status === JOB_STATUS.FAILED) {
        delete merged._shimmerCols;
      } else if (JOB_STATUS_ACTIVE.has(merged._status)) {
        delete merged._error;
      }
      return merged;
    });
    const nextSheet = { ...s, rows };
    return { ...nextSheet, progress: refreshSlowColumnProgress(nextSheet.progress, rows) };
  });
}

function appendRowsToSheet(sheets, sheetId, rows, progress) {
  const incoming = Array.isArray(rows) ? rows : [];
  return sheets.map(s => {
    if (s.id !== sheetId) return s;
    const seen = new Set((s.rows || []).map(r => r._id));
    const nextRows = [...(s.rows || [])];
    incoming.forEach(row => {
      if (!row || !row._id || seen.has(row._id)) return;
      seen.add(row._id);
      nextRows.push(row);
    });
    return {
      ...s,
      rows: nextRows,
      progress: progress === undefined ? s.progress : progress,
      emptyState: incoming.length > 0 ? null : s.emptyState,
    };
  });
}

function sheetJobFromEvent(sheet, evt) {
  return {
    ...(sheet.job || {}),
    id: evt.job_id || sheet.job?.id || "",
    kind: evt.kind || sheet.job?.kind || "",
    target_kind: evt.target_kind || sheet.job?.target_kind || "sheet",
    target_id: evt.target_id || sheet.job?.target_id || sheet.id,
    status: evt.status || sheet.job?.status || "",
    error: evt.error || "",
    partial_result: evt.partial_result === true,
    stop_reason: evt.stop_reason || null,
    credit_error: evt.credit_error || null,
    appended_rows: evt.appended_rows ?? sheet.job?.appended_rows ?? null,
  };
}

function updateSheetJobState(sheets, sheetId, evt) {
  return sheets.map(s => {
    if (s.id !== sheetId) return s;
    const job = sheetJobFromEvent(s, evt);
    if (JOB_STATUS_CLEARS_LOADING.has(evt.status) || evt.status === JOB_STATUS.FAILED) {
      return {
        ...s,
        job,
        progress: null,
        emptyState: evt.status === JOB_STATUS.ABORTED && (s.rows || []).length === 0 ? "aborted" : s.emptyState,
      };
    }
    if (evt.progress) {
      return { ...s, job, progress: evt.progress };
    }
    return { ...s, job };
  });
}

// Close-on-outside-click: accepts a single ref or an array of refs (the
// portaled column picker has both a trigger and a panel). The handler reads
// `ref.current` lazily, so we only re-bind when `active` flips.
function useClickOutside(refs, onOutside, active = true) {
  useEffect(() => {
    if (!active) return;
    const list = Array.isArray(refs) ? refs : [refs];
    function onDoc(e) {
      if (list.some(r => r?.current && r.current.contains(e.target))) return;
      onOutside();
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [active]);
}

// Custom hook: open a socket.io connection on the prospector dashboard,
// subscribe to the user's prospector room, and forward each
// `server.prospector_job_update` event to the caller-supplied handler.
//
// Owns connect/auth/cleanup/reconnect. Spreadsheet rendering stays unaware of
// websocket plumbing. Skips entirely in demo mode (marketing pages).
function useProspectorSocket(productId, onJobUpdate) {
  useEffect(() => {
    if (!productId) return;
    if (typeof window === "undefined" || window.PROSPECTOR_DEMO_MODE === true) return;
    if (typeof window.io !== "function") {
      console.warn("Prospector: socket.io client not loaded");
      return;
    }

    const socket = window.io({
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      forceNew: true,
    });

    function subscribe() {
      socket.emit("prospector.subscribe", { product_id: productId });
    }

    socket.on("connect", subscribe);
    socket.on("reconnect", subscribe);
    socket.on("server.auth_failed", (data) => {
      console.error("Prospector: socket auth_failed", data);
    });
    socket.on("server.prospector_job_update", (evt) => {
      try {
        onJobUpdate(evt);
      } catch (err) {
        console.error("Prospector: error handling job update", err, evt);
      }
    });

    return () => {
      socket.off("connect", subscribe);
      socket.off("reconnect", subscribe);
      socket.off("server.prospector_job_update");
      socket.off("server.auth_failed");
      try {
        socket.disconnect();
      } catch (err) {
        console.debug("Prospector: socket disconnect failed", err);
      }
    };
  }, [productId, onJobUpdate]);
}

// ============================================================================
// Top-level App
// ============================================================================

function ProspectorApp() {
  // Demo mode: marketing pages set window.PROSPECTOR_DEMO_MODE = true to
  // suppress URL syncing, hash handling, and PROSPECTOR_INITIAL_SHEET reads.
  const demoMode = typeof window !== "undefined" && window.PROSPECTOR_DEMO_MODE === true;
  const productId = getProspectorProductId();

  const [sheets, setSheets] = useState(() => hydrateInitialSheets(deepClone(INITIAL_SHEETS)));
  const [activeId, setActiveId] = useState(() => resolveInitialActiveSheetId(INITIAL_SHEETS, demoMode, productId));
  const [propagateMsg, setPropagateMsg] = useState(null);
  const [modal, setModal] = useState(null); // { kind, ... }
  const [treeOpen, setTreeOpen] = useState(false);   // mobile drawer state for the tree
  const [selectedSheetIds, setSelectedSheetIds] = useState([]);
  const cancelTokensRef = useRef({}); // sheetId -> { cancelled }

  const activeSheet = sheets.find(s => s.id === activeId) || null;

  function showOutOfCreditsModal(err) {
    const details = insufficientCreditsDetails(err);
    setModal({
      kind: "outOfCredits",
      ...details,
    });
  }

  // Sync activeId → URL (skipped in demo mode so the marketing page URL stays put)
  useEffect(() => {
    if (demoMode) return;
    if (!activeId) {
      if (window.location.pathname !== "/dashboard/prospector" || window.location.hash !== "#new") {
        window.history.replaceState({}, "", "/dashboard/prospector#new");
      }
    } else {
      const target = `/dashboard/prospector/${activeId}`;
      if (window.location.pathname !== target || window.location.hash) {
        window.history.pushState({}, "", target);
      }
    }
  }, [activeId, demoMode]);

  useEffect(() => {
    if (demoMode || !activeSheet || activeSheet.isArchived) return;
    writeLastViewedSheetId(activeSheet.id, productId);
  }, [activeSheet, productId, demoMode]);

  useEffect(() => {
    setSelectedSheetIds(prev => {
      const live = new Set(sheets.map(s => s.id));
      const next = prev.filter(id => live.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [sheets]);

  // Listen for back/forward (skipped in demo mode)
  useEffect(() => {
    if (demoMode) return;
    function onPop() {
      if (window.location.hash === "#new") {
        setActiveId(null);
        return;
      }
      const sheetId = prospectorPathSheetId();
      if (sheetIdExists(sheets, sheetId)) {
        setActiveId(sheetId);
      } else if (!sheetId) {
        setActiveId(resolveProspectorLandingSheetId(sheets, productId));
      } else {
        setActiveId(null);
      }
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [sheets, productId, demoMode]);

  // Handle #new deep links (e.g. the Tools page "New Sheet" action) landing on this mounted app.
  useEffect(() => {
    if (demoMode) return;
    function onHash() {
      if (window.location.hash === "#new") {
        setActiveId(null);
      } else if (!prospectorPathSheetId()) {
        setActiveId(resolveProspectorLandingSheetId(sheets, productId));
      }
    }
    onHash();
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [sheets, productId, demoMode]);

  // Expose a hook so demo-mode pages can switch sheets via external chips/buttons.
  useEffect(() => {
    if (!demoMode) return;
    window.PROSPECTOR_SHOW_SHEET = (id) => {
      if (sheets.some(s => s.id === id)) setActiveId(id);
    };
    return () => {
      if (window.PROSPECTOR_SHOW_SHEET) delete window.PROSPECTOR_SHOW_SHEET;
    };
  }, [demoMode, sheets]);

  // ── Propagation toast ─────────────────────────────────────
  const flashPropagate = useCallback((sheetId) => {
    const desc = descendantIds(sheets, sheetId);
    if (desc.length === 0) return;
    setPropagateMsg(`Propagating change to ${desc.length} child sheet${desc.length === 1 ? "" : "s"}…`);
    setTimeout(() => setPropagateMsg(null), 1400);
  }, [sheets]);

  // ── Live updates over websocket ───────────────────────────
  // Real-time job updates (row enrichment now, transforms / column actions
  // later) are pushed from the prospector-worker pod via the same socket.io
  // infra that powers live chat. The hook subscribes the user to
  // prospector:{user_id}:{product_id} on mount and forwards each
  // server.prospector_job_update event to the row-patcher below.
  const handleJobUpdate = useCallback((evt) => {
    if (!evt || !evt.sheet_id || !evt.target_id) return;
    if (evt.target_kind === "sheet") {
      setSheets(prev => {
        const withRows = evt.event === "rows_appended"
          ? appendRowsToSheet(prev, evt.sheet_id, evt.rows, evt.progress)
          : prev;
        return updateSheetJobState(withRows, evt.sheet_id, evt);
      });
      if (evt.status === JOB_STATUS.FAILED && isInsufficientCreditsError({ message: evt.error || "" })) {
        showOutOfCreditsModal({
          message: evt.error || "Insufficient credits.",
          body: evt.credit_error || {},
        });
      }
      return;
    }
    const patch = evt.row
      ? { ...evt.row }
      : { _status: evt.status, _error: evt.error || "" };
    setSheets(prev => updateRow(prev, evt.sheet_id, evt.target_id, patch));
    if (evt.status === JOB_STATUS.FAILED && isInsufficientCreditsError({ message: evt.error || "" })) {
      showOutOfCreditsModal({
        message: evt.error || "Insufficient credits.",
        body: evt.credit_error || {},
      });
    }
  }, []);
  useProspectorSocket(productId, handleJobUpdate);

  // ── Mutations ─────────────────────────────────────────────

  async function removeRows(sheetId, rowIds) {
    const sheet = sheets.find(s => s.id === sheetId);
    if (!sheet) return;
    const idSet = new Set((rowIds || []).filter(Boolean));
    if (idSet.size === 0) return;
    const removedRows = sheet.rows
      .map((row, index) => ({ row, index }))
      .filter(item => idSet.has(item.row._id));
    if (removedRows.length === 0) return;

    setSheets(prev => patchSheet(prev, sheetId, s => ({
      rows: s.rows.filter(r => !idSet.has(r._id)),
    })));

    try {
      const persistedRowIds = removedRows
        .map(item => item.row._id)
        .filter(isPersistedRowId);
      if (!demoMode && isPersistedSheetId(sheetId) && persistedRowIds.length > 0) {
        await fetchJSON(`/dashboard/api/prospector/sheets/${sheetId}/rows`, {
          method: "DELETE",
          body: { row_ids: persistedRowIds },
          errorFallback: persistedRowIds.length === 1 ? "Failed to remove row." : "Failed to remove rows.",
        });
      }
    } catch (err) {
      setSheets(prev => patchSheet(prev, sheetId, s => {
        if (removedRows.every(item => s.rows.some(r => r._id === item.row._id))) return { rows: s.rows };
        const rows = [...s.rows];
        removedRows.forEach(({ row, index }) => {
          if (rows.some(r => r._id === row._id)) return;
          rows.splice(Math.min(index, rows.length), 0, row);
        });
        return { rows };
      }));
      alert(err.message || "Failed to remove rows. Please try again.");
    }
  }

  async function removeRow(sheetId, rowId) {
    return removeRows(sheetId, [rowId]);
  }

  async function retryRow(sheetId, rowId) {
    if (demoMode || !isPersistedSheetId(sheetId) || !isPersistedRowId(rowId)) return;
    const sheet = sheets.find(s => s.id === sheetId);
    const row = sheet && sheet.rows.find(r => r._id === rowId);
    if (!row) return;
    const prevError = row._error || "";

    // Optimistic pending: the updateRow reducer clears _error for active
    // statuses, which also hides the retry button against double-clicks.
    setSheets(prev => updateRow(prev, sheetId, rowId, { _status: JOB_STATUS.PENDING }));

    try {
      const body = await fetchJSON(`/dashboard/api/prospector/sheets/${sheetId}/rows/${rowId}/retry`, {
        method: "POST",
        errorFallback: "Failed to retry row.",
      });
      if (body && body.row) {
        setSheets(prev => updateRow(prev, sheetId, rowId, { ...body.row }));
      }
    } catch (err) {
      setSheets(prev => updateRow(prev, sheetId, rowId, {
        _status: JOB_STATUS.FAILED,
        _error: err.message || prevError || "Failed to retry row.",
      }));
      if (isInsufficientCreditsError(err)) {
        showOutOfCreditsModal(err);
      }
    }
  }

  async function retrySheetJob(sheetId) {
    if (demoMode || !isPersistedSheetId(sheetId)) return;
    const sheet = sheets.find(s => s.id === sheetId);
    if (!sheet) return;
    const prevError = sheet.job?.error || "";

    setSheets(prev => prev.map(s => (
      s.id === sheetId
        ? {
            ...s,
            job: { ...(s.job || {}), status: JOB_STATUS.PENDING, error: "" },
            progress: s.progress || { kind: "transform", label: "Working", done: 0, total: 1 },
          }
        : s
    )));

    try {
      const body = await fetchJSON(`/dashboard/api/prospector/sheets/${sheetId}/jobs/retry`, {
        method: "POST",
        errorFallback: "Failed to retry spreadsheet action.",
      });
      if (body && body.sheet) {
        setSheets(prev => prev.map(s => (
          s.id === sheetId
            ? { ...s, ...body.sheet, job: body.job || body.sheet.job || s.job }
            : s
        )));
      }
    } catch (err) {
      setSheets(prev => prev.map(s => (
        s.id === sheetId
          ? {
              ...s,
              progress: null,
              job: {
                ...(s.job || {}),
                status: JOB_STATUS.FAILED,
                error: err.message || prevError || "Failed to retry spreadsheet action.",
              },
            }
          : s
      )));
      if (isInsufficientCreditsError(err)) {
        showOutOfCreditsModal(err);
      }
    }
  }

  function cancelSheetTokens(sheetIds) {
    sheetIds.forEach(id => {
      const token = cancelTokensRef.current[id];
      if (token) {
        token.cancelled = true;
        delete cancelTokensRef.current[id];
      }
    });
  }

  function applySheetLifecycleState(action, sheetId, affectedIds) {
    const ids = new Set((affectedIds && affectedIds.length) ? affectedIds : sheetTreeIds(sheets, sheetId));
    cancelSheetTokens(ids);

    if (action === "delete") {
      setSheets(prev => prev.filter(s => !ids.has(s.id)));
      if (ids.has(activeId)) setActiveId(nextActiveSheetId(sheets, ids));
      return;
    }

    if (action === "archive") {
      setSheets(prev => prev.map(s => ids.has(s.id) ? { ...s, isArchived: true } : s));
      if (ids.has(activeId)) setActiveId(nextActiveSheetId(sheets, ids));
      return;
    }

    if (action === "restore") {
      setSheets(prev => prev.map(s => ids.has(s.id) ? { ...s, isArchived: false } : s));
      setActiveId(sheetId);
    }
  }

  async function runSheetLifecycleAction(action, sheetId, { confirmDescendants = false } = {}) {
    const sheet = sheets.find(s => s.id === sheetId);
    if (!sheet) return;

    const method = action === "delete" ? "DELETE" : "POST";
    const suffix = action === "archive" ? "/archive" : action === "restore" ? "/restore" : "";
    const actionVerb = sheetLifecycleVerb(action);

    try {
      let body;
      if (action !== "restore") {
        body = { confirm_descendants: confirmDescendants };
      }
      const response = (!demoMode && isPersistedSheetId(sheetId))
        ? await fetchJSON(`/dashboard/api/prospector/sheets/${sheetId}${suffix}`, {
            method,
            body,
            errorFallback: `Failed to ${actionVerb} spreadsheet.`,
          })
        : { affected_ids: sheetTreeIds(sheets, sheetId) };

      applySheetLifecycleState(action, sheetId, response.affected_ids);
    } catch (err) {
      if (err.status === 409 && err.body?.child_count) {
        setModal({
          kind: "sheetLifecycleConfirm",
          action,
          sheetId,
          childCount: err.body.child_count,
        });
        return;
      }
      alert(err.message || `Failed to ${actionVerb} spreadsheet. Please try again.`);
    }
  }

  function requestSheetLifecycleAction(action, sheetId) {
    const sheet = sheets.find(s => s.id === sheetId);
    if (!sheet) return;
    const childCount = descendantIds(sheets, sheetId).length;
    if ((action === "archive" || action === "delete") && childCount > 0) {
      setModal({ kind: "sheetLifecycleConfirm", action, sheetId, childCount });
      return;
    }
    runSheetLifecycleAction(action, sheetId);
  }

  function rootMostSheetIds(ids) {
    const selected = new Set(ids || []);
    return (ids || []).filter(id => {
      let cur = sheets.find(s => s.id === id);
      while (cur && cur.parentId) {
        if (selected.has(cur.parentId)) return false;
        cur = sheets.find(s => s.id === cur.parentId);
      }
      return true;
    });
  }

  async function bulkArchiveSheets(sheetIds) {
    const activeIds = (sheetIds || []).filter(id => {
      const sheet = sheets.find(s => s.id === id);
      return sheet && !sheet.isArchived;
    });
    const ids = rootMostSheetIds(Array.from(new Set(activeIds)));
    if (ids.length === 0) return;
    setSelectedSheetIds([]);
    for (const id of ids) {
      await runSheetLifecycleAction("archive", id, { confirmDescendants: true });
    }
  }

  async function bulkRestoreSheets(sheetIds) {
    const archivedIds = (sheetIds || []).filter(id => {
      const sheet = sheets.find(s => s.id === id);
      return sheet && sheet.isArchived;
    });
    const ids = rootMostSheetIds(Array.from(new Set(archivedIds)));
    if (ids.length === 0) return;
    setSelectedSheetIds([]);
    for (const id of ids) {
      await runSheetLifecycleAction("restore", id, { confirmDescendants: true });
    }
  }

  async function addRow(sheetId, partial) {
    const sheet = sheets.find(s => s.id === sheetId);
    if (!sheet) return;
    if (!canAppendRowsToSheet(sheet)) return;

    // Company and persisted People sheets route through the backend job
    // substrate so row enrichment is real and websocket-driven.
    if (
      sheet.entityType === "companies"
      || (sheet.entityType === "employees" && !demoMode && isPersistedSheetId(sheetId))
    ) {
      try {
        const body = await fetchJSON(`/dashboard/api/prospector/sheets/${sheetId}/rows`, {
          method: "POST",
          body: partial || {},
          errorFallback: "Failed to add row.",
        });
        const newRow = body.row;
        setSheets(prev => patchSheet(prev, sheetId, s => ({
          columns: Array.isArray(body.columns)
            ? body.columns
            : columnsWithAdditionalKeys(s, requiredColumnsForAddedPerson(newRow || partial)),
          rows: [...s.rows, newRow],
        })));
        flashPropagate(sheetId);
      } catch (err) {
        console.error("addRow failed:", err);
        if (isInsufficientCreditsError(err)) {
          showOutOfCreditsModal(err);
          return;
        }
        alert(err.message || "Network error adding row.");
      }
      return;
    }

    // Non-persisted local sheets can only keep the user's entered row data;
    // there is no server sheet id to attach a real enrichment job to.
    const newRowId = uid("row");
    setSheets(prev => patchSheet(prev, sheetId, s => {
      const newRow = { _id: newRowId, ...partial };
      return {
        columns: columnsWithAdditionalKeys(s, requiredColumnsForAddedPerson(newRow)),
        rows: [...s.rows, newRow],
      };
    }));
    flashPropagate(sheetId);
  }

  // Add a paid column. For non-empty sheets this routes
  // through the PaidColumnConfirmModal so the user sees total credit cost
  // before committing. The actual add+backfill happens in confirmAddColumn.
  function addColumn(sheetId, action) {
    const sheet = sheets.find(s => s.id === sheetId);
    if (!sheet) return;
    const eligibleRowCount = eligibleRowsForPaidAction(action, sheet.rows).length;
    if (eligibleRowCount > 0 && action.action_kind && !demoMode) {
      setModal({
        kind: "paidConfirm",
        sheetId,
        action,
        rowCount: eligibleRowCount,
      });
      return;
    }
    confirmAddColumn(sheetId, action);
  }

  async function confirmAddColumn(sheetId, action) {
    const sheet = sheets.find(s => s.id === sheetId);
    if (!sheet) return;

    // Demo mode (marketing pages) keeps the legacy setTimeout mock so the
    // standalone surface still animates without a backend.
    if (demoMode || !action.action_kind) {
      _legacyMockAddColumn(sheetId, action);
      return;
    }

    // Resolve which catalogue keys this action introduces. Falls back to the
    // action's declared `columns` array if the catalogue isn't bootstrapped.
    const cat = catalogueFor(sheet.entityType);
    const byKey = Object.fromEntries(cat.map(c => [c.key, c]));
    const existingByKey = Object.fromEntries(sheet.columns.map(c => [c.key, c]));
    const newKeys = paidActionTargetKeys(action).filter(k => byKey[k]);
    if (newKeys.length === 0) {
      _legacyMockAddColumn(sheetId, action);
      return;
    }

    const missingKeys = newKeys.filter(k => !existingByKey[k]);
    const eligibleRows = eligibleRowsForPaidAction(action, sheet.rows);
    const canBackfillExistingWorkEmail = (
      action.action_kind === WORK_EMAIL_ACTION_KIND
      && eligibleRows.length > 0
      && newKeys.some(k => existingByKey[k])
    );
    if (missingKeys.length === 0 && !canBackfillExistingWorkEmail) {
      return;
    }

    const finalKeys = [...sheet.columns.map(c => c.key), ...missingKeys];
    const optimisticCols = columnsFromKeys(finalKeys, byKey, existingByKey);

    const eligibleRowIds = new Set(eligibleRows.map(r => r._id));
    const shimmerKeys = missingKeys.length > 0 ? missingKeys : newKeys;
    const optimisticRows = sheet.rows.map(r => {
      if (!eligibleRowIds.has(r._id)) return r;
      return {
        ...r,
        _shimmerCols: mergeShimmerCols(r._shimmerCols, shimmerKeys),
        _status: JOB_STATUS.PENDING,
      };
    });
    const optimisticProgress = action.action_kind === EMPLOYEE_COUNT_ACTION_KIND
      ? buildSlowColumnProgress(action, optimisticRows, shimmerKeys)
      : null;

    // Optimistic shimmer on eligible rows so the UI commits immediately.
    setSheets(prev => patchSheet(prev, sheetId, s => ({
      columns: optimisticCols,
      rows: s.rows.map(r => {
        if (!eligibleRowIds.has(r._id)) return r;
        return {
          ...r,
          _shimmerCols: mergeShimmerCols(r._shimmerCols, shimmerKeys),
          _status: JOB_STATUS.PENDING,
        };
      }),
      progress: optimisticProgress,
    })));

    try {
      const body = await fetchJSON(`/dashboard/api/prospector/sheets/${sheetId}/columns`, {
        method: "PATCH",
        body: { columns: columnConfigPayload(optimisticCols), enqueue_backfill: action.action_kind },
        errorFallback: "Failed to add column.",
      });
      if (Array.isArray(body.columns)) {
        setSheets(prev => prev.map(s => s.id === sheetId ? { ...s, columns: body.columns } : s));
      }
      flashPropagate(sheetId);
    } catch (err) {
      // Rollback to previous columns; the websocket-driven update will
      // eventually reconcile if any jobs did get enqueued.
      setSheets(prev => prev.map(s => s.id === sheetId ? { ...s, columns: sheet.columns, rows: sheet.rows, progress: sheet.progress } : s));
      if (isInsufficientCreditsError(err)) {
        showOutOfCreditsModal(err);
        return;
      }
      alert(err.message || "Failed to add column. Please try again.");
    }
  }

  // Legacy demo-mode setTimeout-based mock - preserved so marketing pages
  // and the no-backend isolated harness still animate column adds.
  function _legacyMockAddColumn(sheetId, action) {
    let total = 0;
    setSheets(prev => prev.map(s => {
      if (s.id !== sheetId) return s;
      total = s.rows.length;
      const newCols = [...s.columns, { key: action.key, label: action.label.replace(/^Get /, ""), width: "w-40" }];
      const newRows = s.rows.map(r => ({ ...r, [action.key]: null, _shimmerCols: [...(r._shimmerCols || []), action.key] }));
      return {
        ...s,
        columns: newCols,
        rows: newRows,
        progress: total > 0 ? { kind: "add_column", label: `Enriching ${action.label.replace(/^Get /, "")}`, done: 0, total } : null,
      };
    }));
    flashPropagate(sheetId);

    if (total === 0) return;

    const token = { cancelled: false };
    cancelTokensRef.current[sheetId] = token;

    let done = 0;
    const tick = () => {
      if (token.cancelled) return;
      let advanced = false;
      setSheets(prev => prev.map(s => {
        if (s.id !== sheetId) return s;
        const stillShimmering = s.rows.findIndex(r => (r._shimmerCols || []).includes(action.key));
        if (stillShimmering === -1) return s;
        advanced = true;
        const newRows = s.rows.map((r, i) => {
          if (i !== stillShimmering) return r;
          const value = typeof action.mockValueFor === "function" ? action.mockValueFor(r) : "-";
          return {
            ...r,
            [action.key]: value,
            _shimmerCols: (r._shimmerCols || []).filter(k => k !== action.key),
          };
        });
        done += 1;
        const finished = done >= total;
        return {
          ...s,
          rows: newRows,
          progress: finished ? null : { ...s.progress, done, total },
        };
      }));
      if (!advanced || done >= total) {
        delete cancelTokensRef.current[sheetId];
        return;
      }
      setTimeout(tick, 90);
    };
    setTimeout(tick, 250);
  }

  async function moveColumn(sheetId, fromKey, toKey) {
    if (fromKey === toKey) return;
    const sheet = sheets.find(s => s.id === sheetId);
    if (!sheet) return;
    const originalIdx = sheet.columns.findIndex(c => c.key === fromKey);
    if (originalIdx === -1) return;
    const nextCols = [...sheet.columns];
    const [moved] = nextCols.splice(originalIdx, 1);
    const newToIdx = nextCols.findIndex(c => c.key === toKey);
    if (newToIdx === -1) return;
    nextCols.splice(newToIdx, 0, moved);

    setSheets(prev => prev.map(s => s.id === sheetId ? { ...s, columns: nextCols } : s));

    if (demoMode || !isPersistedSheetId(sheetId)) return;

    try {
      const body = await fetchJSON(`/dashboard/api/prospector/sheets/${sheetId}/columns`, {
        method: "PATCH",
        body: { columns: columnConfigPayload(nextCols) },
        errorFallback: "Failed to move column.",
      });
      if (Array.isArray(body.columns)) {
        setSheets(prev => prev.map(s => s.id === sheetId ? { ...s, columns: body.columns } : s));
      }
    } catch (err) {
      setSheets(prev => prev.map(s => s.id === sheetId ? { ...s, columns: sheet.columns } : s));
      alert(err.message || "Failed to move column. Please try again.");
    }
  }

  async function sortRows(sheetId, columnKey, direction) {
    const sheet = sheets.find(s => s.id === sheetId);
    if (!sheet) return;
    const col = sheet.columns.find(c => c.key === columnKey);
    if (!col) return;

    const previousRows = sheet.rows;
    const previousOrder = previousRows.map(r => r._id);
    const nextRows = sortRowsByColumn(previousRows, col, direction);
    if (sameRowOrder(previousRows, nextRows)) return;

    setSheets(prev => patchSheet(prev, sheetId, () => ({ rows: nextRows })));

    if (demoMode || !isPersistedSheetId(sheetId)) return;

    try {
      await fetchJSON(`/dashboard/api/prospector/sheets/${sheetId}/rows/order`, {
        method: "PATCH",
        body: { row_ids: nextRows.map(r => r._id) },
        errorFallback: "Failed to sort rows.",
      });
    } catch (err) {
      setSheets(prev => patchSheet(prev, sheetId, s => {
        return { rows: restoreRowsByOrder(s.rows, previousOrder) };
      }));
      alert(err.message || "Failed to sort rows. Please try again.");
    }
  }

  async function moveRow(sheetId, fromRowId, toRowId, placement = "before") {
    if (fromRowId === toRowId) return;
    const sheet = sheets.find(s => s.id === sheetId);
    if (!sheet) return;

    const previousRows = sheet.rows || [];
    const previousOrder = previousRows.map(r => r._id);
    const nextRows = moveRowAround(previousRows, fromRowId, toRowId, placement);
    if (sameRowOrder(previousRows, nextRows)) return;

    setSheets(prev => patchSheet(prev, sheetId, () => ({ rows: nextRows })));

    if (demoMode || !isPersistedSheetId(sheetId)) return;

    try {
      await fetchJSON(`/dashboard/api/prospector/sheets/${sheetId}/rows/order`, {
        method: "PATCH",
        body: { row_ids: nextRows.map(r => r._id) },
        errorFallback: "Failed to move row.",
      });
    } catch (err) {
      setSheets(prev => patchSheet(prev, sheetId, s => {
        return { rows: restoreRowsByOrder(s.rows, previousOrder) };
      }));
      alert(err.message || "Failed to move row. Please try again.");
    }
  }

  // Persist a new free-column key list. Used by the picker dropdown anchored
  // to the trailing column header. Does NOT touch paid column-actions -
  // those go through addColumn (mocked) / the upcoming backfill modal.
  async function setColumnsKeys(sheetId, nextKeys) {
    const sheet = sheets.find(s => s.id === sheetId);
    if (!sheet) return;

    // Optimistic local update so the UI feels instant. Preserve the user's
    // current order when toggling columns; an empty list is the picker reset
    // action, which restores base columns in catalogue order.
    const cat = catalogueFor(sheet.entityType);
    const byKey = Object.fromEntries(cat.map(c => [c.key, c]));
    const existingByKey = Object.fromEntries(sheet.columns.map(c => [c.key, c]));
    const baseKeys = (cat.filter(c => c.default)).map(c => c.key);
    const requestedKeys = Array.isArray(nextKeys) ? nextKeys : [];
    const seen = new Set();
    const orderedKeys = [];
    const candidateKeys = requestedKeys.length > 0
      ? [...requestedKeys, ...baseKeys]
      : baseKeys;
    for (const k of candidateKeys) {
      if (!seen.has(k) && byKey[k]) {
        orderedKeys.push(k);
        seen.add(k);
      }
    }
    const optimisticCols = columnsFromKeys(orderedKeys, byKey, existingByKey);

    // Detect newly-added free columns to power optimistic shimmer on rows
    // that don't yet have the data. The server will enqueue the appropriate
    // refresh job for supported sheet types; this mirrors that locally so the
    // UI doesn't sit on "-" before the worker emits running.
    const previousKeySet = new Set(sheet.columns.map(c => c.key));
    const newlyAddedFreeKeys = orderedKeys.filter(k =>
      !previousKeySet.has(k)
      && byKey[k]?.free
      && !byKey[k]?.default
      && (sheet.entityType !== "employees" || EMPLOYEE_PROFILE_REFRESH_KEYS.has(k)),
    );

    setSheets(prev => patchSheet(prev, sheetId, s => {
      let nextRows = s.rows;
      if (newlyAddedFreeKeys.length > 0) {
        nextRows = s.rows.map(r => {
          if (!canRefreshFreeColumnsForRow(s.entityType, r)) return r;
          const missing = newlyAddedFreeKeys.filter(k => r[k] === undefined);
          if (missing.length === 0) return r;
          return {
            ...r,
            _status: r._status === JOB_STATUS.COMPLETED ? JOB_STATUS.RUNNING : (r._status || JOB_STATUS.PENDING),
            _shimmerCols: mergeShimmerCols(r._shimmerCols, missing),
          };
        });
      }
      return { columns: optimisticCols, rows: nextRows };
    }));

    if (demoMode) return; // marketing demo - no backend.

    try {
      const body = await fetchJSON(`/dashboard/api/prospector/sheets/${sheetId}/columns`, {
        method: "PATCH",
        body: { columns: columnConfigPayload(optimisticCols) },
        errorFallback: "Failed to update columns.",
      });
      if (Array.isArray(body.columns)) {
        setSheets(prev => prev.map(s => s.id === sheetId ? { ...s, columns: body.columns } : s));
      }
    } catch (err) {
      // Roll back to original - the picker's checked state is derived from
      // sheet.columns so the UI tracks reality automatically.
      setSheets(prev => prev.map(s => s.id === sheetId ? { ...s, columns: sheet.columns, rows: sheet.rows } : s));
      if (isInsufficientCreditsError(err)) {
        showOutOfCreditsModal(err);
      } else {
        alert(err.message || "Failed to update columns. Please try again.");
      }
    }
  }

  // Persist a column's resized width. Called once per drag (on mouseup) so
  // we don't spam the backend during the drag itself - local state has the
  // live width while the user drags. Sends the FULL columns config (not just
  // the changed entry) because the PATCH endpoint replaces the stored list
  // wholesale; sending the rest as bare keys keeps payload small for columns
  // at their default width.
  async function setColumnWidth(sheetId, key, widthPx) {
    const sheet = sheets.find(s => s.id === sheetId);
    if (!sheet) return;
    const clamped = Math.max(COL_MIN_PX, Math.min(COL_MAX_PX, Math.round(widthPx)));
    const previousCols = sheet.columns;
    const nextCols = sheet.columns.map(c =>
      c.key === key ? { ...c, width_px: clamped } : c,
    );
    setSheets(prev => prev.map(s => s.id === sheetId ? { ...s, columns: nextCols } : s));

    if (demoMode) return;

    try {
      const body = await fetchJSON(`/dashboard/api/prospector/sheets/${sheetId}/columns`, {
        method: "PATCH",
        body: { columns: columnConfigPayload(nextCols) },
      });
      if (Array.isArray(body.columns)) {
        setSheets(prev => prev.map(s => s.id === sheetId ? { ...s, columns: body.columns } : s));
      }
    } catch (err) {
      // Width persistence is best-effort; rollback silently (no alert).
      setSheets(prev => prev.map(s => s.id === sheetId ? { ...s, columns: previousCols } : s));
    }
  }

  async function deleteColumns(sheetId, keys) {
    const sheet = sheets.find(s => s.id === sheetId);
    if (!sheet) return { deleted: 0, protected: 0 };
    const requested = Array.from(new Set((keys || []).filter(Boolean)));
    if (requested.length === 0) return { deleted: 0, protected: 0 };

    const baseKeys = BASE_COLUMN_KEYS[sheet.entityType] || new Set();
    const visibleKeys = new Set(sheet.columns.map(c => c.key));
    const removableKeys = requested.filter(k => visibleKeys.has(k) && !baseKeys.has(k) && k !== "logo");
    const protectedCount = requested.filter(k => visibleKeys.has(k) && (baseKeys.has(k) || k === "logo")).length;
    if (removableKeys.length === 0) {
      alert("Identity columns can't be removed.");
      return { deleted: 0, protected: protectedCount };
    }

    const removableSet = new Set(removableKeys);
    const previousColumns = sheet.columns;
    const previousRows = sheet.rows;
    const nextCols = sheet.columns.filter(c => !removableSet.has(c.key));
    const nextRows = sheet.rows.map(r => {
      if (!r._shimmerCols || r._shimmerCols.length === 0) return r;
      const nextShimmer = r._shimmerCols.filter(k => !removableSet.has(k));
      if (nextShimmer.length === r._shimmerCols.length) return r;
      const copy = { ...r };
      if (nextShimmer.length > 0) copy._shimmerCols = nextShimmer;
      else delete copy._shimmerCols;
      return copy;
    });

    setSheets(prev => prev.map(s => s.id === sheetId ? { ...s, columns: nextCols, rows: nextRows } : s));

    if (demoMode || !isPersistedSheetId(sheetId)) {
      return { deleted: removableKeys.length, protected: protectedCount };
    }

    try {
      const body = await fetchJSON(`/dashboard/api/prospector/sheets/${sheetId}/columns`, {
        method: "PATCH",
        body: { columns: columnConfigPayload(nextCols) },
        errorFallback: "Failed to delete columns.",
      });
      if (Array.isArray(body.columns)) {
        setSheets(prev => prev.map(s => s.id === sheetId ? { ...s, columns: body.columns } : s));
      }
      return { deleted: removableKeys.length, protected: protectedCount };
    } catch (err) {
      setSheets(prev => prev.map(s => s.id === sheetId ? { ...s, columns: previousColumns, rows: previousRows } : s));
      alert(err.message || "Failed to delete columns. Please try again.");
      return { deleted: 0, protected: protectedCount };
    }
  }

  async function deleteColumn(sheetId, key) {
    return deleteColumns(sheetId, [key]);
  }

  function requestDeleteRows(sheetId, rowIds) {
    const sheet = sheets.find(s => s.id === sheetId);
    if (!sheet) return;
    if (sheet.entityType === "updates") {
      alert("Updates rows are generated by transforms and can't be removed.");
      return;
    }
    const visibleIds = new Set((sheet.rows || []).map(r => r._id));
    const selectedIds = Array.from(new Set((rowIds || []).filter(id => visibleIds.has(id))));
    if (selectedIds.length === 0) return;
    setModal({ kind: "bulkDeleteConfirm", sheetId, axis: "rows", rowIds: selectedIds });
  }

  function requestDeleteColumns(sheetId, keys) {
    const sheet = sheets.find(s => s.id === sheetId);
    if (!sheet) return;
    const visibleKeys = new Set((sheet.columns || []).map(c => c.key));
    const selectedKeys = Array.from(new Set((keys || []).filter(k => visibleKeys.has(k))));
    if (selectedKeys.length === 0) return;

    const baseKeys = BASE_COLUMN_KEYS[sheet.entityType] || new Set();
    const removableKeys = selectedKeys.filter(k => !baseKeys.has(k) && k !== "logo");
    const protectedCount = selectedKeys.length - removableKeys.length;
    if (removableKeys.length === 0) {
      alert("Identity columns can't be removed.");
      return;
    }
    setModal({
      kind: "bulkDeleteConfirm",
      sheetId,
      axis: "columns",
      columnKeys: removableKeys,
      protectedCount,
    });
  }

  async function transform(sheetId, transformDef, options = {}) {
    const source = sheets.find(s => s.id === sheetId);
    if (!source) return;

    const actionKey = transformDef.actionKey;
    if (actionKey && !demoMode && isPersistedSheetId(sheetId)) {
      if (options.neverShowCreditWarning) {
        setLocalStorageFlag(transformCreditWarningStorageKey(transformDef), true);
      }
      try {
        const body = await fetchJSON(`/dashboard/api/prospector/sheets/${sheetId}/actions/${actionKey}`, {
          method: "POST",
          body: { params: options.params || {} },
          errorFallback: `Failed to start ${transformDef.label}.`,
        });
        if (body.redirect_url) {
          window.location.href = body.redirect_url;
          return;
        }
        const child = body.sheet;
        if (child && child.id) {
          setSheets(prev => {
            if (prev.some(s => s.id === child.id)) {
              return prev.map(s => s.id === child.id ? { ...s, ...child } : s);
            }
            return [...prev, child];
          });
          setActiveId(child.id);
        }
      } catch (err) {
        if (isInsufficientCreditsError(err)) {
          showOutOfCreditsModal(err);
          return;
        }
        alert(err.message || `Failed to start ${transformDef.label}. Please try again.`);
      }
      return;
    }

    if (transformDef.key === "monitor") {
      alert("Monitor is only available on saved company spreadsheets.");
      return;
    }

    const newId = uid("sheet");
    const targetRows = synthesizeTransformedRows(transformDef.yields, source);
    const total = targetRows.length;
    const newSheet = {
      id: newId,
      name: `${truncate(source.name, 26)} → ${transformDef.label}`,
      entityType: transformDef.yields,
      parentId: sheetId,
      producedBy: transformDef.label,
      origin: `Transformed: ${transformDef.label}`,
      columns: [...COLUMN_PRESETS[transformDef.yields]],
      rows: [],
      progress: { kind: "transform", label: `Getting ${transformDef.label.toLowerCase().replace(/^get /, "")}`, done: 0, total },
    };
    setSheets(prev => [...prev, newSheet]);
    setActiveId(newId);

    const token = { cancelled: false };
    cancelTokensRef.current[newId] = token;
    streamRowsIntoSheet(newId, targetRows, token, /* batchSize */ 2, /* delayMs */ 220);
  }

  // Streams rows into a sheet in chunks, updating sheet.progress as it goes.
  // Calls completion handler when done or cleans up on cancel.
  function streamRowsIntoSheet(sheetId, allRows, token, batchSize, delayMs) {
    const total = allRows.length;
    let cursor = 0;
    const pump = () => {
      if (token.cancelled) return;
      const next = allRows.slice(cursor, cursor + batchSize);
      cursor += next.length;
      setSheets(prev => patchSheet(prev, sheetId, s => {
        const done = Math.min(total, cursor);
        const finished = done >= total;
        return {
          rows: [...s.rows, ...next],
          progress: finished ? null : { ...s.progress, done, total },
        };
      }));
      if (cursor < total) {
        setTimeout(pump, delayMs);
      } else {
        delete cancelTokensRef.current[sheetId];
      }
    };
    if (total === 0) {
      setSheets(prev => prev.map(s => s.id === sheetId ? { ...s, progress: null } : s));
      delete cancelTokensRef.current[sheetId];
      return;
    }
    setTimeout(pump, delayMs);
  }

  async function cancelLoading(sheetId) {
    const sheet = sheets.find(s => s.id === sheetId);
    if (!sheet) return;

    const token = cancelTokensRef.current[sheetId];
    if (token) {
      token.cancelled = true;
      delete cancelTokensRef.current[sheetId];
    }

    setSheets(prev => patchSheet(prev, sheetId, clearSheetLoadingState));

    if (demoMode || !isPersistedSheetId(sheetId)) return;

    try {
      await fetchJSON(`/dashboard/api/prospector/sheets/${sheetId}/jobs/abort`, {
        method: "POST",
        errorFallback: "Failed to abort spreadsheet jobs.",
      });
    } catch (err) {
      setSheets(prev => prev.map(s => s.id === sheetId ? sheet : s));
      alert(err.message || "Failed to abort spreadsheet jobs. Please try again.");
    }
  }

  async function seedBlank(entityType) {
    if (!demoMode) {
      try {
        const body = await createProspectorSheet(entityType);
        const sheet = body.sheet;
        if (!sheet || !sheet.id) throw new Error("No sheet returned");
        if (body.redirect_url) {
          window.location.href = body.redirect_url;
          return;
        }
        setSheets(prev => [...prev.filter(s => s.id !== sheet.id), sheet]);
        setActiveId(sheet.id);
      } catch (err) {
        console.error("seedBlank failed:", err);
        alert(err.message || "Network error creating sheet.");
      }
      return;
    }

    const newId = uid("sheet");
    const labelFor = entityType === "companies" ? "Companies" : "People";
    setSheets(prev => {
      const sheet = {
        id: newId,
        name: nextDefaultName(prev, entityType),
        entityType,
        parentId: null,
        origin: `New blank ${labelFor.toLowerCase()} sheet`,
        columns: [...COLUMN_PRESETS[entityType]],
        rows: [],
      };
      return [...prev, sheet];
    });
    setActiveId(newId);
  }

  async function renameSheet(sheetId, newName) {
    const trimmed = (newName || "").trim();
    if (!trimmed) return;
    const sheet = sheets.find(s => s.id === sheetId);
    if (!sheet || sheet.name === trimmed) return;

    const prevName = sheet.name;
    setSheets(prev => prev.map(s => s.id === sheetId ? { ...s, name: trimmed } : s));

    if (!isPersistedSheetId(sheetId)) return;

    try {
      const body = await persistSheetName(sheetId, trimmed);
      const persistedName = (body.sheet && body.sheet.name) || trimmed;
      setSheets(prev => prev.map(s =>
        s.id === sheetId && s.name === trimmed ? { ...s, name: persistedName } : s
      ));
    } catch (err) {
      console.error("renameSheet failed:", err);
      setSheets(prev => prev.map(s =>
        s.id === sheetId && s.name === trimmed ? { ...s, name: prevName } : s
      ));
      alert(err.message || "Network error renaming sheet.");
    }
  }

  function seedFromCSV(file, entityType) {
    if (!demoMode) {
      const form = new FormData();
      form.append("file", file);
      form.append("entity_type", entityType);

      fetchJSON("/dashboard/api/prospector/sheets/import-csv", {
        method: "POST",
        body: form,
        errorFallback: "Failed to import CSV.",
      })
        .then(body => {
          const sheet = body.sheet;
          if (!sheet || !sheet.id) throw new Error("No sheet returned");
          setSheets(prev => [...prev.filter(s => s.id !== sheet.id), sheet]);
          setActiveId(sheet.id);
        })
        .catch(err => {
          console.error("seedFromCSV failed:", err);
          if (isInsufficientCreditsError(err)) {
            showOutOfCreditsModal(err);
            return;
          }
          alert(err.message || "Network error importing CSV.");
        });
      return;
    }

    const newId = uid("sheet");
    const baseName = ((file.name || "").replace(/\.[^.]+$/, "") || "").trim();
    setSheets(prev => {
      const sheet = {
        id: newId,
        name: baseName || nextDefaultName(prev, entityType),
        entityType,
        parentId: null,
        origin: `Imported from ${file.name || "CSV"}`,
        columns: [...COLUMN_PRESETS[entityType]],
        rows: [],
        progress: { kind: "csv_import", label: "Reading CSV", done: 0, total: 0 },
      };
      return [...prev, sheet];
    });
    setActiveId(newId);

    const token = { cancelled: false };
    cancelTokensRef.current[newId] = token;

    const reader = new FileReader();
    reader.onload = () => {
      if (token.cancelled) return;
      const rows = parseCSV(String(reader.result || ""), entityType);
      streamRowsIntoSheet(newId, rows, token, /* batchSize */ 25, /* delayMs */ 60);
    };
    reader.onerror = () => {
      setSheets(prev => prev.map(s => s.id === newId ? { ...s, progress: null, rows: [] } : s));
      delete cancelTokensRef.current[newId];
    };
    reader.readAsText(file);
  }

  function seedPrebuilt(kind) {
    if (!demoMode) {
      fetchJSON(`/dashboard/api/prospector/templates/${kind}`, {
        method: "POST",
        errorFallback: "Failed to start template.",
      })
        .then(body => {
          const sourceSheet = body.source_sheet;
          const childSheet = body.sheet;
          if (!childSheet || !childSheet.id) throw new Error("No sheet returned");
          setSheets(prev => {
            const incoming = [sourceSheet, childSheet].filter(s => s && s.id);
            const incomingIds = new Set(incoming.map(s => s.id));
            return [
              ...prev.filter(s => !incomingIds.has(s.id)),
              ...incoming,
            ];
          });
          setActiveId(childSheet.id);
        })
        .catch(err => {
          console.error("seedPrebuilt failed:", err);
          if (isInsufficientCreditsError(err)) {
            showOutOfCreditsModal(err);
            return;
          }
          alert(err.message || "Network error starting template.");
        });
      return;
    }

    const productName = prospectorTemplateSource().name;
    const newId = uid("sheet");
    const targetRows = synthesizeTransformedRows("companies", null);
    const sheet = {
      id: newId,
      name: kind === "competitors" ? `${productName} - Competitors` : `${productName} - Customers`,
      entityType: "companies",
      parentId: null,
      origin: kind === "competitors" ? `Prebuilt: ${productName}'s competitors` : `Prebuilt: ${productName}'s customers`,
      columns: [...COLUMN_PRESETS.companies],
      rows: [],
      progress: { kind: "transform", label: kind === "competitors" ? "Loading competitors" : "Loading customers", done: 0, total: targetRows.length },
    };
    setSheets(prev => [...prev, sheet]);
    setActiveId(newId);
    const token = { cancelled: false };
    cancelTokensRef.current[newId] = token;
    streamRowsIntoSheet(newId, targetRows, token, /* batchSize */ 2, /* delayMs */ 220);
  }

  // ── Render ────────────────────────────────────────────────

  return (
    <React.Fragment>
      {/* Mobile-only backdrop for the tree drawer */}
      {treeOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-30 lg:hidden"
          onClick={() => setTreeOpen(false)}
        />
      )}

      {/* Left rail - sheet tree (drawer on mobile, inline on lg+) */}
      <SheetTree
        sheets={sheets}
        activeId={activeId}
        selectedIds={selectedSheetIds}
        onSelectionChange={setSelectedSheetIds}
        onSelect={(id) => { setActiveId(id); setTreeOpen(false); }}
        onNewSheet={() => { setActiveId(null); setTreeOpen(false); }}
        onUseTemplate={(kind) => { seedPrebuilt(kind); setTreeOpen(false); }}
        onArchiveSheet={(id) => requestSheetLifecycleAction("archive", id)}
        onBulkArchiveSheets={bulkArchiveSheets}
        onRestoreSheet={(id) => requestSheetLifecycleAction("restore", id)}
        onBulkRestoreSheets={bulkRestoreSheets}
        onDeleteSheet={(id) => requestSheetLifecycleAction("delete", id)}
        open={treeOpen}
        onClose={() => setTreeOpen(false)}
      />

      {/* Main workspace + (optional) right transform pane */}
      <div className="flex-1 flex flex-col overflow-hidden bg-zinc-50 relative min-w-0">
        <div className="flex-1 flex overflow-hidden min-w-0 relative">
          {!activeSheet ? (
            <EmptyState
              onSeedBlank={(entityType) => seedBlank(entityType)}
              onSeedCSV={(file, entityType) => seedFromCSV(file, entityType)}
              onOpenTree={() => setTreeOpen(true)}
              hasSheets={sheets.length > 0}
            />
          ) : (
            <SheetWorkspace
              sheet={activeSheet}
              sheets={sheets}
              onAddRow={() => setModal({ kind: "addRow", sheetId: activeSheet.id })}
              onAddColumn={(action) => addColumn(activeSheet.id, action)}
              onSetColumnsKeys={(keys) => setColumnsKeys(activeSheet.id, keys)}
              onResizeColumn={(key, widthPx) => setColumnWidth(activeSheet.id, key, widthPx)}
              onDeleteColumn={(key) => deleteColumn(activeSheet.id, key)}
              onRequestDeleteRows={(rowIds) => requestDeleteRows(activeSheet.id, rowIds)}
              onRequestDeleteColumns={(keys) => requestDeleteColumns(activeSheet.id, keys)}
              onMoveColumn={(fromKey, toKey) => moveColumn(activeSheet.id, fromKey, toKey)}
              onSortColumn={(key, direction) => sortRows(activeSheet.id, key, direction)}
              onMoveRow={(fromRowId, toRowId, placement) => moveRow(activeSheet.id, fromRowId, toRowId, placement)}
              onTransform={(t, options) => transform(activeSheet.id, t, options)}
              onRenameSheet={renameSheet}
              onCancelLoading={cancelLoading}
              onDownloadCSV={() => exportCSV(activeSheet)}
              onDownloadXLSX={() => exportXLSX(activeSheet)}
              onSelectSheet={setActiveId}
              onOpenTree={() => setTreeOpen(true)}
              onRemoveRow={(rowId) => removeRow(activeSheet.id, rowId)}
              onRetryRow={(rowId) => retryRow(activeSheet.id, rowId)}
              onRetrySheetJob={() => retrySheetJob(activeSheet.id)}
            />
          )}

          {propagateMsg && <PropagationToast message={propagateMsg} />}
        </div>
      </div>

      {/* Modals */}
      {modal?.kind === "addRow" && (
        <AddRowModal
          sheet={sheets.find(s => s.id === modal.sheetId)}
          onCancel={() => setModal(null)}
          onSubmit={(partial) => {
            addRow(modal.sheetId, partial);
            setModal(null);
          }}
        />
      )}
      {modal?.kind === "paidConfirm" && (
        <PaidColumnConfirmModal
          action={modal.action}
          rowCount={modal.rowCount}
          onCancel={() => setModal(null)}
          onConfirm={() => {
            const { sheetId, action } = modal;
            setModal(null);
            confirmAddColumn(sheetId, action);
          }}
        />
      )}
      {modal?.kind === "bulkDeleteConfirm" && (
        <BulkDeleteConfirmModal
          sheet={sheets.find(s => s.id === modal.sheetId)}
          axis={modal.axis}
          rowIds={modal.rowIds || []}
          columnKeys={modal.columnKeys || []}
          protectedCount={modal.protectedCount || 0}
          onCancel={() => setModal(null)}
          onConfirm={() => {
            const request = modal;
            setModal(null);
            if (request.axis === "rows") {
              removeRows(request.sheetId, request.rowIds || []);
            } else if (request.axis === "columns") {
              deleteColumns(request.sheetId, request.columnKeys || []);
            }
          }}
        />
      )}
      {modal?.kind === "outOfCredits" && (
        <OutOfCreditsModal
          message={modal.message}
          actionLabel={modal.actionLabel}
          requiredCredits={modal.requiredCredits}
          currentCredits={modal.currentCredits}
          onCancel={() => setModal(null)}
        />
      )}
      {modal?.kind === "sheetLifecycleConfirm" && (
        <SheetLifecycleConfirmModal
          action={modal.action}
          sheet={sheets.find(s => s.id === modal.sheetId)}
          childCount={modal.childCount}
          onCancel={() => setModal(null)}
          onConfirm={() => {
            const { action, sheetId } = modal;
            setModal(null);
            runSheetLifecycleAction(action, sheetId, { confirmDescendants: true });
          }}
        />
      )}
    </React.Fragment>
  );
}

// ============================================================================
// SheetTree - left rail
// ============================================================================

function SheetTree({ sheets, activeId, selectedIds = [], onSelectionChange, onSelect, onNewSheet, onUseTemplate, onArchiveSheet, onBulkArchiveSheets, onRestoreSheet, onBulkRestoreSheets, onDeleteSheet, open, onClose }) {
  const [query, setQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [sheetMenu, setSheetMenu] = useState(null);
  const selectionAnchorRef = useRef(null);
  const trimmed = query.trim().toLowerCase();
  const activeSheets = sheets.filter(s => !s.isArchived);
  const archivedSheets = sheets.filter(s => s.isArchived);
  const searchableSheets = showArchived ? sheets : activeSheets;
  const matches = buildSheetSearchMatches(searchableSheets, trimmed);
  const activeRoots = rootsForSheets(activeSheets);
  const archivedRoots = rootsForSheets(archivedSheets);
  const selectedSet = new Set(selectedIds || []);

  function sheetMode(sheet) {
    return sheet && sheet.isArchived ? "archived" : "active";
  }

  function visibleSelectableIds(mode) {
    if (matches !== null) {
      return matches.map(match => match.sheet).filter(s => sheetMode(s) === mode).map(s => s.id);
    }
    return flattenSheetTreeIds(mode === "archived" ? archivedSheets : activeSheets);
  }

  function selectedIdsForMode(mode) {
    return (selectedIds || []).filter(id => {
      const sheet = sheets.find(s => s.id === id);
      if (!sheet) return false;
      return sheetMode(sheet) === mode;
    });
  }

  function commitSheetSelection(ids) {
    if (typeof onSelectionChange === "function") {
      onSelectionChange(Array.from(new Set((ids || []).filter(Boolean))));
    }
  }

  function selectSheet(sheet, e) {
    if (!sheet) return;
    const id = sheet.id;
    const mode = sheetMode(sheet);
    const visibleIds = visibleSelectableIds(mode);
    let nextIds = [id];
    if (e?.shiftKey && selectionAnchorRef.current) {
      const anchorIdx = visibleIds.indexOf(selectionAnchorRef.current);
      const currentIdx = visibleIds.indexOf(id);
      if (anchorIdx !== -1 && currentIdx !== -1) {
        const start = Math.min(anchorIdx, currentIdx);
        const end = Math.max(anchorIdx, currentIdx);
        nextIds = visibleIds.slice(start, end + 1);
      }
    } else if (e?.metaKey || e?.ctrlKey) {
      const current = new Set(selectedIdsForMode(mode));
      if (current.has(id)) current.delete(id);
      else current.add(id);
      nextIds = Array.from(current);
    }
    selectionAnchorRef.current = id;
    commitSheetSelection(nextIds);
    onSelect(id);
  }

  function openSheetContextMenu(sheet, e) {
    if (!sheet) return;
    e.preventDefault();
    e.stopPropagation();
    const mode = sheetMode(sheet);
    const selectedInMode = selectedIdsForMode(mode);
    const ids = selectedSet.has(sheet.id) && selectedInMode.length > 0 ? selectedInMode : [sheet.id];
    if (!selectedSet.has(sheet.id)) {
      selectionAnchorRef.current = sheet.id;
      commitSheetSelection(ids);
    }
    setSheetMenu({ x: e.clientX, y: e.clientY, ids, mode });
  }

  return (
    <aside
      className={`w-72 sm:w-80 border-r border-zinc-200 bg-white flex flex-col shrink-0
        fixed inset-y-0 left-0 z-40 transform transition-transform duration-300 ease-in-out
        lg:relative lg:translate-x-0 lg:flex lg:transition-none
        ${open ? "translate-x-0" : "-translate-x-full"}`}
    >
      <div className="p-4 xl:p-6 border-b border-zinc-200">
        <div className="flex items-center justify-between mb-3">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h2 className="text-base xl:text-lg font-semibold text-zinc-900">Spreadsheets</h2>
              {sheets.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowArchived(v => !v)}
                  aria-pressed={showArchived}
                  aria-label={showArchived ? "Hide archived spreadsheets" : "Show archived spreadsheets"}
                  title={showArchived ? "Hide archived spreadsheets" : "Show archived spreadsheets"}
                  className={`relative inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border transition-colors ${showArchived ? "border-zinc-300 bg-zinc-100 text-zinc-700" : "border-transparent text-zinc-400 hover:border-zinc-200 hover:text-zinc-700"}`}
                >
                  <i className="ph ph-archive-box text-sm"></i>
                  {archivedSheets.length > 0 && (
                    <span className="absolute -right-1 -top-1 min-w-[14px] h-[14px] rounded-full bg-zinc-700 px-1 text-[9px] font-bold leading-[14px] text-white">
                      {archivedSheets.length}
                    </span>
                  )}
                </button>
              )}
            </div>
            <p className="text-xs text-zinc-500 mt-0.5">
              {matches
                ? `${matches.length} match${matches.length === 1 ? "" : "es"}`
                : `Lineage tree · ${activeSheets.length} active`}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onNewSheet}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded"
            >
              <i className="ph ph-plus"></i> New
            </button>
            <button
              onClick={onClose}
              className="lg:hidden text-zinc-400 hover:text-zinc-700 p-1"
              aria-label="Close"
            >
              <i className="ph ph-x text-lg"></i>
            </button>
          </div>
        </div>
        <div className="relative">
          <i className="ph ph-magnifying-glass absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 text-sm"></i>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search sheets…"
            className="w-full pl-8 pr-7 py-1.5 text-sm border border-zinc-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 p-0.5"
              aria-label="Clear search"
            >
              <i className="ph ph-x text-xs"></i>
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-3">
        {matches !== null ? (
          matches.length === 0 ? (
            <div className="px-4 py-6 text-sm text-zinc-400 text-center">
              No sheets match "{query}".
            </div>
          ) : (
            <SearchResults
              sheets={sheets}
              matches={matches}
              activeId={activeId}
              query={trimmed}
              selectedIds={selectedIds}
              onSelectSheet={selectSheet}
              onContextMenuSheet={openSheetContextMenu}
              onArchiveSheet={onArchiveSheet}
              onRestoreSheet={onRestoreSheet}
              onDeleteSheet={onDeleteSheet}
            />
          )
        ) : (
          <React.Fragment>
            {activeRoots.length === 0 ? (
              <div className="px-4 py-6 text-sm text-zinc-400 text-center">
                No sheets yet.
              </div>
            ) : (
              activeRoots.map(root => (
                <TreeNode
                  key={root.id}
                  sheet={root}
                  sheets={activeSheets}
                  activeId={activeId}
                  selectedIds={selectedIds}
                  depth={0}
                  onSelectSheet={selectSheet}
                  onContextMenuSheet={openSheetContextMenu}
                  actionMode="active"
                  onArchiveSheet={onArchiveSheet}
                  onRestoreSheet={onRestoreSheet}
                  onDeleteSheet={onDeleteSheet}
                />
              ))
            )}

            {showArchived && (
              <div className="mt-4 pt-3 border-t border-zinc-100">
                <div className="px-4 pb-2 text-[10px] uppercase tracking-wider font-semibold text-zinc-400">
                  Archived
                </div>
                {archivedRoots.length === 0 ? (
                  <div className="px-4 py-4 text-sm text-zinc-400 text-center">
                    No archived sheets.
                  </div>
                ) : (
                  archivedRoots.map(root => (
                    <TreeNode
                      key={root.id}
                      sheet={root}
                      sheets={archivedSheets}
                      activeId={activeId}
                      selectedIds={selectedIds}
                      depth={0}
                      onSelectSheet={selectSheet}
                      onContextMenuSheet={openSheetContextMenu}
                      actionMode="archived"
                      onArchiveSheet={onArchiveSheet}
                      onRestoreSheet={onRestoreSheet}
                      onDeleteSheet={onDeleteSheet}
                    />
                  ))
                )}
              </div>
            )}
          </React.Fragment>
        )}
      </div>

      {/* Templates - prebuilt starting points */}
      <TemplatesSection onUseTemplate={onUseTemplate} />

      {sheetMenu && (
        <SheetLifecycleContextMenu
          pos={{ x: sheetMenu.x, y: sheetMenu.y }}
          count={(sheetMenu.ids || []).length}
          mode={sheetMenu.mode}
          onClose={() => setSheetMenu(null)}
          onArchive={() => {
            const ids = sheetMenu.ids || [];
            setSheetMenu(null);
            if (typeof onBulkArchiveSheets === "function") onBulkArchiveSheets(ids);
          }}
          onRestore={() => {
            const ids = sheetMenu.ids || [];
            setSheetMenu(null);
            if (typeof onBulkRestoreSheets === "function") onBulkRestoreSheets(ids);
          }}
          onDelete={() => {
            const ids = sheetMenu.ids || [];
            setSheetMenu(null);
            if (ids.length === 1 && typeof onDeleteSheet === "function") onDeleteSheet(ids[0]);
          }}
        />
      )}
    </aside>
  );
}

function SearchResults({ sheets, matches, activeId, selectedIds = [], query, onSelectSheet, onContextMenuSheet, onArchiveSheet, onRestoreSheet, onDeleteSheet }) {
  const selectedSet = new Set(selectedIds || []);
  return (
    <div className="px-2 space-y-1">
      {matches.map(match => {
        const s = match.sheet;
        const meta = ENTITY_TYPES[s.entityType];
        const parent = s.parentId ? sheets.find(p => p.id === s.parentId) : null;
        const isActive = s.id === activeId;
        const isSelected = selectedSet.has(s.id);
        const selectable = typeof onSelectSheet === "function";
        return (
          <div
            key={s.id}
            onContextMenu={(e) => onContextMenuSheet && onContextMenuSheet(s, e)}
            className={`group flex items-start gap-1 rounded transition-colors ${isActive ? "bg-purple-50 text-purple-700" : isSelected ? "bg-zinc-100 text-zinc-900" : "hover:bg-zinc-50"}`}
          >
            <button
              type="button"
              aria-disabled={!selectable}
              onClick={(e) => selectable && onSelectSheet(s, e)}
              className={`min-w-0 flex-1 text-left px-2 py-1.5 ${selectable ? "" : "cursor-default"}`}
            >
              <div className="flex items-center gap-1.5">
                <i className={`ph ${meta.icon} text-zinc-400 shrink-0 text-sm`}></i>
                <span className="text-sm font-medium text-zinc-800 truncate">
                  <HighlightedName name={s.name} query={query} />
                </span>
              </div>
              <div className="text-[10px] text-zinc-400 truncate pl-5 mt-0.5">
                {parent
                  ? <span><i className="ph ph-arrow-up-right text-[8px] mr-0.5"></i> via {s.producedBy || "transform"} from {parent.name}</span>
                  : <span>{s.isArchived ? "Archived" : "Root"}</span>}
              </div>
              {match.cellMatches.length > 0 && (
                <div className="mt-1.5 pl-5 space-y-1">
                  {match.cellMatches.map(cell => (
                    <div key={`${s.id}-${cell.rowIndex}-${cell.key}`} className="text-[11px] leading-snug text-zinc-500">
                      <span className="font-medium text-zinc-600">{cell.label || cell.key}</span>
                      <span className="text-zinc-300"> · </span>
                      <span>Row {cell.rowIndex + 1}</span>
                      <span className="text-zinc-300"> · </span>
                      <span className="text-zinc-700">
                        <HighlightedText text={cell.text} query={query} />
                      </span>
                    </div>
                  ))}
                  {match.totalCellMatches > match.cellMatches.length && (
                    <div className="text-[10px] text-zinc-400">
                      +{match.totalCellMatches - match.cellMatches.length} more cell match{match.totalCellMatches - match.cellMatches.length === 1 ? "" : "es"}
                    </div>
                  )}
                </div>
              )}
            </button>
            <SheetNodeActions
              sheet={s}
              mode={s.isArchived ? "archived" : "active"}
              selected={isSelected}
              onArchiveSheet={onArchiveSheet}
              onRestoreSheet={onRestoreSheet}
              onDeleteSheet={onDeleteSheet}
            />
          </div>
        );
      })}
    </div>
  );
}

function sheetLifecycleMenuItems({ mode, count = 1, compact = false, onArchive, onRestore, onDelete }) {
  if (mode === "archived") {
    const restoreLabel = compact
      ? "Unarchive"
      : count === 1
        ? "Unarchive spreadsheet"
        : `Unarchive ${count} selected spreadsheets`;
    const items = [
      { key: "restore", label: restoreLabel, icon: "ph-arrow-counter-clockwise", onClick: onRestore },
    ];
    if (count === 1 && typeof onDelete === "function") {
      items.push({ key: "delete", label: compact ? "Delete" : "Delete spreadsheet", icon: "ph-trash", danger: true, onClick: onDelete });
    }
    return items;
  }

  const archiveLabel = compact
    ? "Archive"
    : count === 1
      ? "Archive spreadsheet"
      : `Archive ${count} selected spreadsheets`;
  return [
    { key: "archive", label: archiveLabel, icon: "ph-archive", onClick: onArchive },
  ];
}

function SheetLifecycleContextMenu({ pos, count, mode, onClose, onArchive, onRestore, onDelete }) {
  const panelRef = useRef(null);
  const [panelPos, setPanelPos] = useState(null);

  useEffect(() => {
    const width = 256;
    const margin = 8;
    setPanelPos({
      top: Math.min(window.innerHeight - 48, Math.max(margin, pos.y)),
      left: Math.min(window.innerWidth - width - margin, Math.max(margin, pos.x)),
    });
  }, [pos]);

  useEffect(() => {
    function onDoc(e) {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      onClose();
    }
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("contextmenu", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("contextmenu", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  if (!panelPos) return null;
  const items = sheetLifecycleMenuItems({ mode, count, onArchive, onRestore, onDelete });
  return ReactDOM.createPortal(
    <div
      ref={panelRef}
      className="fixed z-[9999] w-64 rounded border border-zinc-200 bg-white py-1 shadow-lg prospector-fade-in"
      style={{ top: panelPos.top, left: panelPos.left }}
    >
      {items.map(item => (
        <button
          key={item.key}
          type="button"
          onClick={item.onClick}
          className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-zinc-50 ${item.danger ? "text-red-600" : "text-zinc-700"}`}
        >
          <i className={`ph ${item.icon} text-sm`}></i>
          {item.label}
        </button>
      ))}
    </div>,
    document.body,
  );
}

function HighlightedName({ name, query }) {
  return <HighlightedText text={name} query={query} />;
}

function HighlightedText({ text, query }) {
  const value = String(text || "");
  if (!query) return <span>{value}</span>;
  const lower = value.toLowerCase();
  const idx = lower.indexOf(query);
  if (idx === -1) return <span>{value}</span>;
  return (
    <React.Fragment>
      <span>{value.slice(0, idx)}</span>
      <span className="bg-yellow-100 text-zinc-900 rounded-sm px-0.5">{value.slice(idx, idx + query.length)}</span>
      <span>{value.slice(idx + query.length)}</span>
    </React.Fragment>
  );
}

function TemplatesSection({ onUseTemplate }) {
  const productName = prospectorTemplateSource().name;
  const templates = [
    { key: "competitors", icon: "ph-flag",      label: `${productName}'s competitors`, description: "Companies competing with you" },
    { key: "customers",   icon: "ph-handshake", label: `${productName}'s customers`,   description: "Companies that buy from you" },
  ];
  return (
    <div className="px-3 py-3 border-t border-zinc-200">
      <h3 className="px-2 text-[10px] uppercase tracking-wider font-semibold text-zinc-400 mb-2">Templates</h3>
      <div className="space-y-1">
        {templates.map(t => (
          <button
            key={t.key}
            onClick={() => onUseTemplate(t.key)}
            className="w-full text-left flex items-start gap-2 px-2 py-2 rounded hover:bg-purple-50 group transition-colors"
          >
            <span className="inline-flex items-center justify-center w-7 h-7 rounded bg-purple-50 text-purple-600 group-hover:bg-purple-100 shrink-0">
              <i className={`ph ${t.icon}`}></i>
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-zinc-800 group-hover:text-purple-700 truncate">{t.label}</div>
              <div className="text-[10px] text-zinc-500 truncate">{t.description}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// A 20px-wide rail cell that draws one of: nothing, a vertical line, or an L-elbow.
function RailCell({ kind }) {
  return (
    <div className="w-5 relative shrink-0 self-stretch">
      {(kind === "vertical" || kind === "elbow-mid" || kind === "elbow-end") && (
        <span
          aria-hidden="true"
          className="absolute left-1/2 -translate-x-1/2 top-0 border-l-2 border-zinc-300"
          style={{ height: kind === "elbow-end" ? "50%" : "100%" }}
        />
      )}
      {(kind === "elbow-mid" || kind === "elbow-end") && (
        <span
          aria-hidden="true"
          className="absolute left-1/2 top-[14px] border-t-2 border-zinc-300"
          style={{ width: "calc(50% + 4px)" }}
        />
      )}
    </div>
  );
}

function SheetNodeActions({ sheet, mode, selected = false, onArchiveSheet, onRestoreSheet, onDeleteSheet }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [panelPos, setPanelPos] = useState(null);
  useClickOutside([triggerRef, panelRef], () => setOpen(false), open);

  useEffect(() => {
    if (!open) return;
    function updatePosition() {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = 144; // w-36
      const margin = 8;
      setPanelPos({
        top: rect.bottom + 4,
        left: Math.min(window.innerWidth - width - margin, Math.max(margin, rect.right - width)),
      });
    }
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  const items = sheetLifecycleMenuItems({
    mode,
    compact: true,
    onArchive: () => onArchiveSheet(sheet.id),
    onRestore: () => onRestoreSheet(sheet.id),
    onDelete: () => onDeleteSheet(sheet.id),
  });

  return (
    <div className="relative shrink-0 self-center pr-1">
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        className={`inline-flex h-6 w-6 items-center justify-center rounded text-zinc-400 transition-opacity hover:bg-zinc-100 hover:text-zinc-700 focus:opacity-100 ${selected || open ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
        aria-label="Spreadsheet actions"
      >
        <i className="ph ph-dots-three-vertical text-base"></i>
      </button>
      {open && panelPos && ReactDOM.createPortal(
        <div
          ref={panelRef}
          className="fixed z-[9999] w-36 rounded border border-zinc-200 bg-white py-1 shadow-lg prospector-fade-in"
          style={{ top: panelPos.top, left: panelPos.left }}
        >
          {items.map(item => (
            <button
              key={item.key}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                item.onClick();
              }}
              className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-zinc-50 ${item.danger ? "text-red-600" : "text-zinc-700"}`}
            >
              <i className={`ph ${item.icon} text-sm`}></i>
              {item.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

function TreeNode({ sheet, sheets, activeId, selectedIds = [], ancestorIsLast = [], isLastChild = true, depth = 0, onSelectSheet, onContextMenuSheet, actionMode = "active", onArchiveSheet, onRestoreSheet, onDeleteSheet }) {
  const [open, setOpen] = useState(true);
  const children = sheets.filter(s => s.parentId === sheet.id);
  const hasChildren = children.length > 0;
  const isActive = sheet.id === activeId;
  const isSelected = (selectedIds || []).includes(sheet.id);
  const meta = ENTITY_TYPES[sheet.entityType];
  const isRoot = depth === 0;
  const selectable = typeof onSelectSheet === "function";

  // For descendants: include this node's isLastChild only when we're below the root.
  // Roots are independent trees, so no rails thread between sibling roots.
  const childAncestorIsLast = isRoot ? [] : [...ancestorIsLast, isLastChild];

  return (
    <div className={isRoot ? "mb-1" : ""}>
      <div className="flex items-stretch min-h-[28px]">
        {ancestorIsLast.map((isLast, i) => (
          <RailCell key={i} kind={isLast ? "empty" : "vertical"} />
        ))}
        {!isRoot && <RailCell kind={isLastChild ? "elbow-end" : "elbow-mid"} />}

        <div
          onContextMenu={(e) => onContextMenuSheet && onContextMenuSheet(sheet, e)}
          className={`group flex-1 min-w-0 flex items-center text-sm rounded-r-md transition-colors ${isActive ? "bg-purple-50 text-purple-700 border-r-2 border-purple-600 font-medium" : isSelected ? "bg-zinc-100 text-zinc-900" : "text-zinc-700 hover:bg-zinc-50"}`}
        >
          <button
            type="button"
            aria-disabled={!selectable}
            onClick={(e) => selectable && onSelectSheet(sheet, e)}
            className={`min-w-0 flex-1 text-left flex items-center gap-1.5 py-1.5 px-2 ${selectable ? "" : "cursor-default"}`}
          >
            {hasChildren ? (
              <span
                onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
                className="text-zinc-400 hover:text-zinc-700 w-4 inline-flex justify-center cursor-pointer"
              >
                <i className={`ph ${open ? "ph-caret-down" : "ph-caret-right"} text-xs`}></i>
              </span>
            ) : (
              <span className="w-4" />
            )}
            <i className={`ph ${meta.icon} text-zinc-400 shrink-0`}></i>
            <span className="truncate flex-1">{sheet.name}</span>
            {sheet.progress && (
              <i className="ph ph-circle-notch ph-spin text-zinc-400 text-xs"></i>
            )}
          </button>
          <SheetNodeActions
            sheet={sheet}
            mode={actionMode}
            selected={isSelected}
            onArchiveSheet={onArchiveSheet}
            onRestoreSheet={onRestoreSheet}
            onDeleteSheet={onDeleteSheet}
          />
        </div>
      </div>

      {/* "via {transform}" sub-line, with rails so it stays visually anchored */}
      {!isRoot && sheet.producedBy && (
        <div className="flex items-stretch">
          {ancestorIsLast.map((isLast, i) => (
            <RailCell key={i} kind={isLast ? "empty" : "vertical"} />
          ))}
          <RailCell kind={isLastChild ? "empty" : "vertical"} />
          <div className="text-[10px] text-zinc-400 italic py-0.5 pl-2 pb-1.5">
            ↳ via {sheet.producedBy}
          </div>
        </div>
      )}

      {hasChildren && open && (
        <div>
          {children.map((child, idx) => (
            <TreeNode
              key={child.id}
              sheet={child}
              sheets={sheets}
              activeId={activeId}
              selectedIds={selectedIds}
              ancestorIsLast={childAncestorIsLast}
              isLastChild={idx === children.length - 1}
              depth={depth + 1}
              onSelectSheet={onSelectSheet}
              onContextMenuSheet={onContextMenuSheet}
              actionMode={actionMode}
              onArchiveSheet={onArchiveSheet}
              onRestoreSheet={onRestoreSheet}
              onDeleteSheet={onDeleteSheet}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// EmptyState - landing UI when no active sheet
// ============================================================================

function EmptyState({ onSeedBlank, onSeedCSV, onOpenTree, hasSheets }) {
  const [csvType, setCsvType] = useState("companies");
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);
  const dragCounterRef = useRef(0);

  function handleFile(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    onSeedCSV(file, csvType);
    e.target.value = "";
  }

  function isCSVFile(file) {
    if (!file) return false;
    if (file.type === "text/csv" || file.type === "application/csv") return true;
    return /\.csv$/i.test(file.name || "");
  }

  function onDropZoneDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    setDragging(true);
  }
  function onDropZoneDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setDragging(false);
  }
  function onDropZoneDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  }
  function onDropZoneDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setDragging(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (!file) return;
    if (!isCSVFile(file)) {
      alert("Please drop a .csv file.");
      return;
    }
    onSeedCSV(file, csvType);
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {hasSheets && (
        <div className="lg:hidden px-4 py-3 border-b border-zinc-200 bg-white">
          <button
            onClick={onOpenTree}
            className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-zinc-700 bg-white border border-zinc-200 rounded hover:bg-zinc-50"
          >
            <i className="ph ph-tree-structure"></i> View existing sheets
          </button>
        </div>
      )}
      <div className="max-w-3xl p-4 xl:p-6">
        <div className="mb-6">
          <h1 className="text-xl xl:text-2xl font-semibold text-zinc-900 mb-2">New spreadsheet</h1>
          <p className="text-sm xl:text-base text-zinc-600">Pick a starting point. You can grow the sheet row-by-row, import a CSV, or pick a template from the left rail.</p>
        </div>

        {/* Blank-sheet picker */}
        <div className="text-xs uppercase tracking-wider text-zinc-400 font-semibold mb-3">Start from blank</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <button
            onClick={() => onSeedBlank("companies")}
            className="group text-left bg-white border border-zinc-200 rounded p-5 hover:border-purple-400 hover:shadow-sm transition-colors"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-blue-100 text-blue-700 group-hover:bg-blue-200">
                <i className="ph-bold ph-buildings"></i>
              </span>
              <h4 className="text-sm font-semibold text-zinc-900">Companies</h4>
            </div>
            <p className="text-xs text-zinc-600">Add companies one-by-one by domain. Enrich with employee count, funding, and more.</p>
          </button>
          <button
            onClick={() => onSeedBlank("employees")}
            className="group text-left bg-white border border-zinc-200 rounded p-5 hover:border-purple-400 hover:shadow-sm transition-colors"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-purple-100 text-purple-700 group-hover:bg-purple-200">
                <i className="ph-bold ph-users-three"></i>
              </span>
              <h4 className="text-sm font-semibold text-zinc-900">People</h4>
            </div>
            <p className="text-xs text-zinc-600">Add people by work email, name, employer, or role.</p>
          </button>
        </div>

        {/* CSV import */}
        <div className="text-xs uppercase tracking-wider text-zinc-400 font-semibold mb-3">Or import a CSV</div>
        <div
          onDragEnter={onDropZoneDragEnter}
          onDragLeave={onDropZoneDragLeave}
          onDragOver={onDropZoneDragOver}
          onDrop={onDropZoneDrop}
          className={`relative bg-white rounded p-5 transition-colors ${
            dragging
              ? "border-2 border-dashed border-purple-500 bg-purple-50/60"
              : "border border-dashed border-zinc-300"
          }`}
        >
          <div className="flex items-center gap-3 mb-3">
            <span className={`inline-flex items-center justify-center w-8 h-8 rounded ${dragging ? "bg-purple-100 text-purple-700" : "bg-zinc-100 text-zinc-700"}`}>
              <i className="ph-bold ph-upload-simple"></i>
            </span>
            <h4 className="text-sm font-semibold text-zinc-900">Upload CSV</h4>
            <span className="text-xs text-zinc-400 hidden sm:inline">- or drag a .csv file anywhere on this card</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr_auto] gap-3 items-start">
            <div className="flex items-center gap-2 text-sm">
              <label className="text-xs text-zinc-500">Type</label>
              <select
                value={csvType}
                onChange={(e) => setCsvType(e.target.value)}
                className="border border-zinc-200 rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="companies">Companies</option>
                <option value="employees">People</option>
              </select>
            </div>
            <div className="text-xs text-zinc-500 leading-relaxed">
              {csvType === "companies" ? (
                <React.Fragment>
                  <div className="font-medium text-zinc-700">Columns: domain, name, industry</div>
                  <div>
                    <span className="font-mono">domain</span> is enough; name and industry are optional.
                    {" "}
                    <button type="button" onClick={() => downloadSampleCSV("companies")} className="font-medium text-purple-600 underline-offset-2 hover:underline">
                      Download companies sample
                    </button>
                  </div>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <div className="font-medium text-zinc-700">Columns: work_email, first_name, last_name, employer_website, role</div>
                  <div>
                    Each row needs work_email, first_name + employer_website, or employer_website + role.
                    {" "}
                    <button type="button" onClick={() => downloadSampleCSV("employees")} className="font-medium text-purple-600 underline-offset-2 hover:underline">
                      Download people sample
                    </button>
                  </div>
                </React.Fragment>
              )}
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <button
                onClick={() => fileRef.current && fileRef.current.click()}
                className="px-3 xl:px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded hover:bg-purple-700 transition-colors inline-flex items-center gap-2 justify-center"
              >
                <i className="ph ph-upload-simple"></i> Choose .csv
              </button>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={handleFile}
            />
          </div>
          {dragging && (
            <div className="absolute inset-0 flex items-center justify-center bg-purple-50/90 rounded pointer-events-none prospector-fade-in">
              <div className="text-center">
                <i className="ph-bold ph-file-csv text-3xl text-purple-600"></i>
                <div className="text-sm font-semibold text-purple-700 mt-2">
                  Drop CSV to import as {csvType === "companies" ? "Companies" : "People"}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SheetWorkspace
// ============================================================================

function SheetJobStatusBanner({ sheet, onRetry }) {
  const failed = sheet.job?.status === JOB_STATUS.FAILED;
  const partial = (
    sheet.job?.status === JOB_STATUS.COMPLETED
    && sheet.job?.partial_result === true
    && sheet.job?.stop_reason === "insufficient_credits"
  );
  if (!failed && !partial) return null;

  const canRetry = failed && typeof onRetry === "function";
  const title = partial
    ? "Search stopped because credits ran out"
    : "Spreadsheet action failed";
  const detail = partial
    ? "All paid results were saved. Add credits to continue searching."
    : (sheet.job?.error || "Failed to run spreadsheet action.");
  return (
    <div className="border-b border-amber-200 bg-amber-50 px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <i className="ph ph-warning-circle mt-0.5 text-base text-amber-700"></i>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-amber-950">{title}</div>
            <div className="mt-0.5 break-words text-xs text-amber-900">
              {detail}
            </div>
          </div>
        </div>
        {canRetry && (
          <ProspectorRetryButton
            flush
            onClick={onRetry}
            title={sheet.job?.error || "Retry action"}
            ariaLabel="Retry action"
          >
            Retry action
          </ProspectorRetryButton>
        )}
        {partial && (
          <a
            href={BUY_CREDITS_URL}
            className="inline-flex items-center gap-1.5 rounded-md bg-purple-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-purple-700"
          >
            <i className="ph ph-coins"></i>
            Buy credits
          </a>
        )}
      </div>
    </div>
  );
}

function SheetWorkspace({ sheet, sheets, onAddRow, onAddColumn, onSetColumnsKeys, onResizeColumn, onDeleteColumn, onRequestDeleteRows, onRequestDeleteColumns, onMoveColumn, onSortColumn, onMoveRow, onTransform, onRenameSheet, onCancelLoading, onDownloadCSV, onDownloadXLSX, onSelectSheet, onOpenTree, onRemoveRow, onRetryRow, onRetrySheetJob }) {
  const meta = ENTITY_TYPES[sheet.entityType];
  const colActions = COLUMN_ACTIONS[sheet.entityType] || [];
  const transforms = TRANSFORMS[sheet.entityType] || [];
  const lineage = getLineage(sheets, sheet.id);
  const archivedView = !!sheet.isArchived;

  return (
    <div className="flex-1 flex flex-col overflow-hidden min-w-0">
      <SheetHeader
        sheet={sheet}
        lineage={lineage}
        meta={meta}
        onRenameSheet={onRenameSheet}
        onDownloadCSV={archivedView ? null : onDownloadCSV}
        onDownloadXLSX={archivedView ? null : onDownloadXLSX}
        onSelectSheet={onSelectSheet}
        onOpenTree={onOpenTree}
        readOnly={archivedView}
      />
      {sheet.progress && !archivedView && (
        <ProgressChip progress={sheet.progress} onCancel={() => onCancelLoading(sheet.id)} />
      )}
      {!sheet.progress && !archivedView && (sheet.rows || []).length > 0 && (
        <SheetJobStatusBanner sheet={sheet} onRetry={onRetrySheetJob} />
      )}
      {transforms.length > 0 && !archivedView && (
        <TransformBar transforms={transforms} onTransform={onTransform} sheet={sheet} />
      )}
      <div className="flex-1 overflow-auto">
        <SheetTable
          sheet={sheet}
          colActions={archivedView ? [] : colActions}
          onAddRow={archivedView ? null : onAddRow}
          onAddColumn={archivedView ? null : onAddColumn}
          onSetColumnsKeys={archivedView ? null : onSetColumnsKeys}
          onResizeColumn={archivedView ? null : onResizeColumn}
          onDeleteColumn={archivedView ? null : onDeleteColumn}
          onRequestDeleteRows={archivedView ? null : onRequestDeleteRows}
          onRequestDeleteColumns={archivedView ? null : onRequestDeleteColumns}
          onMoveColumn={archivedView ? null : onMoveColumn}
          onSortColumn={archivedView ? null : onSortColumn}
          onMoveRow={archivedView ? null : onMoveRow}
          onRemoveRow={archivedView ? null : onRemoveRow}
          onRetryRow={archivedView ? null : onRetryRow}
          onRetrySheetJob={archivedView ? null : onRetrySheetJob}
          readOnly={archivedView}
        />
      </div>
    </div>
  );
}

// ============================================================================
// TransformBar - thin horizontal strip of transform CTAs nested in the workspace
// ============================================================================

function TransformBar({ transforms, onTransform, sheet }) {
  const rowCount = sheet.rows.length;
  const entityLabel = ENTITY_TYPES[sheet.entityType].label.toLowerCase();
  const noun = rowCount === 1 ? entityLabel.replace(/s$/, "") : entityLabel;
  const empty = rowCount === 0;
  const actions = transforms.map(transform => {
    const eligibleCount = eligibleRowsForTransform(transform, sheet.rows).length;
    return {
      transform,
      rowCount: eligibleCount,
      disabled: empty || eligibleCount === 0 || transform.unwired,
    };
  });
  const {
    barRef,
    labelRef,
    moreMeasureRef,
    visibleCount,
    setButtonMeasureRef,
  } = useTransformOverflow(actions);
  const visibleActions = actions.slice(0, visibleCount);
  const hiddenActions = actions.slice(visibleCount);

  return (
    <div ref={barRef} className="relative bg-white border-b border-zinc-200 px-4 xl:px-6 py-2.5 flex items-center gap-2 overflow-visible min-w-0">
      <span ref={labelRef} className="text-xs font-semibold text-zinc-700 inline-flex items-center gap-1.5 shrink-0">
        <i className="ph-bold ph-magic-wand text-purple-600"></i>
        {empty
          ? <span className="text-zinc-400">Add rows to enable transforms</span>
          : <span>Apply to all <span className="text-purple-700">{rowCount}</span> {noun} <i className="ph ph-arrow-right text-zinc-400 text-[10px]"></i></span>}
      </span>
      {visibleActions.map(action => (
        <TransformButton
          key={action.transform.key}
          transform={action.transform}
          rowCount={action.rowCount}
          disabled={action.disabled}
          sheetName={sheet.name}
          sheet={sheet}
          onClick={(options) => onTransform(action.transform, options)}
        />
      ))}
      {hiddenActions.length > 0 && (
        <TransformOverflowMenu
          actions={hiddenActions}
          sheet={sheet}
          onTransform={onTransform}
        />
      )}
      <div
        className="fixed flex items-center gap-2 invisible pointer-events-none"
        style={{ left: -10000, top: -10000 }}
        aria-hidden="true"
      >
        {actions.map(action => (
          <div
            key={action.transform.key}
            ref={(node) => setButtonMeasureRef(action.transform.key, node)}
            className="shrink-0"
          >
            <TransformButtonMeasure
              transform={action.transform}
              rowCount={action.rowCount}
              disabled={action.disabled}
              sheet={sheet}
            />
          </div>
        ))}
        <div ref={moreMeasureRef} className="shrink-0">
          <TransformOverflowTrigger hiddenCount={actions.length} open={false} />
        </div>
      </div>
    </div>
  );
}

const TRANSFORM_BAR_GAP_PX = 8;
const TRANSFORM_MORE_MENU_WIDTH_PX = 360;

function useTransformOverflow(actions) {
  const barRef = useRef(null);
  const labelRef = useRef(null);
  const moreMeasureRef = useRef(null);
  const buttonMeasureRefs = useRef({});
  const [visibleCount, setVisibleCount] = useState(actions.length);
  const measurementKey = actions
    .map(action => `${action.transform.key}:${action.rowCount}:${action.disabled ? "1" : "0"}`)
    .join("|");

  const setButtonMeasureRef = useCallback((key, node) => {
    if (node) buttonMeasureRefs.current[key] = node;
    else delete buttonMeasureRefs.current[key];
  }, []);

  const recompute = useCallback(() => {
    const bar = barRef.current;
    if (!bar) return;
    if (actions.length === 0) {
      setVisibleCount(0);
      return;
    }

    const widths = actions.map(action => {
      const node = buttonMeasureRefs.current[action.transform.key];
      return node ? node.getBoundingClientRect().width : 0;
    });
    if (widths.some(width => width <= 0)) {
      setVisibleCount(prev => (prev === actions.length ? prev : actions.length));
      return;
    }

    const style = window.getComputedStyle(bar);
    const horizontalPadding = parseFloat(style.paddingLeft || "0") + parseFloat(style.paddingRight || "0");
    const available = Math.max(0, bar.clientWidth - horizontalPadding);
    const labelWidth = labelRef.current ? labelRef.current.getBoundingClientRect().width : 0;
    const moreWidth = moreMeasureRef.current ? moreMeasureRef.current.getBoundingClientRect().width : 96;
    const buttonWidth = widths.reduce((sum, width) => sum + width, 0);
    const allButtonsWidth = buttonWidth + Math.max(0, widths.length - 1) * TRANSFORM_BAR_GAP_PX;
    const allFitWidth = labelWidth + TRANSFORM_BAR_GAP_PX + allButtonsWidth;

    if (allFitWidth <= available) {
      setVisibleCount(prev => (prev === actions.length ? prev : actions.length));
      return;
    }

    let nextVisibleCount = 0;
    let visibleButtonsWidth = 0;
    for (let i = 0; i < widths.length; i += 1) {
      const candidateButtonsWidth = visibleButtonsWidth
        + widths[i]
        + (nextVisibleCount > 0 ? TRANSFORM_BAR_GAP_PX : 0);
      const candidateWidth = labelWidth
        + TRANSFORM_BAR_GAP_PX
        + candidateButtonsWidth
        + TRANSFORM_BAR_GAP_PX
        + moreWidth;

      if (candidateWidth > available) break;
      nextVisibleCount += 1;
      visibleButtonsWidth = candidateButtonsWidth;
    }

    setVisibleCount(prev => (prev === nextVisibleCount ? prev : nextVisibleCount));
  }, [measurementKey, actions.length]);

  useEffect(() => {
    let active = true;
    let raf = null;
    const schedule = () => {
      if (!active) return;
      if (raf !== null) window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(() => {
        raf = null;
        if (active) recompute();
      });
    };

    schedule();
    const nodes = [
      barRef.current,
      labelRef.current,
      moreMeasureRef.current,
      ...actions.map(action => buttonMeasureRefs.current[action.transform.key]),
    ].filter(Boolean);
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(schedule) : null;
    if (observer) nodes.forEach(node => observer.observe(node));
    window.addEventListener("resize", schedule);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(schedule);
    }

    return () => {
      active = false;
      if (raf !== null) window.cancelAnimationFrame(raf);
      if (observer) observer.disconnect();
      window.removeEventListener("resize", schedule);
    };
  }, [recompute, measurementKey]);

  useEffect(() => {
    setVisibleCount(prev => Math.min(prev, actions.length));
  }, [actions.length]);

  return {
    barRef,
    labelRef,
    moreMeasureRef,
    visibleCount,
    setButtonMeasureRef,
  };
}

function TransformOverflowTrigger({ hiddenCount, open, buttonRef, onClick }) {
  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onClick}
      aria-label={`${hiddenCount} more action${hiddenCount === 1 ? "" : "s"}`}
      aria-haspopup="menu"
      aria-expanded={open}
      title={`${hiddenCount} more action${hiddenCount === 1 ? "" : "s"}`}
      className="inline-flex h-11 w-8 shrink-0 items-center justify-center rounded border border-zinc-200 bg-white px-1 py-1 text-[10px] font-semibold text-zinc-700 hover:border-purple-200 hover:bg-purple-50 hover:text-purple-700 transition-colors"
    >
      <span
        className="leading-none"
        style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}
      >
        More
      </span>
    </button>
  );
}

function TransformOverflowMenu({ actions, sheet, onTransform }) {
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const recomputePos = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportPad = 8;
    const width = Math.min(TRANSFORM_MORE_MENU_WIDTH_PX, window.innerWidth - viewportPad * 2);
    const left = Math.max(
      viewportPad,
      Math.min(rect.right - width, window.innerWidth - width - viewportPad),
    );
    const below = window.innerHeight - rect.bottom - viewportPad - 4;
    const above = rect.top - viewportPad - 4;
    const openUp = below < 240 && above > below;
    const availableHeight = openUp ? above : below;
    const maxHeight = Math.max(160, Math.min(480, availableHeight));
    setPanelPos({
      top: openUp ? Math.max(viewportPad, rect.top - maxHeight - 4) : rect.bottom + 4,
      left,
      width,
      maxHeight,
    });
  }, []);

  useClickOutside([triggerRef, panelRef], () => setOpen(false), open);

  useEffect(() => {
    if (!open) {
      setPanelPos(null);
      return;
    }

    recomputePos();
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    let raf = null;
    function scheduleRecompute(e) {
      if (panelRef.current && e?.target && panelRef.current.contains(e.target)) return;
      if (raf !== null) window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(() => {
        raf = null;
        recomputePos();
      });
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", scheduleRecompute);
    window.addEventListener("scroll", scheduleRecompute, true);
    return () => {
      if (raf !== null) window.cancelAnimationFrame(raf);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", scheduleRecompute);
      window.removeEventListener("scroll", scheduleRecompute, true);
    };
  }, [open, recomputePos]);

  const panel = open && panelPos && (
    <div
      ref={panelRef}
      role="menu"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      className="fixed flex flex-col overflow-hidden bg-white border border-zinc-200 rounded-lg shadow-xl prospector-fade-in"
      style={{
        top: panelPos.top,
        left: panelPos.left,
        width: panelPos.width,
        maxHeight: panelPos.maxHeight,
        zIndex: 9999,
      }}
    >
      <div className="border-b border-zinc-100 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
        More actions
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-1.5 space-y-1">
        {actions.map(action => (
          <TransformButton
            key={action.transform.key}
            transform={action.transform}
            rowCount={action.rowCount}
            disabled={action.disabled}
            sheetName={sheet.name}
            sheet={sheet}
            variant="menu"
            onClick={(options) => onTransform(action.transform, options)}
            onAfterRun={() => setOpen(false)}
          />
        ))}
      </div>
    </div>
  );

  return (
    <>
      <TransformOverflowTrigger
        buttonRef={triggerRef}
        hiddenCount={actions.length}
        open={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(value => !value);
        }}
      />
      {panel && ReactDOM.createPortal(panel, document.body)}
    </>
  );
}

function transformButtonClass(disabled, variant = "inline") {
  if (variant === "menu") {
    return `w-full flex items-start gap-2.5 px-3 py-2.5 text-left rounded-md border transition-colors ${
      disabled
        ? "text-zinc-400 bg-zinc-50 border-zinc-100 cursor-not-allowed"
        : "text-purple-700 bg-white border-transparent hover:bg-purple-50 hover:border-purple-100"
    }`;
  }

  return `flex flex-col items-start gap-0 px-3 py-1.5 text-xs font-medium rounded border transition-colors ${
    disabled
      ? "text-zinc-400 bg-zinc-50 border-zinc-200 cursor-not-allowed"
      : "text-purple-700 bg-purple-50 hover:bg-purple-100 border-purple-200"
  }`;
}

function transformFallbackCreditRowCount(transform, sheet) {
  if (!sheet || !PROFILE_FALLBACK_TRANSFORM_KEYS.has(transform?.actionKey)) return 0;
  return eligibleRowsNeedingProfileFallback(transform, sheet.rows).length;
}

function transformCostCredits(transform, rowCount, sheet) {
  if (transform?.fallbackCredits) {
    return transformFallbackCreditRowCount(transform, sheet) * transform.fallbackCredits;
  }
  return transformMinimumCredits(transform, rowCount);
}

function transformEnabledCostLabel(transform, rowCount, sheet) {
  const credits = transformCostCredits(transform, rowCount, sheet);
  if (credits > 0) {
    return `min ${credits.toLocaleString()} credits`;
  }
  return transform.pricingLabel || "No credits";
}

function TransformButtonInlineContent({ transform, rowCount, disabled, sheet }) {
  const yieldsMeta = ENTITY_TYPES[transform.yields];
  const disabledLabel = transform.fallbackPricingLabel || transform.pricingLabel || `${transform.credits} credits/row`;
  return (
    <React.Fragment>
      <span className="inline-flex items-center gap-1.5">
        <i className={`ph ${transform.icon}`}></i>
        <span>{transform.label}</span>
        <span className={disabled ? "text-zinc-300 inline-flex items-center gap-0.5" : "text-purple-400 inline-flex items-center gap-0.5"}>
          <i className="ph ph-arrow-right text-[10px]"></i>
          <i className={`ph ${yieldsMeta.icon} text-[10px]`}></i>
        </span>
      </span>
      <span className={`text-[10px] font-normal ${disabled ? "text-zinc-300" : "text-purple-500/80"}`}>
        {disabled
          ? disabledLabel
          : transformEnabledCostLabel(transform, rowCount, sheet)}
      </span>
    </React.Fragment>
  );
}

function TransformButtonMenuContent({ transform, rowCount, disabled, sheet }) {
  const yieldsMeta = ENTITY_TYPES[transform.yields];
  const disabledLabel = transform.fallbackPricingLabel || transform.pricingLabel || `${transform.credits} credits/row`;
  return (
    <React.Fragment>
      <span className={`mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded border ${disabled ? "border-zinc-200 bg-white text-zinc-300" : "border-purple-100 bg-purple-50 text-purple-700"}`}>
        <i className={`ph ${transform.icon}`}></i>
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block text-sm font-semibold leading-tight ${disabled ? "text-zinc-400" : "text-zinc-900"}`}>
          {transform.label}
        </span>
        <span className={`mt-0.5 flex items-center gap-1 text-[11px] leading-tight ${disabled ? "text-zinc-300" : "text-purple-500/80"}`}>
          {disabled
            ? disabledLabel
            : transformEnabledCostLabel(transform, rowCount, sheet)}
        </span>
      </span>
      <span className={disabled ? "mt-1 text-zinc-300 inline-flex items-center gap-0.5" : "mt-1 text-purple-400 inline-flex items-center gap-0.5"}>
        <i className="ph ph-arrow-right text-[10px]"></i>
        <i className={`ph ${yieldsMeta.icon} text-[10px]`}></i>
      </span>
    </React.Fragment>
  );
}

function TransformButtonMeasure({ transform, rowCount, disabled, sheet }) {
  return (
    <button
      type="button"
      tabIndex={-1}
      disabled={disabled}
      className={transformButtonClass(disabled)}
    >
      <TransformButtonInlineContent
        transform={transform}
        rowCount={rowCount}
        disabled={disabled}
        sheet={sheet}
      />
    </button>
  );
}

function requiresCreditWarning(transform, sheet) {
  if (!transform || transform.unwired || !transform.actionKey) return false;
  if (!sheet || !isPersistedSheetId(sheet.id)) return false;
  if (transform.fallbackCredits) {
    return transformFallbackCreditRowCount(transform, sheet) > 0;
  }
  return (transform.credits || 0) > 0;
}

function transformCreditWarningNote(transform) {
  if (PROFILE_FALLBACK_TRANSFORM_KEYS.has(transform?.actionKey)) {
    return "Rows that already have a person id or slug do not use credits; this only covers rows that need a profile lookup first.";
  }
  if (transform?.key === "competitors") {
    return "Actual charge follows the competitor listing endpoint result cost.";
  }
  if (transform?.key === "monitor") {
    return "Monitor charges once when creating a feed; updating an existing feed may not charge again.";
  }
  return "Actual charge may vary when result-based pricing returns more data.";
}

function transformRunLabel(transform, inputFields = []) {
  if (transform?.key === "monitor") return "Start Monitoring";
  return inputFields.length > 0 ? "Search" : "Get Results";
}

function transformMinimumCredits(transform, rowCount) {
  const credits = transform?.credits || 0;
  return transform?.flatCreditCost ? credits : rowCount * credits;
}

function TransformButton({ transform, rowCount, disabled, sheetName, sheet, onClick, variant = "inline", onAfterRun }) {
  const [hover, setHover] = useState(false);
  const [lockedOpen, setLockedOpen] = useState(false);
  const [panelHover, setPanelHover] = useState(false);
  const [panelPos, setPanelPos] = useState(null);
  const [inputValues, setInputValues] = useState({});
  const [inputError, setInputError] = useState("");
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const isMenu = variant === "menu";
  const yieldsMeta = ENTITY_TYPES[transform.yields];
  const totalCredits = transformCostCredits(transform, rowCount, sheet);
  const warningKey = transformCreditWarningStorageKey(transform);
  const showConfirm = requiresCreditWarning(transform, sheet);
  const inputFields = Array.isArray(transform.inputFields) ? transform.inputFields : [];
  const supportsResultLimit = transform.supportsResultLimit === true;
  const hasInputPanel = inputFields.length > 0 || supportsResultLimit;
  const hasRequiredInputFields = inputFields.some(field => field.required);
  const canRememberCreditWarning = !hasRequiredInputFields && !transform.forceCreditWarning;
  const skipCreditWarning = showConfirm && canRememberCreditWarning && getLocalStorageFlag(warningKey);
  const runDirectlyAfterRememberedWarning = skipCreditWarning && !hasRequiredInputFields;
  const runLabel = transformRunLabel(transform, inputFields);
  const confirmPanelSignal = isMenu ? (lockedOpen || panelHover) : (hover || lockedOpen || panelHover);
  const showConfirmPanel = !disabled && showConfirm && !skipCreditWarning && confirmPanelSignal;
  const showInputPanel = !disabled && hasInputPanel && !runDirectlyAfterRememberedWarning && (lockedOpen || panelHover);
  const showFloatingPanel = showConfirmPanel || showInputPanel;

  useClickOutside([triggerRef, panelRef], () => setLockedOpen(false), lockedOpen);

  useEffect(() => {
    if (isMenu || !showFloatingPanel || !triggerRef.current) {
      setPanelPos(null);
      return;
    }

    function updatePosition() {
      const rect = triggerRef.current.getBoundingClientRect();
      const panelWidth = 320;
      const viewportPad = 8;
      const left = Math.max(
        viewportPad,
        Math.min(rect.left, window.innerWidth - panelWidth - viewportPad),
      );
      setPanelPos({
        top: rect.bottom,
        left,
      });
    }

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [showFloatingPanel, isMenu]);

  function run(options = {}) {
    if (disabled) return;
    setLockedOpen(false);
    setHover(false);
    setPanelHover(false);
    setInputError("");
    onClick(options);
    if (typeof onAfterRun === "function") onAfterRun();
  }

  function handleButtonClick() {
    if (disabled) return;
    if (showConfirm && runDirectlyAfterRememberedWarning) {
      run({ skipCreditWarning: true });
      return;
    }
    if (showConfirm || hasInputPanel) {
      setLockedOpen(true);
      return;
    }
    run();
  }

  function setInputValue(key, value) {
    setInputValues(prev => ({ ...prev, [key]: value }));
    if (inputError) setInputError("");
  }

  function inputParamsOrNull() {
    const params = {};
    for (const field of inputFields) {
      const raw = String(inputValues[field.key] || "").trim();
      if (field.required && !raw) {
        setInputError(`${field.label} is required.`);
        return null;
      }
      if (!raw) continue;
      params[field.key] = field.key === "country" ? raw.toUpperCase() : raw;
    }
    if (supportsResultLimit && inputValues.result_limit_enabled) {
      const rawLimit = String(inputValues.result_limit || "").trim();
      if (!rawLimit || !/^\d+$/.test(rawLimit) || Number(rawLimit) <= 0) {
        setInputError("Result limit must be a positive whole number.");
        return null;
      }
      params.result_limit = Number(rawLimit);
    }
    return params;
  }

  function runWithInputs(options = {}) {
    const params = inputParamsOrNull();
    if (params === null) return;
    run({ ...options, params });
  }

  function runFromConfirm(options = {}) {
    if (hasInputPanel) {
      runWithInputs(options);
      return;
    }
    run(options);
  }

  function renderInputFields() {
    if (inputFields.length === 0) return null;
    return (
      <React.Fragment>
        {inputFields.map(field => (
          <label key={field.key} className="block">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
              {field.label}{field.required ? " *" : ""}
            </span>
            <input
              type="text"
              value={inputValues[field.key] || ""}
              maxLength={field.maxLength || undefined}
              onChange={(e) => setInputValue(field.key, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (showConfirmPanel) {
                    runFromConfirm({ neverShowCreditWarning: false });
                  } else {
                    runWithInputs();
                  }
                }
              }}
              placeholder={field.placeholder || ""}
              className="w-full rounded-md border border-zinc-200 bg-white px-2.5 py-2 text-sm text-zinc-900 outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
            />
          </label>
        ))}
      </React.Fragment>
    );
  }

  function renderResultLimitField() {
    if (!supportsResultLimit) return null;
    const enabled = inputValues.result_limit_enabled === true;
    return (
      <label className={`flex items-center gap-2 rounded-md border px-2.5 py-2 text-sm transition-colors ${
        enabled
          ? "border-zinc-200 bg-white text-zinc-600"
          : "border-zinc-100 bg-zinc-50/70 text-zinc-400"
      }`}>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setInputValue("result_limit_enabled", e.target.checked)}
          className="h-3.5 w-3.5 rounded border-zinc-300 text-zinc-500 accent-zinc-500"
        />
        <span className="shrink-0 text-xs font-medium">Limit it to</span>
        <input
          type="number"
          min="1"
          step="1"
          inputMode="numeric"
          disabled={!enabled}
          value={inputValues.result_limit || ""}
          onChange={(e) => setInputValue("result_limit", e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (showConfirmPanel) {
                runFromConfirm({ neverShowCreditWarning: false });
              } else {
                runWithInputs();
              }
            }
          }}
          className={`h-8 min-w-0 flex-1 rounded border px-2 text-sm tabular-nums outline-none transition-colors ${
            enabled
              ? "border-zinc-200 bg-white text-zinc-900 focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
              : "border-zinc-100 bg-zinc-100 text-zinc-400 cursor-not-allowed"
          }`}
        />
        <span className="shrink-0 text-xs font-medium">results</span>
      </label>
    );
  }

  function renderInputError() {
    if (!inputError) return null;
    return (
      <div className="rounded-md bg-red-50 border border-red-100 px-2.5 py-2 text-xs text-red-700">
        {inputError}
      </div>
    );
  }

  function renderCostBlock({ withNote = false } = {}) {
    const costLabel = transform.fallbackCredits && totalCredits > 0
      ? (transform.fallbackPricingLabel || `${transform.fallbackCredits} credits/source`)
      : (transform.pricingLabel || `${transform.credits} credits/source`);
    return (
      <div className="rounded-md bg-zinc-50 border border-zinc-100 px-3 py-2">
        <div className="flex items-baseline justify-between text-xs">
          <span className="text-zinc-500">{costLabel}</span>
          <span className="font-semibold text-zinc-900 inline-flex items-center gap-1">
            <CreditAmount value={totalCredits} />
            <span>minimum</span>
          </span>
        </div>
        {withNote && (
          <div className="text-[11px] text-zinc-500 mt-1">
            {transformCreditWarningNote(transform)}
          </div>
        )}
      </div>
    );
  }

  const actionPanel = showFloatingPanel && (isMenu || panelPos) ? (
    <div
      ref={panelRef}
      onMouseEnter={() => setPanelHover(true)}
      onMouseLeave={() => setPanelHover(false)}
      className={`${isMenu ? "mt-1 w-full" : "fixed w-80"} bg-white border border-zinc-200 rounded-lg shadow-xl z-[9999] p-3 prospector-fade-in`}
      style={isMenu ? undefined : { top: panelPos.top, left: panelPos.left }}
    >
      <div className="flex items-start gap-2.5 mb-3">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded bg-purple-50 text-purple-700 border border-purple-100">
          <i className={`ph ${transform.icon}`}></i>
        </span>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-zinc-900">{transform.label}</div>
          <div className="text-xs text-zinc-500 truncate">Creates a new {yieldsMeta.label.toLowerCase()} sheet from "{sheetName}"</div>
        </div>
      </div>
      {showConfirmPanel ? (
        <div className="space-y-2.5">
          {renderInputFields()}
          {renderResultLimitField()}
          {renderInputError()}
          {renderCostBlock({ withNote: true })}
          {canRememberCreditWarning ? (
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLocalStorageFlag(warningKey, true);
                  runFromConfirm({ neverShowCreditWarning: true });
                }}
                className="rounded-md bg-purple-600 px-3 py-2 text-left text-white hover:bg-purple-700 transition-colors"
              >
                <div className="text-xs font-semibold">{runLabel}</div>
                <div className="text-[10px] text-purple-100 leading-snug">Never show credit warning again</div>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  runFromConfirm({ neverShowCreditWarning: false });
                }}
                className="rounded-md border border-zinc-200 bg-white px-3 py-2 text-left text-zinc-800 hover:bg-zinc-50 transition-colors"
              >
                <div className="text-xs font-semibold">{runLabel}</div>
                <div className="text-[10px] text-zinc-500 leading-snug">Keep confirmation dialog</div>
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                runFromConfirm({ neverShowCreditWarning: false });
              }}
              className="w-full rounded-md bg-purple-600 px-3 py-2 text-sm font-semibold text-white hover:bg-purple-700 transition-colors"
            >
              {runLabel}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2.5">
          {renderInputFields()}
          {renderResultLimitField()}
          {renderInputError()}
          {renderCostBlock()}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              runWithInputs();
            }}
            className="w-full rounded-md bg-purple-600 px-3 py-2 text-sm font-semibold text-white hover:bg-purple-700 transition-colors"
          >
            {runLabel}
          </button>
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className={isMenu ? "relative w-full" : "relative shrink-0"} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <button
        ref={triggerRef}
        type="button"
        role={isMenu ? "menuitem" : undefined}
        disabled={disabled}
        onClick={handleButtonClick}
        className={transformButtonClass(disabled, variant)}
      >
        {isMenu ? (
          <TransformButtonMenuContent
            transform={transform}
            rowCount={rowCount}
            disabled={disabled}
            sheet={sheet}
          />
        ) : (
          <TransformButtonInlineContent
            transform={transform}
            rowCount={rowCount}
            disabled={disabled}
            sheet={sheet}
          />
        )}
      </button>
      {actionPanel && (isMenu ? actionPanel : ReactDOM.createPortal(actionPanel, document.body))}
      {!isMenu && hover && !disabled && !showConfirm && !hasInputPanel && (
        <div className="absolute left-0 top-full mt-1 w-72 bg-white border border-zinc-200 rounded-lg shadow-xl z-30 p-3 prospector-fade-in pointer-events-none">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-400 mb-1.5">Preview</div>
          <div className="text-xs text-zinc-700 space-y-1">
            <div className="flex items-center gap-1.5">
              <i className={`ph ${yieldsMeta.icon} text-purple-600`}></i>
              <span>Creates a new <strong>{yieldsMeta.label}</strong> sheet</span>
            </div>
            <div>
              From all <strong>{rowCount}</strong> rows in <span className="italic text-zinc-500 truncate inline-block max-w-[12rem] align-bottom">"{sheetName}"</span>
            </div>
            <div className="pt-1 border-t border-zinc-100 flex justify-between">
              <span className="text-zinc-500">Estimated cost</span>
              <span className="font-semibold text-purple-700">{totalCredits.toLocaleString()} credits</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// EditableSheetName - click-to-edit sheet title
// ============================================================================

function EditableSheetName({ name, onRename }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const inputRef = useRef(null);

  useEffect(() => { if (!editing) setDraft(name); }, [name, editing]);
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  function commit() {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== name) onRename(trimmed);
    setDraft(trimmed || name);
    setEditing(false);
  }

  function cancel() {
    setDraft(name);
    setEditing(false);
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          else if (e.key === "Escape") { e.preventDefault(); cancel(); }
        }}
        className="text-xl xl:text-2xl font-semibold text-zinc-900 border-b-2 border-purple-500 focus:outline-none bg-transparent px-0 py-0 min-w-0 max-w-full"
        style={{ width: `${Math.max(8, draft.length + 1)}ch` }}
      />
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      title="Click to rename"
      className="group inline-flex items-center gap-1.5 text-xl xl:text-2xl font-semibold text-zinc-900 truncate hover:text-purple-700 transition-colors text-left max-w-full bg-transparent border-0 p-0"
    >
      <span className="truncate">{name}</span>
      <i className="ph ph-pencil-simple text-sm text-zinc-300 group-hover:text-purple-500 shrink-0"></i>
    </button>
  );
}

// ============================================================================
// LineageBreadcrumb - collapses middle crumbs when chain is deep
// ============================================================================

function LineageBreadcrumb({ lineage, level, onSelectSheet }) {
  const [expanded, setExpanded] = useState(false);
  // Collapse when chain has >4 levels: show root, "…", parent, current.
  const collapsed = !expanded && lineage.length > 4;
  const visible = collapsed
    ? [
        { kind: "sheet", sheet: lineage[0] },
        { kind: "ellipsis", hidden: lineage.slice(1, -2) },
        { kind: "sheet", sheet: lineage[lineage.length - 2] },
        { kind: "sheet", sheet: lineage[lineage.length - 1] },
      ]
    : lineage.map(s => ({ kind: "sheet", sheet: s }));

  return (
    <div className="flex items-center gap-1 text-xs mb-2 flex-wrap text-zinc-500">
      <span className="font-mono text-[10px] text-zinc-400 shrink-0">L{level}</span>
      <span className="text-zinc-300 mx-0.5">·</span>
      {visible.map((entry, i) => {
        const isLast = i === visible.length - 1;
        if (entry.kind === "ellipsis") {
          return (
            <React.Fragment key="ellipsis">
              <button
                onClick={() => setExpanded(true)}
                title={`Expand ${entry.hidden.length} hidden level${entry.hidden.length === 1 ? "" : "s"}`}
                className="text-zinc-400 hover:text-purple-600 px-0.5"
              >
                <i className="ph ph-dots-three-bold"></i>
              </button>
              <span className="text-zinc-300 mx-0.5">/</span>
            </React.Fragment>
          );
        }
        const s = entry.sheet;
        const nextEntry = visible[i + 1];
        const nextSheet = nextEntry && nextEntry.kind === "sheet" ? nextEntry.sheet : null;
        return (
          <React.Fragment key={s.id}>
            <button
              onClick={() => !isLast && onSelectSheet(s.id)}
              disabled={isLast}
              className={
                isLast
                  ? "text-zinc-700 font-medium truncate max-w-[180px]"
                  : "text-zinc-500 hover:text-purple-600 underline-offset-2 hover:underline truncate max-w-[140px]"
              }
            >
              {s.name}
            </button>
            {!isLast && (
              <span className="text-zinc-300 inline-flex items-center gap-1 shrink-0 mx-0.5">
                <span>/</span>
                {nextSheet?.producedBy && (
                  <span className="text-purple-600/70 italic">{nextSheet.producedBy}</span>
                )}
                <span>/</span>
              </span>
            )}
          </React.Fragment>
        );
      })}
      {expanded && lineage.length > 4 && (
        <button
          onClick={() => setExpanded(false)}
          className="ml-2 text-[10px] text-zinc-400 hover:text-zinc-700 underline underline-offset-2"
        >
          collapse
        </button>
      )}
    </div>
  );
}

// ============================================================================
// SheetHeader - chip, breadcrumb, toolbar
// ============================================================================

function SheetHeader({ sheet, lineage, meta, onRenameSheet, onDownloadCSV, onDownloadXLSX, onSelectSheet, onOpenTree, readOnly = false }) {
  const level = lineage.length - 1; // 0 = root
  const hasDownloadActions = typeof onDownloadCSV === "function" || typeof onDownloadXLSX === "function";
  return (
    <div className="bg-white border-b border-zinc-200 p-4 xl:p-6">
      {/* Mobile-only: open tree drawer */}
      <div className="lg:hidden mb-2">
        <button
          onClick={onOpenTree}
          className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-zinc-700 bg-white border border-zinc-200 rounded hover:bg-zinc-50"
        >
          <i className="ph ph-tree-structure"></i> Sheets
        </button>
      </div>
      {/* Lineage breadcrumb */}
      {lineage.length > 1 && (
        <LineageBreadcrumb
          lineage={lineage}
          level={level}
          onSelectSheet={onSelectSheet}
        />
      )}

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 xl:gap-3 flex-wrap mb-1">
            {readOnly ? (
              <h1 className="text-xl xl:text-2xl font-semibold text-zinc-900 truncate">{sheet.name}</h1>
            ) : (
              <EditableSheetName
                name={sheet.name}
                onRename={(newName) => onRenameSheet(sheet.id, newName)}
              />
            )}
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full border ${meta.chip}`}>
              <i className={`ph ${meta.icon}`}></i>
              {meta.label}
            </span>
            {readOnly && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 text-[10px] font-semibold uppercase tracking-wide border border-zinc-200">
                <i className="ph ph-archive-box"></i>
                Archived
              </span>
            )}
            {level === 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500 text-[10px] font-semibold uppercase tracking-wide">Root</span>
            )}
          </div>
          <p className="text-sm xl:text-base text-zinc-600">
            {sheet.rows.length} row{sheet.rows.length === 1 ? "" : "s"}
            {sheet.origin && <span className="text-zinc-400"> · {sheet.origin}</span>}
          </p>
        </div>

        {/* Toolbar - Download only; Add row, Add column, and Transform live elsewhere */}
        {hasDownloadActions && (
          <div className="flex items-center gap-2 flex-wrap shrink-0">
            <ActionMenu
              label="Download"
              icon="ph-download-simple"
              items={[
                typeof onDownloadCSV === "function" && { key: "csv",  label: "Download as CSV",  icon: "ph-file-csv",  onClick: onDownloadCSV },
                typeof onDownloadXLSX === "function" && { key: "xlsx", label: "Download as XLSX", icon: "ph-file-xls", onClick: onDownloadXLSX },
              ].filter(Boolean)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// ActionMenu - generic dropdown
// ============================================================================

function ActionMenu({ label, icon, items, variant }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false), open);

  const baseClass = variant === "primary"
    ? "px-3 xl:px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded hover:bg-purple-700 transition-colors"
    : "px-3 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-200 rounded hover:bg-zinc-50 transition-colors";

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`${baseClass} inline-flex items-center gap-1.5`}
      >
        <i className={`ph ${icon}`}></i> {label}
        <i className="ph ph-caret-down text-xs"></i>
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-64 bg-white border border-zinc-200 rounded-lg shadow-lg z-30 py-1 prospector-fade-in">
          {items.map(item => (
            <button
              key={item.key}
              onClick={() => { item.onClick(); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 flex items-center gap-2"
            >
              <i className={`ph ${item.icon} text-zinc-500`}></i>
              <div className="flex-1">
                <div className="font-medium text-zinc-900">{item.label}</div>
                {item.detail && <div className="text-xs text-zinc-500">{item.detail}</div>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SheetTable
// ============================================================================

function ProspectorRetryButton({ onClick, title = "Retry enrichment", ariaLabel = "Retry enrichment", compact = false, flush = false, children = "Retry action" }) {
  if (compact) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        title={title}
        className="inline-flex h-5 w-5 items-center justify-center rounded border border-amber-300 bg-white text-amber-700 shadow-sm transition-colors hover:bg-amber-100 hover:text-amber-900"
      >
        <i className="ph ph-arrow-clockwise text-[10px]"></i>
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      title={title}
      className={`${flush ? "" : "mt-4"} inline-flex items-center gap-1.5 rounded-md bg-zinc-900 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-zinc-800`}
    >
      <i className="ph ph-arrow-clockwise text-sm"></i>
      {children}
    </button>
  );
}

function SheetEmptyRowsState({ sheet, canAddRow, onAddRow, onRetrySheetJob, colSpan }) {
  const meta = ENTITY_TYPES[sheet.entityType] || ENTITY_TYPES.companies;
  const canCreateRows = canAddRow && typeof onAddRow === "function";
  const isGenerated = isGeneratedSheet(sheet);
  const isLoading = !!sheet.progress;
  const failedJob = sheet.job?.status === JOB_STATUS.FAILED;
  const canRetrySheet = failedJob && typeof onRetrySheetJob === "function";
  const wasAborted = sheet.emptyState === "aborted";
  const title = isLoading
    ? `${sheet.progress.label || "Working"}...`
    : failedJob
      ? "Spreadsheet action failed"
      : wasAborted
        ? "No results found"
        : "No rows yet";
  const detail = isLoading
    ? "Results will appear here as they are found."
    : failedJob
      ? (sheet.job?.error || "Failed to run spreadsheet action.")
      : canCreateRows
        ? `Add a ${sheet.entityType === "companies" ? "company" : "person"} to start this sheet.`
        : wasAborted
          ? "The spreadsheet action was aborted before any rows were added."
          : isGenerated
            ? "This generated sheet has no results yet."
            : "This sheet has no rows.";
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-12 text-center bg-white border-b border-zinc-100">
        <div className="mx-auto flex max-w-md flex-col items-center">
          <span className={`mb-3 inline-flex h-10 w-10 items-center justify-center rounded border ${meta.chip || "bg-zinc-100 text-zinc-700 border-zinc-200"}`}>
            <i className={`ph ${isLoading ? "ph-circle-notch ph-spin" : failedJob ? "ph-warning-circle" : meta.icon}`}></i>
          </span>
          <div className="text-sm font-semibold text-zinc-900">{title}</div>
          <div className="mt-1 text-xs text-zinc-500">{detail}</div>
          {canRetrySheet && !isLoading && (
            <ProspectorRetryButton
              onClick={onRetrySheetJob}
              title={sheet.job?.error || "Retry action"}
              ariaLabel="Retry action"
            >
              Retry action
            </ProspectorRetryButton>
          )}
          {canCreateRows && !isLoading && !failedJob && (
            <button
              type="button"
              onClick={onAddRow}
              className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-purple-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-purple-700"
            >
              <i className="ph-bold ph-plus text-xs"></i>
              Add {sheet.entityType === "companies" ? "company" : "person"}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function SheetTable({ sheet, colActions, onAddRow, onAddColumn, onSetColumnsKeys, onResizeColumn, onDeleteColumn, onMoveColumn, onSortColumn, onMoveRow, onRemoveRow, onRetryRow, onRetrySheetJob, onRequestDeleteRows, onRequestDeleteColumns, readOnly = false }) {
  const canAddRow = !readOnly && canAppendRowsToSheet(sheet);
  const canMoveRows = !readOnly && typeof onMoveRow === "function" && (sheet.rows || []).length > 1;
  const canReorderColumns = !readOnly && typeof onMoveColumn === "function";
  const canResizeColumns = !readOnly && typeof onResizeColumn === "function";
  const canOpenColumnMenu = !readOnly;
  // Right-click row context menu state. ``canAddRow`` doubles as the "row is
  // user-managed" gate - updates rows are derived from transforms and aren't
  // user-removable.
  const [rowMenu, setRowMenu] = useState(null);
  const availableActions = readOnly ? [] : (colActions || []).filter(a => isPaidColumnActionAvailable(a, sheet.columns, sheet.rows));
  // Free columns (from the catalogue) the user could opt into via the picker.
  const catalogue = catalogueFor(sheet.entityType);
  const freeCatalogueCols = catalogue.filter(c => c.free && !c.default && c.user_addable !== false);
  const hasPickerOptions = !readOnly && freeCatalogueCols.length > 0 && typeof onSetColumnsKeys === "function";
  const canAddCol = availableActions.length > 0 || hasPickerOptions;
  const totalCols = 1 /* row# */ + sheet.columns.length + (canAddCol ? 1 : 0);

  // Keep the company anchor visible while scrolling wide company sheets.
  // Only logo + company name freeze; row numbers stay in normal table flow so
  // non-sticky data columns line up the same in the header and body.
  const stickyEnabled = sheet.entityType === "companies" && sheet.columns.length > 4;
  const rowNumHeaderRef = useRef(null);
  const col0HeaderRef = useRef(null);
  const [stickyLefts, setStickyLefts] = useState({ col0: 0, col1: 40 });

  const [dragKey, setDragKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const [dragRowId, setDragRowId] = useState(null);
  const [dragOverRowId, setDragOverRowId] = useState(null);
  const [dragOverRowPlacement, setDragOverRowPlacement] = useState(null);
  const [headerMenuRequest, setHeaderMenuRequest] = useState(null);
  const tableInteractionRef = useRef(null);
  const [selection, setSelection] = useState({ axis: null, ids: [], anchorId: null, focusId: null });
  const [dragSelection, setDragSelection] = useState(null);
  const selectedRowIds = selection.axis === "rows" ? new Set(selection.ids) : new Set();
  const selectedColumnKeys = selection.axis === "columns" ? new Set(selection.ids) : new Set();
  const canSelectRows = !readOnly && typeof onRequestDeleteRows === "function" && (sheet.rows || []).length > 0;
  const canSelectColumns = !readOnly && typeof onRequestDeleteColumns === "function";

  function clearDrag() {
    setDragKey(null);
    setDragOverKey(null);
  }

  function clearRowDrag() {
    setDragRowId(null);
    setDragOverRowId(null);
    setDragOverRowPlacement(null);
  }

  function idsForAxis(axis) {
    return axis === "rows"
      ? (sheet.rows || []).map(r => r._id)
      : (sheet.columns || []).map(c => c.key);
  }

  function rangeIds(axis, fromId, toId) {
    const ids = idsForAxis(axis);
    const fromIdx = ids.indexOf(fromId);
    const toIdx = ids.indexOf(toId);
    if (fromIdx === -1 || toIdx === -1) return [toId].filter(Boolean);
    const start = Math.min(fromIdx, toIdx);
    const end = Math.max(fromIdx, toIdx);
    return ids.slice(start, end + 1);
  }

  function focusTableInteraction() {
    if (tableInteractionRef.current) {
      tableInteractionRef.current.focus({ preventScroll: true });
    }
  }

  function isSelectionControlTarget(e) {
    return !!e.target?.closest?.(
      "button,input,select,textarea,[contenteditable='true'],[data-prospector-resize-handle],[data-prospector-drag-handle]",
    );
  }

  function startHeaderSelection(axis, id, e) {
    if (e.button !== 0 || isSelectionControlTarget(e)) return;
    if (axis === "rows" && !canSelectRows) return;
    if (axis === "columns" && !canSelectColumns) return;

    e.preventDefault();
    setRowMenu(null);
    clearDrag();
    clearRowDrag();
    focusTableInteraction();

    if (e.shiftKey) {
      setSelection(prev => {
        const liveIds = new Set(idsForAxis(axis));
        const anchorId = prev.axis === axis && liveIds.has(prev.anchorId) ? prev.anchorId : id;
        return {
          axis,
          ids: rangeIds(axis, anchorId, id),
          anchorId,
          focusId: id,
        };
      });
      setDragSelection(null);
      return;
    }

    const additive = e.metaKey || e.ctrlKey;
    if (additive) {
      setSelection(prev => {
        const currentIds = prev.axis === axis ? prev.ids : [];
        const exists = currentIds.includes(id);
        const ids = exists ? currentIds.filter(existingId => existingId !== id) : [...currentIds, id];
        return ids.length === 0
          ? { axis: null, ids: [], anchorId: null, focusId: null }
          : { axis, ids, anchorId: id, focusId: id };
      });
      return;
    }

    setSelection({ axis, ids: [id], anchorId: id, focusId: id });
    setDragSelection({ axis, anchorId: id });
  }

  function extendDragSelection(axis, id) {
    if (!dragSelection || dragSelection.axis !== axis) return;
    const ids = rangeIds(axis, dragSelection.anchorId, id);
    setSelection({ axis, ids, anchorId: dragSelection.anchorId, focusId: id });
  }

  function clearSelection() {
    setSelection({ axis: null, ids: [], anchorId: null, focusId: null });
    setDragSelection(null);
  }

  function handleSelectionKeyDown(e) {
    if (isSelectionControlTarget(e)) return;
    if (e.key === "Escape" && selection.axis) {
      e.preventDefault();
      clearSelection();
      return;
    }
    if (!selection.axis || selection.ids.length === 0) return;

    const axis = selection.axis;
    const ids = idsForAxis(axis);
    const focusId = selection.focusId || selection.anchorId || selection.ids[selection.ids.length - 1];
    const focusIdx = ids.indexOf(focusId);

    if (e.shiftKey) {
      const delta =
        axis === "rows" && e.key === "ArrowDown" ? 1 :
        axis === "rows" && e.key === "ArrowUp" ? -1 :
        axis === "columns" && e.key === "ArrowRight" ? 1 :
        axis === "columns" && e.key === "ArrowLeft" ? -1 :
        0;
      if (delta !== 0 && focusIdx !== -1) {
        e.preventDefault();
        const nextIdx = Math.max(0, Math.min(ids.length - 1, focusIdx + delta));
        const anchorId = selection.anchorId || focusId;
        setSelection({
          axis,
          ids: rangeIds(axis, anchorId, ids[nextIdx]),
          anchorId,
          focusId: ids[nextIdx],
        });
        return;
      }
    }

    if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      if (axis === "rows" && canSelectRows) {
        onRequestDeleteRows(selection.ids);
      } else if (axis === "columns" && canSelectColumns) {
        onRequestDeleteColumns(selection.ids);
      }
    }
  }

  function selectedRemovableColumnKeys() {
    if (selection.axis !== "columns") return [];
    const baseKeys = BASE_COLUMN_KEYS[sheet.entityType] || new Set();
    const visibleKeys = new Set((sheet.columns || []).map(c => c.key));
    return selection.ids.filter(k => visibleKeys.has(k) && !baseKeys.has(k) && k !== "logo");
  }

  function deleteColumnsFromMenu(key) {
    const selectedKeys = selection.axis === "columns" && selectedColumnKeys.has(key)
      ? selectedRemovableColumnKeys()
      : [];
    if (selectedKeys.length > 0 && selection.ids.length > 1 && canSelectColumns) {
      onRequestDeleteColumns(selection.ids);
      return;
    }
    if (typeof onDeleteColumn === "function") onDeleteColumn(key);
  }

  function deleteRowsFromMenu(rowId) {
    const selectedIds = selection.axis === "rows" && selectedRowIds.has(rowId)
      ? selection.ids
      : [];
    if (selectedIds.length > 1 && canSelectRows) {
      onRequestDeleteRows(selectedIds);
      return;
    }
    if (typeof onRemoveRow === "function") onRemoveRow(rowId);
  }

  useEffect(() => {
    if (!dragSelection) return;
    function onUp() {
      setDragSelection(null);
      document.body.style.userSelect = "";
    }
    document.body.style.userSelect = "none";
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
    };
  }, [dragSelection]);

  useEffect(() => {
    setSelection(prev => {
      if (!prev.axis) return prev;
      const liveIds = new Set(idsForAxis(prev.axis));
      const ids = prev.ids.filter(id => liveIds.has(id));
      if (ids.length === 0) return { axis: null, ids: [], anchorId: null, focusId: null };
      const anchorId = liveIds.has(prev.anchorId) ? prev.anchorId : ids[0];
      const focusId = liveIds.has(prev.focusId) ? prev.focusId : ids[ids.length - 1];
      if (ids.length === prev.ids.length && anchorId === prev.anchorId && focusId === prev.focusId) return prev;
      return { axis: prev.axis, ids, anchorId, focusId };
    });
  }, [sheet.id, sheet.rows, sheet.columns]);

  // Column resize. ``resizingKey`` is set while a drag is in progress; the
  // header keeps its actual rendered width during the drag because we paint
  // ``localResizePx`` into the inline style. On mouseup we PATCH the width via
  // ``onResizeColumn``. We bind the move/up listeners to ``window`` so the
  // drag survives the cursor leaving the th's bounds (typical resize UX).
  // Declared above the sticky useLayoutEffect because the effect's dep array
  // references resizingKey/localResizePx - TDZ would crash on first render.
  const [resizingKey, setResizingKey] = useState(null);
  const [localResizePx, setLocalResizePx] = useState(null);
  const resizeRef = useRef({ startX: 0, startWidth: 0, key: null });

  // Serialize column widths so resize → sticky-offset re-measure happens. We
  // use a string fingerprint instead of the array directly so the dep stays
  // referentially stable across renders that don't change widths.
  const widthsFingerprint = sheet.columns.map(c => `${c.key}:${colWidthPx(c)}`).join(",");

  React.useLayoutEffect(() => {
    if (!stickyEnabled) return;
    const firstCol = sheet.columns[0];
    const firstColW = col0HeaderRef.current?.getBoundingClientRect().width ||
      (resizingKey === firstCol?.key && typeof localResizePx === "number"
        ? localResizePx
        : colWidthPx(firstCol));
    setStickyLefts({
      col0: 0,
      col1: firstColW,
    });
  }, [stickyEnabled, sheet.columns.length, sheet.id, widthsFingerprint, resizingKey, localResizePx]);

  // 1px right-side box-shadow paints over any subpixel seam between
  // adjacent sticky cells with the same bg color. Without it, fractional
  // widths from getBoundingClientRect can leave a hairline gap that
  // exposes scrolled content underneath.
  function stickyHeaderStyle(left, bg = "#fafafa" /* zinc-50 */) {
    if (left === undefined) return undefined;
    return { position: "sticky", left, zIndex: 11, boxShadow: `1px 0 0 0 ${bg}` };
  }
  function stickyCellStyle(left, baseBg = "#ffffff") {
    if (left === undefined) return undefined;
    return {
      position: "sticky",
      left,
      zIndex: 5,
      backgroundColor: baseBg,
      boxShadow: `1px 0 0 0 ${baseBg}`,
    };
  }

  const onResizeMouseDown = useCallback((e, col) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const startWidth = colWidthPx(col);
    resizeRef.current = { startX: e.clientX, startWidth, key: col.key };
    setResizingKey(col.key);
    setLocalResizePx(startWidth);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    function onMove(ev) {
      const dx = ev.clientX - resizeRef.current.startX;
      const next = Math.max(COL_MIN_PX, Math.min(COL_MAX_PX, resizeRef.current.startWidth + dx));
      setLocalResizePx(next);
    }
    function onUp(ev) {
      const dx = ev.clientX - resizeRef.current.startX;
      const next = Math.max(COL_MIN_PX, Math.min(COL_MAX_PX, resizeRef.current.startWidth + dx));
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      const key = resizeRef.current.key;
      resizeRef.current = { startX: 0, startWidth: 0, key: null };
      setResizingKey(null);
      setLocalResizePx(null);
      // Skip PATCH if the user didn't actually move the handle (a click).
      if (Math.abs(dx) > 1 && typeof onResizeColumn === "function") {
        onResizeColumn(key, next);
      }
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [onResizeColumn]);

  function widthForCol(col) {
    if (resizingKey === col.key && typeof localResizePx === "number") return localResizePx;
    return colWidthPx(col);
  }

  function startColumnDrag(e, col) {
    if (resizeRef.current.key) {
      e.preventDefault();
      return;
    }
    setDragKey(col.key);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", col.key);
  }

  // Width of the trailing "+ Add column" cell when present. Kept narrower than
  // a data column since paid actions now live inside the picker dropdown.
  const TRAILING_COL_PX = 144;
  const ROW_NUM_PX = 40;
  const rowNumWidthStyle = fixedCellWidthStyle(ROW_NUM_PX);
  const trailingColWidthStyle = fixedCellWidthStyle(TRAILING_COL_PX);

  // Exact sum of column widths so the table renders at the correct pixel size.
  // ``table-layout: fixed`` + ``width: auto`` is unreliable across browsers -
  // Safari frequently falls back to auto-layout, which lets body content push
  // cells wider than their colgroup width and breaks header/cell alignment.
  // Setting an explicit numeric ``width`` removes the ambiguity.
  const tableWidthPx = ROW_NUM_PX
    + sheet.columns.reduce((sum, c) => sum + widthForCol(c), 0)
    + (canAddCol ? TRAILING_COL_PX : 0);

  return (
    <div className="px-4 sm:px-6 py-4 min-w-0">
      <div
        ref={tableInteractionRef}
        tabIndex={0}
        onKeyDown={handleSelectionKeyDown}
        className="w-full min-w-0 bg-white border border-zinc-200 rounded-lg shadow-sm focus:outline-none"
      >
        <div className="overflow-x-auto rounded-t-lg">
          <table className="text-sm border-separate" style={{ borderSpacing: 0, tableLayout: "fixed", width: tableWidthPx, minWidth: "100%" }}>
            {/* Single source of truth for column widths so headers and cells
                stay in lockstep. table-layout: fixed makes col widths
                authoritative; the final unpinned col absorbs wide screens. */}
            <colgroup>
              <col style={{ width: ROW_NUM_PX }} />
              {sheet.columns.map(col => (
                <col key={col.key} style={{ width: widthForCol(col) }} />
              ))}
              {canAddCol && <col style={{ width: TRAILING_COL_PX }} />}
            </colgroup>
            <thead className="bg-zinc-50 sticky top-0 z-10">
              <tr>
                <th
                  ref={rowNumHeaderRef}
                  className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-zinc-500 border-b border-r border-zinc-200"
                  style={rowNumWidthStyle}
                >
                  #
                </th>
                {sheet.columns.map((col, idx) => {
                  const isDragging = dragKey === col.key;
                  const isDropTarget = dragOverKey === col.key && dragKey && dragKey !== col.key;
                  const isSelectedColumn = selectedColumnKeys.has(col.key);
                  let stickyLeft;
                  let headerRef;
                  if (stickyEnabled) {
                    if (idx === 0) {
                      stickyLeft = stickyLefts.col0;
                      headerRef = col0HeaderRef;
                    } else if (idx === 1) {
                      stickyLeft = stickyLefts.col1;
                    }
                  }
                  const stickyHdrStyle = stickyHeaderStyle(stickyLeft);
                  const headerPositionStyle = stickyHdrStyle || { position: "relative" };
                  return (
                    <th
                      key={col.key}
                      ref={headerRef}
                      onMouseDown={(e) => startHeaderSelection("columns", col.key, e)}
                      onMouseEnter={() => extendDragSelection("columns", col.key)}
                      onContextMenu={canOpenColumnMenu ? (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        clearDrag();
                        setHeaderMenuRequest({ key: col.key, nonce: Date.now() });
                      } : undefined}
                      onDragOver={canReorderColumns ? (e) => {
                        if (dragKey && dragKey !== col.key) {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = "move";
                          if (dragOverKey !== col.key) setDragOverKey(col.key);
                        }
                      } : undefined}
                      onDragLeave={canReorderColumns ? (e) => {
                        // Only clear if leaving to outside this th (not into a child)
                        if (e.currentTarget.contains(e.relatedTarget)) return;
                        if (dragOverKey === col.key) setDragOverKey(null);
                      } : undefined}
                      onDrop={canReorderColumns ? (e) => {
                        e.preventDefault();
                        if (dragKey && dragKey !== col.key) onMoveColumn(dragKey, col.key);
                        clearDrag();
                      } : undefined}
                      className={`px-3 py-2 text-left text-[10px] uppercase tracking-wider font-semibold border-b border-r border-zinc-200 select-none transition-colors group overflow-hidden ${canSelectColumns ? "cursor-cell" : ""} ${isDragging ? "opacity-40 bg-purple-50" : ""} ${isDropTarget ? "bg-purple-100" : ""} ${stickyHdrStyle ? "bg-zinc-50" : ""} ${isSelectedColumn ? "bg-purple-100 text-purple-800 border-purple-200" : "text-zinc-600"}`}
                      style={{
                        ...fixedCellWidthStyle(widthForCol(col)),
                        ...headerPositionStyle,
                        ...(isSelectedColumn ? { boxShadow: "inset 0 -2px 0 0 #9333ea" } : {}),
                        ...(isDropTarget ? { boxShadow: "inset 3px 0 0 0 #9333ea" } : {}),
                      }}
                      title={canSelectColumns ? "Click to select column. Drag the handle to reorder." : undefined}
                    >
                      <div className="flex items-center justify-between gap-1 pr-1">
                        <span
                          data-prospector-drag-handle={canReorderColumns ? true : undefined}
                          draggable={canReorderColumns}
                          onMouseDown={canReorderColumns ? (e) => e.stopPropagation() : undefined}
                          onDragStart={canReorderColumns ? (e) => startColumnDrag(e, col) : undefined}
                          onDragEnd={canReorderColumns ? clearDrag : undefined}
                          title={canReorderColumns ? "Drag to reorder column" : undefined}
                          className={`inline-flex items-center gap-1 truncate ${canReorderColumns ? "cursor-grab active:cursor-grabbing" : ""}`}
                        >
                          {canReorderColumns && (
                            <i
                              className="ph ph-dots-six-vertical text-zinc-300 group-hover:text-zinc-500 -ml-1"
                            ></i>
                          )}
                          {col.key !== "logo" && <ColumnTypeIcon col={col} />}
                          {col.label || <span className="text-zinc-400">·</span>}
                        </span>
                        {canOpenColumnMenu && (
                          <ColumnHeaderMenu
                            col={col}
                            sheet={sheet}
                            columns={sheet.columns}
                            onMoveColumn={onMoveColumn}
                            onSortColumn={onSortColumn}
                            onDeleteColumn={deleteColumnsFromMenu}
                            openRequest={headerMenuRequest}
                            selectedDeleteCount={isSelectedColumn ? selectedRemovableColumnKeys().length : 0}
                          />
                        )}
                      </div>
                      {canResizeColumns && (
                        <div
                          data-prospector-resize-handle
                          onMouseDown={(e) => onResizeMouseDown(e, col)}
                          onClick={(e) => e.stopPropagation()}
                          onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
                          draggable={false}
                          title="Drag to resize column"
                          className={`absolute top-0 bottom-0 right-0 w-[6px] cursor-col-resize select-none z-20 ${resizingKey === col.key ? "bg-purple-400" : "hover:bg-purple-300"}`}
                        ></div>
                      )}
                    </th>
                  );
                })}
              {canAddCol && (
                <th
                  className="border-b border-l-2 border-l-purple-200 border-r border-zinc-200 px-3 py-2 bg-purple-50 text-left text-[10px] uppercase tracking-wider font-semibold text-purple-600"
                  style={{ ...trailingColWidthStyle, position: "sticky", right: 0, zIndex: 12 }}
                >
                  {hasPickerOptions || availableActions.length > 0 ? (
                    <ColumnPickerDropdown
                      catalogueCols={freeCatalogueCols}
                      currentKeys={sheet.columns.map(c => c.key)}
                      onSetColumnsKeys={onSetColumnsKeys}
                      paidActions={availableActions}
                      rowCount={sheet.rows.length}
                      rows={sheet.rows}
                      onAddPaidColumn={onAddColumn}
                    />
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      <i className="ph-bold ph-plus text-[10px]"></i>
                      Add column
                    </span>
                  )}
                </th>
              )}
              </tr>
            </thead>
            <tbody>
              {sheet.rows.length === 0 && (
                <SheetEmptyRowsState
                  sheet={sheet}
                  canAddRow={canAddRow}
                  onAddRow={onAddRow}
                  onRetrySheetJob={onRetrySheetJob}
                  colSpan={totalCols}
                />
              )}
              {sheet.rows.map((row, idx) => {
                const failed = row._status === JOB_STATUS.FAILED;
                const canRightClickDelete = !failed && typeof onRemoveRow === "function";
                const isRowDragging = dragRowId === row._id;
                const isRowDropTarget = dragOverRowId === row._id && dragRowId && dragRowId !== row._id;
                const isSelectedRow = selectedRowIds.has(row._id);
                const rowDropShadow = isRowDropTarget
                  ? (dragOverRowPlacement === "after"
                    ? "inset 0 -3px 0 0 #9333ea"
                    : "inset 0 3px 0 0 #9333ea")
                  : undefined;
                return (
                <tr
                  key={row._id}
                  className={`hover:bg-purple-50/40 prospector-fade-in group ${failed ? "bg-amber-50/40" : ""} ${isSelectedRow ? "bg-purple-50/70" : ""}`}
                  onDragOver={canMoveRows ? (e) => {
                    if (!dragRowId || dragRowId === row._id) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    const rect = e.currentTarget.getBoundingClientRect();
                    const placement = e.clientY > rect.top + rect.height / 2 ? "after" : "before";
                    if (dragOverRowId !== row._id) setDragOverRowId(row._id);
                    if (dragOverRowPlacement !== placement) setDragOverRowPlacement(placement);
                  } : undefined}
                  onDragLeave={canMoveRows ? (e) => {
                    if (e.currentTarget.contains(e.relatedTarget)) return;
                    if (dragOverRowId === row._id) {
                      setDragOverRowId(null);
                      setDragOverRowPlacement(null);
                    }
                  } : undefined}
                  onDrop={canMoveRows ? (e) => {
                    e.preventDefault();
                    if (dragRowId && dragRowId !== row._id) {
                      onMoveRow(dragRowId, row._id, dragOverRowPlacement || "before");
                    }
                    clearRowDrag();
                  } : undefined}
                >
                  <td
                    onMouseDown={(e) => startHeaderSelection("rows", row._id, e)}
                    onMouseEnter={() => extendDragSelection("rows", row._id)}
                    onContextMenu={canRightClickDelete ? (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setRowMenu({ rowId: row._id, x: e.clientX, y: e.clientY });
                    } : undefined}
                    title={canSelectRows ? "Click to select row. Drag the handle to reorder." : undefined}
                    className={`px-2 py-2 text-xs border-b border-r select-none ${failed ? "border-l-2 border-l-amber-400 border-zinc-100" : "border-zinc-100 text-zinc-400 group-hover:text-zinc-700"} ${canSelectRows ? "cursor-cell" : ""} ${isRowDragging ? "opacity-40 bg-purple-50" : ""} ${isRowDropTarget ? "bg-purple-100" : ""} ${isSelectedRow ? "bg-purple-100 text-purple-800 border-purple-200" : ""}`}
                    style={{
                      ...rowNumWidthStyle,
                      ...(isSelectedRow ? { boxShadow: "inset 2px 0 0 0 #9333ea" } : {}),
                      ...(rowDropShadow ? { boxShadow: rowDropShadow } : {}),
                    }}
                  >
                    <div className="flex items-center justify-center gap-0.5">
                      {failed && (onRetryRow || onRemoveRow) ? (
                        <React.Fragment>
                          {onRetryRow && (
                            <ProspectorRetryButton
                              compact
                              onClick={(e) => { e.stopPropagation(); onRetryRow(row._id); }}
                              ariaLabel="Retry enrichment"
                              title={row._error ? `${row._error} - click to retry enrichment` : "Retry enrichment"}
                            />
                          )}
                          {onRemoveRow && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); onRemoveRow(row._id); }}
                              aria-label="Remove failed row"
                              title={row._error ? `${row._error} - click to remove this row` : "Remove this row"}
                              className="inline-flex h-5 w-5 items-center justify-center rounded border border-amber-300 bg-white text-amber-700 shadow-sm transition-colors hover:bg-amber-100 hover:text-amber-900"
                            >
                              <i className="ph ph-x text-[10px]"></i>
                            </button>
                          )}
                        </React.Fragment>
                      ) : failed ? (
                        <i
                          className="ph-bold ph-warning-circle text-amber-500 text-base shrink-0"
                          title={row._error || "Could not enrich this row"}
                        ></i>
                      ) : (
                        <React.Fragment>
                          {canMoveRows && (
                            <i
                              data-prospector-drag-handle
                              className="ph ph-dots-six-vertical text-zinc-300 group-hover:text-zinc-500 cursor-grab active:cursor-grabbing -ml-1"
                              draggable
                              onMouseDown={(e) => e.stopPropagation()}
                              onDragStart={(e) => {
                                setRowMenu(null);
                                setDragRowId(row._id);
                                e.dataTransfer.effectAllowed = "move";
                                e.dataTransfer.setData("text/plain", row._id);
                              }}
                              onDragEnd={clearRowDrag}
                              title="Drag to reorder row"
                            ></i>
                          )}
                          <span>{idx + 1}</span>
                        </React.Fragment>
                      )}
                    </div>
                  </td>
                  {sheet.columns.map((col, colIdx) => {
                    const isSelectedColumn = selectedColumnKeys.has(col.key);
                    const isSelectedCell = isSelectedRow || isSelectedColumn;
                    let stickyLeft;
                    if (stickyEnabled) {
                      if (colIdx === 0) stickyLeft = stickyLefts.col0;
                      else if (colIdx === 1) stickyLeft = stickyLefts.col1;
                    }
                    const stickyBodyStyle = stickyCellStyle(stickyLeft, isSelectedCell ? "#faf5ff" : failed ? "#fffbeb" : "#ffffff");
                    return (
                      <td
                        key={col.key}
                        className={`px-3 py-2 text-zinc-800 border-b border-r border-zinc-100 ${stickyBodyStyle ? "bg-white" : ""} ${isSelectedCell ? "bg-purple-50/70" : ""}`}
                        style={{
                          ...fixedCellWidthStyle(widthForCol(col)),
                          ...(stickyBodyStyle || {}),
                        }}
                      >
                        <Cell row={row} col={col} />
                      </td>
                    );
                  })}
                  {canAddCol && (
                    <td
                      aria-hidden="true"
                      className="p-0 border-b border-r border-zinc-100 bg-white"
                      style={trailingColWidthStyle}
                    ></td>
                  )}
                </tr>
                );
              })}

              {!readOnly && sheet.rows.length > 0 && !canAddRow && sheet.entityType === "updates" && (
                <tr>
                  <td
                    colSpan={totalCols}
                    className="px-3 py-2 text-xs text-zinc-400 italic bg-zinc-50/60 text-center"
                    title="Updates can only be generated via transformation."
                  >
                    <i className="ph ph-info text-zinc-400 mr-1"></i>
                    Updates rows are only created via Transform → Get updates from a Companies sheet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {canAddRow && (
          <button
            type="button"
            onClick={onAddRow}
            className="w-full flex items-stretch text-left cursor-pointer bg-purple-50/50 hover:bg-purple-100 group transition-colors border-l-0 border-r-0 border-b-0 border-t border-purple-100 p-0 rounded-b-lg"
          >
            <span
              className="px-3 py-2 text-xs text-purple-500 border-r border-purple-100 group-hover:text-purple-700 inline-flex items-center shrink-0"
              style={rowNumWidthStyle}
            >
              <i className="ph-bold ph-plus"></i>
            </span>
            <span className="px-3 py-2 text-sm text-purple-600 font-medium group-hover:text-purple-700 flex-1">
              Add {sheet.entityType === "companies" ? "company" : "person"}…
            </span>
          </button>
        )}
      </div>
      {rowMenu && (
        <RowContextMenu
          pos={{ x: rowMenu.x, y: rowMenu.y }}
          onClose={() => setRowMenu(null)}
          deleteLabel={
            selection.axis === "rows" && selectedRowIds.has(rowMenu.rowId) && selection.ids.length > 1
              ? `Delete ${selection.ids.length} selected rows`
              : "Delete row"
          }
          onDelete={() => {
            const id = rowMenu.rowId;
            setRowMenu(null);
            deleteRowsFromMenu(id);
          }}
        />
      )}
    </div>
  );
}

function ColumnTypeIcon({ col }) {
  // Map column keys to small icons so the column type is glanceable, like a
  // spreadsheet "column type" indicator. Falls back to col.type when the key
  // isn't in the lookup so new catalogue columns don't all render iconless.
  const icon = COL_KEY_ICONS[col.key] || COL_TYPE_ICONS[col.type];
  if (!icon) return null;
  return <i className={`ph ${icon} text-zinc-400 text-xs`}></i>;
}

function ColumnHeaderMenu({ col, sheet, columns, onMoveColumn, onSortColumn, onDeleteColumn, openRequest, selectedDeleteCount = 0 }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [panelPos, setPanelPos] = useState(null);
  useClickOutside([triggerRef, panelRef], () => setOpen(false), open);

  const recomputePos = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = 192; // w-48
    const margin = 8;
    setPanelPos({
      top: rect.bottom + 4,
      left: Math.min(window.innerWidth - width - margin, Math.max(margin, rect.right - width)),
    });
  }, []);

  const openMenu = useCallback(() => {
    recomputePos();
    setOpen(true);
  }, [recomputePos]);

  useEffect(() => {
    if (!openRequest || openRequest.key !== col.key) return;
    openMenu();
  }, [openRequest?.nonce, openRequest?.key, col.key, openMenu]);

  useEffect(() => {
    if (!open) return;
    recomputePos();
    function onWin() { recomputePos(); }
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
    };
  }, [open, recomputePos]);

  const idx = columns.findIndex(c => c.key === col.key);
  const canLeft  = idx > 0;
  const canRight = idx < columns.length - 1;
  const isBaseCol = sheet && BASE_COLUMN_KEYS[sheet.entityType] && BASE_COLUMN_KEYS[sheet.entityType].has(col.key);
  const canDelete = selectedDeleteCount > 0 || (!isBaseCol && col.key !== "logo");

  function move(targetIdx) {
    const target = columns[targetIdx];
    if (!target) return;
    onMoveColumn(col.key, target.key);
    setOpen(false);
  }

  function sort(direction) {
    if (typeof onSortColumn !== "function") return;
    onSortColumn(col.key, direction);
    setOpen(false);
  }

  function deleteCol() {
    if (!canDelete || !onDeleteColumn) return;
    onDeleteColumn(col.key);
    setOpen(false);
  }

  const panel = open && panelPos && (
    <div
      ref={panelRef}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
      className="fixed w-48 bg-white border border-zinc-200 rounded-lg shadow-xl prospector-fade-in normal-case tracking-normal"
      style={{ top: panelPos.top, left: panelPos.left, zIndex: 9999 }}
    >
      <div className="py-1">
        <MenuItem icon="ph-sort-ascending" label="Sort ascending" onClick={() => sort("asc")} />
        <MenuItem icon="ph-sort-descending" label="Sort descending" onClick={() => sort("desc")} />
        <div className="border-t border-zinc-100 my-1" />
        <MenuItem disabled={!canLeft} icon="ph-arrow-left"   label="Move left"     onClick={() => move(idx - 1)} />
        <MenuItem disabled={!canRight} icon="ph-arrow-right" label="Move right"    onClick={() => move(idx + 1)} />
        <div className="border-t border-zinc-100 my-1" />
        <MenuItem disabled={!canLeft}  icon="ph-arrow-line-left"  label="Move to start" onClick={() => move(0)} />
        <MenuItem disabled={!canRight} icon="ph-arrow-line-right" label="Move to end"   onClick={() => move(columns.length - 1)} />
        <div className="border-t border-zinc-100 my-1" />
        <MenuItem
          disabled={!canDelete}
          disabledTitle={canDelete ? undefined : "Identity columns can't be removed"}
          icon="ph-trash"
          label={selectedDeleteCount > 0 ? `Delete ${selectedDeleteCount} selected column${selectedDeleteCount === 1 ? "" : "s"}` : "Delete column"}
          onClick={deleteCol}
          intent="danger"
        />
      </div>
    </div>
  );

  return (
    <>
      <div className="relative" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <button
          ref={triggerRef}
          onClick={(e) => {
            e.stopPropagation();
            if (open) setOpen(false);
            else openMenu();
          }}
          draggable={false}
          className={`p-0.5 rounded text-zinc-400 hover:text-purple-600 hover:bg-zinc-100 transition-opacity ${open ? "text-purple-600 bg-zinc-100 opacity-100" : "lg:opacity-0 lg:group-hover:opacity-100"}`}
          title="Column options"
        >
          <i className="ph ph-caret-down text-xs"></i>
        </button>
      </div>
      {panel && ReactDOM.createPortal(panel, document.body)}
    </>
  );
}

function MenuItem({ icon, label, onClick, disabled, intent, disabledTitle }) {
  const enabledClass = intent === "danger"
    ? "text-red-600 hover:bg-red-50"
    : "text-zinc-700 hover:bg-zinc-50";
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      title={disabled ? disabledTitle : undefined}
      className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${disabled ? "text-zinc-300 cursor-not-allowed" : enabledClass}`}
    >
      <i className={`ph ${icon} ${disabled ? "" : (intent === "danger" ? "text-red-500" : "text-zinc-500")}`}></i>
      <span className="font-medium">{label}</span>
    </button>
  );
}

// Spreadsheet-native right-click menu for a row. Anchored to the cursor
// position passed in via ``pos`` and clamped against the viewport so it
// never spills offscreen near the right/bottom edges. Closes on
// click-outside, Escape, scroll, or window resize.
function RowContextMenu({ pos, onClose, onDelete, deleteLabel = "Delete row" }) {
  const panelRef = useRef(null);
  useClickOutside([panelRef], onClose, true);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    function onWin() { onClose(); }
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
    };
  }, [onClose]);

  const margin = 8;
  const width = 192;
  const height = 44;
  const left = Math.min(window.innerWidth - width - margin, Math.max(margin, pos.x));
  const top = Math.min(window.innerHeight - height - margin, Math.max(margin, pos.y));

  const panel = (
    <div
      ref={panelRef}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
      className="fixed w-48 bg-white border border-zinc-200 rounded-lg shadow-xl prospector-fade-in normal-case tracking-normal"
      style={{ top, left, zIndex: 9999 }}
    >
      <div className="py-1">
        <MenuItem icon="ph-trash" label={deleteLabel} onClick={onDelete} intent="danger" />
      </div>
    </div>
  );

  return ReactDOM.createPortal(panel, document.body);
}

// ============================================================================
// ColumnPickerDropdown - anchored to the trailing column header. Lets the
// user toggle free (already-fetched) columns on/off. Spreadsheet-native
// surface: lives in the table header, not the toolbar.
// ============================================================================

function ColumnPickerDropdown({ catalogueCols, currentKeys, onSetColumnsKeys, paidActions = [], rowCount = 0, rows = [], onAddPaidColumn }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [panelPos, setPanelPos] = useState(null); // {top, right} in viewport coords

  // Compute panel placement on open + on viewport changes. Right-aligns to the
  // trigger so the dropdown grows leftward (mirrors the previous in-table
  // behavior). Anchored via fixed positioning + portal so it escapes the
  // spreadsheet's overflow-x-auto container - no more scroll-trapping.
  const recomputePos = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setPanelPos({
      top: r.bottom + 4,
      right: window.innerWidth - r.right,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    recomputePos();
    function onWin() { recomputePos(); }
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true); // capture so nested scrollers fire
    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
    };
  }, [open, recomputePos]);

  useClickOutside([triggerRef, panelRef], () => setOpen(false), open);

  const activeKeySet = new Set(currentKeys);
  const q = query.trim().toLowerCase();
  const matchesQuery = (label, key) =>
    !q || (label || "").toLowerCase().includes(q) || (key || "").toLowerCase().includes(q);
  const filtered = catalogueCols.filter(c => matchesQuery(c.label, c.key));
  const filteredPaid = paidActions.filter(a => matchesQuery(a.label, a.key));

  // Group free columns for display, preserving COLUMN_GROUP_ORDER.
  const byGroup = {};
  for (const c of filtered) {
    const g = c.group || "other";
    if (!byGroup[g]) byGroup[g] = [];
    byGroup[g].push(c);
  }
  const orderedGroups = COLUMN_GROUP_ORDER.filter(g => byGroup[g] && byGroup[g].length > 0);
  for (const g of Object.keys(byGroup)) {
    if (!orderedGroups.includes(g)) orderedGroups.push(g);
  }

  function toggle(key) {
    const nextKeys = activeKeySet.has(key)
      ? currentKeys.filter(k => k !== key)
      : [...currentKeys, key];
    onSetColumnsKeys(nextKeys);
  }

  function addPaid(action) {
    setOpen(false);
    if (typeof onAddPaidColumn === "function") onAddPaidColumn(action);
  }

  function reset() {
    onSetColumnsKeys([]);
    setOpen(false);
  }

  const noResults = orderedGroups.length === 0 && filteredPaid.length === 0;

  const panel = open && panelPos && (
    <div
      ref={panelRef}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      className="fixed w-[360px] bg-white border border-zinc-200 rounded-lg shadow-xl prospector-fade-in normal-case tracking-normal text-zinc-800"
      style={{ top: panelPos.top, right: panelPos.right, zIndex: 9999 }}
    >
      <div className="p-2 border-b border-zinc-100">
        <div className="relative">
          <i className="ph ph-magnifying-glass absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 text-sm"></i>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search columns…"
            autoFocus
            className="w-full pl-7 pr-2 py-1.5 text-sm bg-zinc-50 border border-zinc-200 rounded focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300"
          />
        </div>
      </div>
      <div className="max-h-[480px] overflow-y-auto py-1">
        {noResults && (
          <div className="px-3 py-4 text-xs text-zinc-400 text-center">No columns match.</div>
        )}
        {filteredPaid.length > 0 && (
          <div className="py-1 mb-1 border-b border-zinc-100">
            <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-purple-500 flex items-center gap-1">
              <i className="ph ph-currency-circle-dollar text-[12px]"></i>
              Paid actions
            </div>
            {filteredPaid.map(action => {
              const eligibleRowCount = rows.length > 0 ? eligibleRowsForPaidAction(action, rows).length : rowCount;
              const total = eligibleRowCount * action.credits;
              const costLine = eligibleRowCount === 0
                ? (
                  <React.Fragment>
                    <CreditAmount value={action.credits} />
                    <span>/ row</span>
                  </React.Fragment>
                )
                : (
                  <React.Fragment>
                    <CreditAmount value={action.credits} />
                    <span>× {eligibleRowCount} =</span>
                    <CreditAmount value={total} />
                  </React.Fragment>
                );
              return (
                <button
                  key={action.key}
                  type="button"
                  onClick={() => addPaid(action)}
                  className="w-full text-left px-3 py-2 text-sm flex items-start gap-2 hover:bg-purple-50 transition-colors"
                >
                  <i className={`ph ${action.icon} text-purple-600 text-base shrink-0 mt-0.5`}></i>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-purple-700 leading-tight flex items-center gap-1">
                      <i className="ph-bold ph-plus text-[10px] text-purple-400"></i>
                      <span>{action.label}</span>
                    </div>
                    <div className="text-[11px] font-normal text-purple-500/80 mt-0.5 leading-tight flex items-center gap-1">{costLine}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
        {orderedGroups.map(group => (
          <div key={group} className="py-1">
            <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
              {COLUMN_GROUP_LABELS[group] || group}
            </div>
            {byGroup[group].map(col => {
              const checked = activeKeySet.has(col.key);
              return (
                <button
                  key={col.key}
                  type="button"
                  onClick={() => toggle(col.key)}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${checked ? "bg-purple-50/60" : "hover:bg-zinc-50"}`}
                >
                  <span
                    className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${checked ? "bg-purple-600 border-purple-600" : "bg-white border-zinc-300"}`}
                  >
                    {checked && <i className="ph-bold ph-check text-white text-[10px]"></i>}
                  </span>
                  <span className="flex-1 inline-flex items-center gap-1.5 truncate">
                    <ColumnTypeIcon col={col} />
                    <span className="text-zinc-800">{col.label || col.key}</span>
                  </span>
                  {col.public_only && (
                    <span
                      title="Only meaningful for public companies"
                      className="text-[9px] text-zinc-400 uppercase tracking-wider"
                    >
                      public
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      <div className="border-t border-zinc-100 px-3 py-2 flex items-center justify-between">
        <button
          type="button"
          onClick={reset}
          className="text-[11px] text-zinc-500 hover:text-zinc-700 underline-offset-2 hover:underline"
        >
          Reset to defaults
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-[11px] text-zinc-500 hover:text-zinc-700"
        >
          Close
        </button>
      </div>
    </div>
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        onMouseDown={(e) => e.stopPropagation()}
        title="Customize columns"
        className="inline-flex items-center gap-1 hover:text-purple-700 transition-colors"
      >
        <i className="ph-bold ph-plus text-[10px]"></i>
        <span>Add column</span>
        <i className={`ph ph-caret-down text-[10px] transition-transform ${open ? "rotate-180" : ""}`}></i>
      </button>
      {panel && ReactDOM.createPortal(panel, document.body)}
    </>
  );
}


// ============================================================================
// ProgressChip - sticky strip shown while a sheet has work in flight
// ============================================================================

function ProgressChip({ progress, onCancel }) {
  const { kind, label, done = 0, total = 0, message } = progress || {};
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : null;
  const counter = (() => {
    if (kind === "csv_import" && total === 0) return "Reading file…";
    if (total > 0) {
      const unit = kind === "add_column" ? "cells" : "rows";
      return `${done.toLocaleString()} / ${total.toLocaleString()} ${unit}`;
    }
    return null;
  })();
  const isSlowColumn = kind === "slow_column";

  return (
    <div className={`${isSlowColumn ? "bg-amber-50 border-amber-200" : "bg-purple-50 border-purple-200"} border-b px-4 xl:px-6 py-2 flex items-center gap-3`}>
      <i className={`ph ph-circle-notch ph-spin ${isSlowColumn ? "text-amber-600" : "text-purple-600"} shrink-0`}></i>
      <div className={`text-xs font-semibold ${isSlowColumn ? "text-amber-900" : "text-purple-800"} shrink-0`}>
        {label || "Working"}…
      </div>
      {counter && (
        <div className={`text-xs ${isSlowColumn ? "text-amber-700/80" : "text-purple-700/80"} shrink-0 tabular-nums`}>{counter}</div>
      )}
      {pct !== null && (
        <div className={`flex-1 max-w-xs h-1.5 ${isSlowColumn ? "bg-amber-200" : "bg-purple-200"} rounded-full overflow-hidden`}>
          <div
            className={`h-full ${isSlowColumn ? "bg-amber-600" : "bg-purple-600"} rounded-full transition-all duration-200`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      {message && (
        <div className={`hidden lg:block text-xs ${isSlowColumn ? "text-amber-800/80" : "text-purple-700/80"} truncate`}>
          {message}
        </div>
      )}
      <div className="flex-1" />
      <button
        type="button"
        onClick={onCancel}
        title="Abort spreadsheet jobs"
        className="px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 rounded inline-flex items-center gap-1 shrink-0"
      >
        <i className="ph ph-stop-circle"></i> Abort
      </button>
    </div>
  );
}

// ============================================================================
// PropagationToast
// ============================================================================

function PropagationToast({ message }) {
  return (
    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-zinc-900 text-white text-xs font-medium px-3 py-2 rounded-lg shadow-lg flex items-center gap-2 prospector-fade-in z-20">
      <i className="ph ph-arrows-clockwise"></i> {message}
    </div>
  );
}

// ============================================================================
// AddRowModal
// ============================================================================

const PERSON_INPUT_METHODS = [
  {
    key: "work_email",
    label: "Work email",
    icon: "ph-envelope-simple",
    fields: ["Work email"],
    error: "Work email is required for the selected option.",
  },
  {
    key: "name_employer",
    label: "Name + employer",
    icon: "ph-user-circle",
    fields: ["First name", "Employer"],
    error: "First name and employer website or company name are required for the selected option.",
  },
  {
    key: "employer_role",
    label: "Employer + role",
    icon: "ph-identification-badge",
    fields: ["Role", "Employer"],
    error: "Role and employer website or company name are required for the selected option.",
  },
];

function personInputMethodError(methodKey) {
  return PERSON_INPUT_METHODS.find(method => method.key === methodKey)?.error
    || "Select a valid input method.";
}

function AddRowModal({ sheet, onCancel, onSubmit }) {
  const [fields, setFields] = useState(() => {
    if (sheet.entityType === "companies") return { query: "" };
    if (sheet.entityType === "employees") {
      return {
        work_email: "",
        first_name: "",
        middle_name: "",
        last_name: "",
        employer_website: "",
        role: "",
      };
    }
    return {};
  });
  const [personInputMethod, setPersonInputMethod] = useState("work_email");
  const [error, setError] = useState("");

  function setField(key, value) {
    setFields(f => ({ ...f, [key]: value }));
    if (error) setError("");
  }

  function selectPersonInputMethod(methodKey) {
    setPersonInputMethod(methodKey);
    if (error) setError("");
  }

  function normalizedDomain(raw) {
    const cleaned = String(raw || "")
      .trim()
      .replace(/^https?:\/\//i, "")
      .replace(/^www\./i, "")
      .split(/[/?#]/)[0]
      .toLowerCase();
    const looksLikeDomain = /^[\w-]+(\.[\w-]+)+$/.test(cleaned) && /\.[a-z]{2,}$/i.test(cleaned);
    return looksLikeDomain ? cleaned : "";
  }

  function companyNameFromEmployerInput(raw, domain) {
    if (domain) return domain.split(".")[0].replace(/^./, c => c.toUpperCase());
    return String(raw || "").trim();
  }

  function submit(e) {
    if (e) e.preventDefault();
    if (sheet.entityType === "companies") {
      const raw = fields.query.trim();
      if (!raw) return;
      const parsed = parseCompanyQuery(raw);
      onSubmit(parsed);
    } else if (sheet.entityType === "employees") {
      const workEmail = personInputMethod === "work_email" ? fields.work_email.trim().toLowerCase() : "";
      const firstName = personInputMethod === "name_employer" ? fields.first_name.trim() : "";
      const middleName = personInputMethod === "name_employer" ? fields.middle_name.trim() : "";
      const lastName = personInputMethod === "name_employer" ? fields.last_name.trim() : "";
      const employerWebsite = personInputMethod === "name_employer" || personInputMethod === "employer_role"
        ? fields.employer_website.trim()
        : "";
      const role = personInputMethod === "employer_role" ? fields.role.trim() : "";
      const hasValidEmail = !workEmail || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(workEmail);
      const hasSelectedCombination = (
        (personInputMethod === "work_email" && workEmail)
        || (personInputMethod === "name_employer" && firstName && employerWebsite)
        || (personInputMethod === "employer_role" && employerWebsite && role)
      );

      if (!hasValidEmail) {
        setError("Enter a valid work email address.");
        return;
      }
      if (!hasSelectedCombination) {
        setError(personInputMethodError(personInputMethod));
        return;
      }

      const domain = normalizedDomain(employerWebsite)
        || (workEmail.includes("@") ? workEmail.split("@")[1] : "");
      const name = [firstName, middleName, lastName].filter(Boolean).join(" ");
      onSubmit({
        _person_input_method: personInputMethod,
        work_email: workEmail,
        first_name: firstName,
        middle_name: middleName,
        last_name: lastName,
        employer_website: employerWebsite,
        role,
        name,
        title: role,
        company_domain: domain,
        company: companyNameFromEmployerInput(employerWebsite, domain),
      });
    }
  }

  const showWorkEmail = personInputMethod === "work_email";
  const showNameFields = personInputMethod === "name_employer";
  const showEmployer = personInputMethod === "name_employer" || personInputMethod === "employer_role";
  const showRole = personInputMethod === "employer_role";

  return (
    <ModalShell onClose={onCancel} title="Add row">
      <form onSubmit={submit}>
        {sheet.entityType === "companies" && (
          <Field label="Website or company name" required>
            <input
              autoFocus
              value={fields.query}
              onChange={(e) => setField("query", e.target.value)}
              placeholder="stripe.com  ·  Stripe  ·  https://stripe.com"
              className="w-full border border-zinc-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </Field>
        )}
        {sheet.entityType === "employees" && (
          <React.Fragment>
            <div className="mb-4">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="text-xs font-semibold text-zinc-700">Choose one input method</div>
                <div className="text-[11px] text-zinc-400">Work email is selected by default</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {PERSON_INPUT_METHODS.map(method => {
                  const active = personInputMethod === method.key;
                  return (
                    <button
                      key={method.key}
                      type="button"
                      aria-pressed={active}
                      onClick={() => selectPersonInputMethod(method.key)}
                      className={`min-h-[82px] rounded-md border px-3 py-2 text-left transition-colors ${
                        active
                          ? "border-purple-300 bg-purple-50 text-purple-900 ring-1 ring-purple-200"
                          : "border-zinc-200 bg-white text-zinc-700 hover:border-purple-200 hover:bg-purple-50/50"
                      }`}
                    >
                      <div className="mb-1.5 flex items-center gap-1.5">
                        <i className={`ph ${method.icon} ${active ? "text-purple-700" : "text-zinc-400"}`}></i>
                        <span className="text-xs font-semibold">{method.label}</span>
                        {active && <i className="ph-bold ph-check-circle ml-auto text-purple-600"></i>}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {method.fields.map(field => (
                          <span
                            key={field}
                            className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${
                              active
                                ? "border-purple-200 bg-white text-purple-800"
                                : "border-zinc-200 bg-zinc-50 text-zinc-500"
                            }`}
                          >
                            {field}
                          </span>
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            {showWorkEmail && (
              <Field label="Work email" required>
                <input
                  autoFocus
                  type="email"
                  value={fields.work_email}
                  onChange={(e) => setField("work_email", e.target.value)}
                  placeholder="patrick@stripe.com"
                  className="w-full border border-zinc-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </Field>
            )}
            {showNameFields && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="First name" required>
                  <input
                    autoFocus
                    value={fields.first_name}
                    onChange={(e) => setField("first_name", e.target.value)}
                    placeholder="Patrick"
                    className="w-full border border-zinc-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </Field>
                <Field label="Middle name">
                  <input
                    value={fields.middle_name}
                    onChange={(e) => setField("middle_name", e.target.value)}
                    placeholder=""
                    className="w-full border border-zinc-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </Field>
                <Field label="Last name">
                  <input
                    value={fields.last_name}
                    onChange={(e) => setField("last_name", e.target.value)}
                    placeholder="Collison"
                    className="w-full border border-zinc-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </Field>
              </div>
            )}
            {showRole && (
              <Field label="Role" required>
                <input
                  autoFocus
                  value={fields.role}
                  onChange={(e) => setField("role", e.target.value)}
                  placeholder="CEO"
                  className="w-full border border-zinc-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </Field>
            )}
            {showEmployer && (
              <Field label="Employer website or company name" required>
                <input
                  value={fields.employer_website}
                  onChange={(e) => setField("employer_website", e.target.value)}
                  placeholder="stripe.com or Stripe"
                  className="w-full border border-zinc-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </Field>
            )}
          </React.Fragment>
        )}
        {error && (
          <div className="mb-3 rounded-md border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-2">
          <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-100 rounded-md">Cancel</button>
          <button type="submit" className="px-3 py-1.5 text-sm font-semibold text-white bg-purple-600 rounded-md hover:bg-purple-700">Add row</button>
        </div>
      </form>
    </ModalShell>
  );
}

// ============================================================================
// PaidColumnConfirmModal - credit-cost confirmation before backfilling a paid
// column-action over an existing sheet's rows.
// ============================================================================

function formatCreditAmount(value) {
  if (value === null || value === undefined || value === "") return "-";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return numeric.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function CreditAmount({ value, className = "", iconClassName = "" }) {
  const formatted = formatCreditAmount(value);
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap tabular-nums ${className}`.trim()} title={`${formatted} credits`}>
      <span>{formatted}</span>
      <i className={`ph-fill ph-coins ${iconClassName}`.trim()} aria-label="credits"></i>
    </span>
  );
}

function OutOfCreditsModal({ message, actionLabel, requiredCredits, currentCredits, onCancel }) {
  function goToBilling() {
    window.location.href = BUY_CREDITS_URL;
  }

  return (
    <ModalShell onClose={onCancel} title="Not enough credits">
      <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white text-amber-700 shadow-sm ring-1 ring-amber-200">
            <i className="ph-fill ph-coins text-xl"></i>
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-950">
              {actionLabel ? `${actionLabel} needs more credits.` : "Buy credits to continue prospecting."}
            </p>
            <p className="text-sm text-amber-900/80 mt-1">{message}</p>
          </div>
        </div>
        {(requiredCredits !== undefined || currentCredits !== undefined) && (
          <div className="mt-4 grid grid-cols-2 gap-2">
            <div className="rounded-md bg-white/75 border border-amber-100 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-700">Available</div>
              <div className="text-sm font-bold text-zinc-900">
                <CreditAmount value={currentCredits} />
              </div>
            </div>
            <div className="rounded-md bg-white/75 border border-amber-100 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-700">Required</div>
              <div className="text-sm font-bold text-zinc-900">
                <CreditAmount value={requiredCredits} />
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-100 rounded-md"
        >
          Cancel
        </button>
        <button
          onClick={goToBilling}
          className="px-3 py-1.5 text-sm font-semibold text-white bg-purple-600 rounded-md hover:bg-purple-700 inline-flex items-center gap-1.5"
        >
          <i className="ph ph-coins"></i> Buy credits
        </button>
      </div>
    </ModalShell>
  );
}

function PaidColumnConfirmModal({ action, rowCount, onCancel, onConfirm }) {
  const totalCredits = (action.credits || 0) * rowCount;
  const isEmployeeCount = action.action_kind === EMPLOYEE_COUNT_ACTION_KIND;
  const isWorkEmail = action.action_kind === WORK_EMAIL_ACTION_KIND;
  return (
    <ModalShell onClose={onCancel} title={action.label}>
      <p className="text-sm text-zinc-700 mb-3">
        Enable <span className="font-semibold">{action.label.replace(/^Get /, "")}</span> for the
        {" "}<span className="font-semibold text-purple-700">{rowCount}</span> existing
        {" "}{rowCount === 1 ? "row" : "rows"} in this sheet?
      </p>
      <div className="rounded-md bg-purple-50 border border-purple-100 px-3 py-2 mb-4">
        <div className="flex items-baseline justify-between text-sm">
          <span className="text-zinc-600 inline-flex items-center gap-1">
            <CreditAmount value={action.credits} />
            <span>× {rowCount} rows</span>
          </span>
          <CreditAmount value={totalCredits} className="font-semibold text-purple-700 text-base" />
        </div>
        <p className="text-[11px] text-zinc-500 mt-1">
          {isEmployeeCount
            ? "Employee count lookups are slow. You can leave and check back later; rows fill in as results arrive."
            : isWorkEmail
              ? "Work email charges up to this amount; no-email results charge less and rows fill in as results arrive."
              : "Each row's enrichment runs as a background job; cells will fill in as results arrive."}
        </p>
      </div>
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-100 rounded-md"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="px-3 py-1.5 text-sm font-semibold text-white bg-purple-600 rounded-md hover:bg-purple-700"
        >
          <span className="inline-flex items-center gap-1.5">
            <span>Continue ·</span>
            <CreditAmount value={totalCredits} />
          </span>
        </button>
      </div>
    </ModalShell>
  );
}

function BulkDeleteConfirmModal({ sheet, axis, rowIds, columnKeys, protectedCount, onCancel, onConfirm }) {
  if (!sheet) return null;
  const isRows = axis === "rows";
  const count = isRows ? rowIds.length : columnKeys.length;
  const noun = isRows ? (count === 1 ? "row" : "rows") : (count === 1 ? "column" : "columns");
  const title = isRows ? "Delete selected rows" : "Delete selected columns";
  const columnsByKey = Object.fromEntries((sheet.columns || []).map(c => [c.key, c]));
  const columnLabels = (columnKeys || [])
    .map(key => columnsByKey[key]?.label || key)
    .filter(Boolean);

  return (
    <ModalShell onClose={onCancel} title={title}>
      <div className="flex items-start gap-3 mb-4">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-red-50 text-red-600 ring-1 ring-red-100">
          <i className="ph ph-trash text-lg"></i>
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-zinc-900">
            Delete <span className="text-red-600">{count}</span> selected {noun} from <span className="font-semibold">{sheet.name}</span>?
          </p>
          <p className="text-sm text-zinc-600 mt-1">
            This removes the selected {noun} from the spreadsheet after you confirm.
          </p>
        </div>
      </div>

      {!isRows && columnLabels.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-1.5">
          {columnLabels.slice(0, 8).map(label => (
            <span key={label} className="inline-flex items-center rounded border border-zinc-200 bg-zinc-50 px-2 py-1 text-xs font-medium text-zinc-700">
              {label}
            </span>
          ))}
          {columnLabels.length > 8 && (
            <span className="inline-flex items-center rounded border border-zinc-200 bg-zinc-50 px-2 py-1 text-xs text-zinc-500">
              +{columnLabels.length - 8} more
            </span>
          )}
        </div>
      )}

      {!isRows && protectedCount > 0 && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {protectedCount} protected identity {protectedCount === 1 ? "column was" : "columns were"} left out.
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-100 rounded-md">Cancel</button>
        <button onClick={onConfirm} className="px-3 py-1.5 text-sm font-semibold text-white bg-red-600 rounded-md hover:bg-red-700">
          Delete {count} {noun}
        </button>
      </div>
    </ModalShell>
  );
}

function SheetLifecycleConfirmModal({ action, sheet, childCount, onCancel, onConfirm }) {
  if (!sheet) return null;
  const isDelete = action === "delete";
  const verb = isDelete ? "Delete" : "Archive";
  const childLabel = `${childCount} child spreadsheet${childCount === 1 ? "" : "s"}`;
  return (
    <ModalShell onClose={onCancel} title={`${verb} spreadsheet tree`}>
      <p className="text-sm text-zinc-700 mb-3">
        {verb} <span className="font-semibold">{sheet.name}</span> and {childLabel}?
      </p>
      <div className={`rounded-md border px-3 py-2 mb-4 ${isDelete ? "bg-red-50 border-red-100" : "bg-zinc-50 border-zinc-200"}`}>
        <p className="text-[11px] text-zinc-600">
          {isDelete
            ? "This soft-deletes the archived spreadsheet tree and hides it from Prospector."
            : "This moves the spreadsheet tree into the hidden archived folder."}
        </p>
      </div>
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-100 rounded-md"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className={`px-3 py-1.5 text-sm font-semibold text-white rounded-md ${isDelete ? "bg-red-600 hover:bg-red-700" : "bg-zinc-800 hover:bg-zinc-900"}`}
        >
          {verb} all
        </button>
      </div>
    </ModalShell>
  );
}


function Field({ label, required, children }) {
  return (
    <div className="mb-3">
      <label className="block text-xs font-semibold text-zinc-700 mb-1">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      {children}
    </div>
  );
}

function ModalShell({ title, children, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 prospector-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-zinc-900">{title}</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700">
            <i className="ph ph-x text-xl"></i>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ============================================================================
// Misc helpers
// ============================================================================

function deepClone(x) {
  return JSON.parse(JSON.stringify(x));
}

function truncate(s, n) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function eligibleRowsNeedingProfileFallback(transform, rows) {
  if (!PROFILE_FALLBACK_TRANSFORM_KEYS.has(transform?.actionKey)) return [];
  return eligibleRowsForTransform(transform, rows)
    .filter(row => !rowHasEnrichedPersonIdentity(row));
}

// ============================================================================
// Mount
// ============================================================================

document.addEventListener("DOMContentLoaded", () => {
  const root = document.getElementById("prospector-root");
  if (!root) return;
  ReactDOM.render(<ProspectorApp />, root);
});
